import { useState, useEffect, useRef, useCallback, Dispatch, SetStateAction } from "react";
import { apiRequest } from "@/lib/queryClient";
import { ENABLE_LEGACY_BILLING } from "@config/featureFlags";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePlan, navigateToUpgrade } from "@/hooks/usePlan";
import { PlanUsageCompact, PlanUsageFull, FreeRoleExpiryBanner } from "@/components/PlanUsageWidget";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { useRequireAuth } from "@/hooks/useAuth";
import { DocumentUploader } from "@/components/DocumentUploader";
import { BulkInvitesSection } from "@/components/BulkInvitesSection";
import { TopCandidatesView } from "@/components/TopCandidatesView";
import { AgentDashboardLayout, AgentTab } from "@/components/AgentDashboardLayout";
import { OnboardingTour, useOnboardingTour, TourTriggerButton } from "@/components/OnboardingTour";
import { startOnboardingTour, resetTour as resetTourStorage } from "@/lib/onboardingTour";
import { ThemeToggle } from "@/components/ThemeToggle";
import { dashboardTourSteps } from "@/components/tour-steps";
import { 
  Plus, Search, Briefcase, Users, Calendar, MapPin, 
  Clock, Eye, Filter, ChevronDown, ChevronRight, MoreHorizontal,
  Play, CheckCircle, CheckCircle2, XCircle, AlertCircle, Shield,
  FileText, UserCheck, AlertTriangle, X, Download, Upload,
  Columns3, Table as TableIcon, Video, Award, Building2, Mail, Key, LogOut, Pencil, Link2, Copy,
  Edit2, Trash2, Menu, HelpCircle, ArrowRight, RefreshCw, GitMerge, Send, Bell, Settings, Info, MessageSquare,
  Sparkles, TrendingUp, UserPlus, Share2, Star
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface EmployerQuestionRequirements {
  customQuestions?: string[];
  topicsToAssess?: string[];
  scenariosToInclude?: string[];
  specificSkillsToTest?: string[];
  notes?: string;
  updatedAt?: string;
}

interface Job {
  id: string;
  title: string;
  location: string;
  employment_type: string;
  work_mode: string;
  date_required: string;
  status: string;
  created_at: string;
  applicant_count?: number;
  applicants_total?: number;
  applications?: Application[];
  employer_question_requirements?: EmployerQuestionRequirements;
  jd_pdf_storage_path?: string;
  description?: string;
  requirements?: string;
  skills?: string;
  salary_range?: string;
  source?: 'postgresql';
}

interface Application {
  id: string;
  candidate_id: string;
  status: string;
  pipeline_stage: string;
  created_at: string;
  candidate_name?: string;
  candidate_email?: string;
  human_verified?: boolean;
  agent_notes?: string;
  identity_verified?: boolean;
  right_to_work_verified?: boolean;
  authenticity_verified?: boolean;
  agent_summary?: string;
  dossier_sent_at?: string;
  dossier_sent_to_email?: string;
  outcome?: string;
  outcome_set_at?: string;
}

interface AgentVerification {
  identity_verified: boolean;
  right_to_work_verified: boolean;
  authenticity_verified: boolean;
  agent_notes: string;
  agent_summary: string;
  flagged_issues: string[];
}

interface InterviewVideo {
  questionId: string;
  question: string;
  responseId: string;
  videoDeleted: boolean;
  videoDeletedAt: string | null;
}

interface InterviewVideoStatus {
  videosDeleted: boolean;
  videosDeletedAt: string | null;
  videosDeletedBy: "recruiter" | "system" | null;
  videoDeleteAt: string | null;
}

interface ContactEnquiry {
  id: number;
  enquiryType: string;
  name: string;
  email: string;
  company?: string | null;
  phone?: string | null;
  message: string;
  fileId?: string | null;
  fileName?: string | null;
  status: string;
  agentNotes?: string | null;
  createdAt: string;
}

const PIPELINE_STAGES = [
  { id: "initial_review", label: "01 Applied", color: "bg-blue-500", tooltip: "Candidate added. Interview link not yet sent." },
  { id: "link_sent", label: "02 Link Sent", color: "bg-yellow-500", tooltip: "Interview link emailed and SMSed. Waiting for candidate to open it." },
  { id: "in_progress", label: "03 In Progress", color: "bg-green-500", tooltip: "Candidate has opened the link and is recording their interview." },
  { id: "under_review", label: "04 Under Review", color: "bg-orange-500", tooltip: "Interview received and under review. AI scoring runs automatically — score badge appears when ready." },
  { id: "dossier_ready", label: "05 Dossier Ready", color: "bg-purple-500", tooltip: "Review complete. Dossier created. Ready to send to employer." },
  { id: "outcome", label: "06 Outcome", color: "bg-teal-500", tooltip: "Employer has reviewed the dossier and selected an outcome." },
];

// Maps transient internal processing stages to their recruiter-visible display bucket.
// 'submitted' and 'ai_triage' are system-only states that last seconds — recruiters
// see them as part of Under Review with a processing indicator on the card.
const STAGE_DISPLAY_MAP: Record<string, string> = {
  applied:       "initial_review",
  initial_review: "initial_review",
  link_sent:     "link_sent",
  in_progress:   "in_progress",
  submitted:     "under_review",
  ai_triage:     "under_review",
  under_review:  "under_review",
  dossier_ready: "dossier_ready",
  outcome:       "outcome",
};

const VISUAL_PIPELINE_STAGES = [
  { label: "01 Applied", tooltip: "Candidate added. Interview link not yet sent.", color: "bg-blue-500" },
  { label: "02 Link Sent", tooltip: "Interview link emailed and SMSed. Waiting for candidate to open it.", color: "bg-yellow-500" },
  { label: "03 In Progress", tooltip: "Candidate has opened the link and is recording their interview.", color: "bg-green-400" },
  { label: "04 Under Review", tooltip: "Interview received and under review. AI scoring runs automatically.", color: "bg-orange-500" },
  { label: "05 Dossier Ready", tooltip: "Review complete. Dossier created. Ready to send to employer.", color: "bg-purple-500" },
  { label: "06 Outcome", tooltip: "Employer has reviewed the dossier and selected an outcome.", color: "bg-teal-500" },
];

interface DossierRecord {
  id: number;
  interviewId: number;
  candidateId?: number;
  applicationId?: number;
  overallScore?: number;
  isPublished?: boolean;
  generatedAt?: string;
  candidateName?: string;
  candidateEmail?: string;
  roleTitle?: string;
  agentNotes?: string;
  verificationNotes?: string;
  riskFlags?: string[];
  transcriptShareLevel?: string;
  recommendation?: string;
  externalDossierUrl?: string;
  externalDossierId?: string;
  dossierJson?: {
    executiveSummary?: string;
    professionalProfile?: {
      headline?: string;
      coreCompetencies?: string[];
      experienceHighlights?: string[];
      technicalStrengths?: string[];
    };
    interviewPerformance?: {
      standoutMoments?: string[];
      communicationStyle?: string;
      problemSolvingApproach?: string;
    };
    marketingSnapshot?: {
      elevatorPitch?: string;
      keyDifferentiators?: string[];
      idealFor?: string;
    };
  };
}

interface InterviewRecord {
  id: number;
  applicationId: number;
  status: string;
  recordingStatus?: string;
  candidateName?: string;
  candidateEmail?: string;
  candidateId?: number;
  roleTitle?: string;
  roleId?: number;
  overallScore?: number;
  hasPublishedDossier?: boolean;
  completedAt?: string;
}

interface CandidateMatch {
  candidateId: number;
  roleId: string;
  roleSource: 'postgresql';
  candidateName: string;
  candidateEmail: string;
  roleTitle: string;
  matchScore: number;
  breakdown: {
    interviewScore: number;
    credentialScore: number;
    skillsScore: number;
  };
  interviewOverallScore: number | null;
  verifiedCredentials: number;
  totalCredentials: number;
  strengths: string[];
  gaps: string[];
  recommendation: string | null;
  matchedSkills: string[];
  missingSkills: string[];
  hasDealBreakerIssue: boolean;
}

function MatchesTab({ jobs }: { jobs: Job[] }) {
  const [matches, setMatches] = useState<CandidateMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState<string>("all");
  const [minScore, setMinScore] = useState<number>(0);
  const [addingToPipeline, setAddingToPipeline] = useState<string | null>(null);
  const [autoLinking, setAutoLinking] = useState(false);
  const [addedMatches, setAddedMatches] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const [matchesRes, applicationsRes] = await Promise.all([
        apiRequest("GET", "/api/agent/matches"),
        apiRequest("GET", "/api/applications")
      ]);
      
      if (matchesRes.ok) {
        const data = await matchesRes.json();
        setMatches(data);
        
        if (applicationsRes.ok) {
          const apps = await applicationsRes.json();
          const existingKeys = new Set<string>();
          apps.forEach((app: any) => {
            if (app.candidateId && app.roleId) {
              existingKeys.add(`${app.candidateId}-${app.roleId}`);
            }
          });
          setAddedMatches(existingKeys);
        }
      }
    } catch (error) {
      console.error("Error fetching matches:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToPipeline = async (candidateId: number, roleId: string) => {
    const key = `${candidateId}-${roleId}`;
    setAddingToPipeline(key);
    try {
      const res = await apiRequest("POST", "/api/agent/matches/add-to-pipeline", { candidateId, roleId });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setAddedMatches(prev => new Set(prev).add(key));
      } else if (res.status === 409 && data.alreadyExists) {
        toast.info("Already in pipeline");
        setAddedMatches(prev => new Set(prev).add(key));
      } else {
        toast.error(data.error || "Failed to add to pipeline");
      }
    } catch (error) {
      toast.error("Failed to add to pipeline");
    } finally {
      setAddingToPipeline(null);
    }
  };

  const runAutoLink = async (threshold: number) => {
    setAutoLinking(true);
    try {
      const res = await apiRequest("POST", "/api/agent/matches/auto-link", { threshold });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Matched ${data.created} candidates (${data.skipped} already in pipeline)`);
        const newAdded = new Set(addedMatches);
        data.results.filter((r: any) => r.status === "created" || r.status === "already_applied")
          .forEach((r: any) => {
            const match = matches.find(m => m.candidateName === r.candidateName && m.roleTitle === r.roleTitle);
            if (match) newAdded.add(`${match.candidateId}-${match.roleId}`);
          });
        setAddedMatches(newAdded);
      } else {
        toast.error("Failed to run matching");
      }
    } catch (error) {
      toast.error("Failed to run matching");
    } finally {
      setAutoLinking(false);
    }
  };

  const filteredMatches = matches.filter(m => {
    if (selectedRoleId !== "all" && m.roleId !== selectedRoleId) return false;
    if (m.matchScore < minScore) return false;
    return true;
  });

  const getScoreColor = (score: number) => {
    if (score >= 85) return "bg-green-500";
    if (score >= 70) return "bg-blue-500";
    if (score >= 50) return "bg-amber-500";
    return "bg-red-500";
  };

  const getScoreBadge = (score: number) => {
    if (score >= 85) return { label: "Excellent", className: "bg-green-100 text-green-800" };
    if (score >= 70) return { label: "Good", className: "bg-blue-100 text-blue-800" };
    if (score >= 50) return { label: "Potential", className: "bg-amber-100 text-amber-800" };
    return { label: "Low", className: "bg-red-100 text-red-800" };
  };

  const uniqueRoles = Array.from(new Set(matches.map(m => m.roleId))).map(roleId => {
    const match = matches.find(m => m.roleId === roleId);
    return { id: roleId, title: match?.roleTitle || "Unknown" };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <Card className="p-4 md:p-6">
        <div className="flex flex-col gap-4 mb-4 md:mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              <Shield className="w-5 h-5 md:w-6 md:h-6 text-primary" />
              Candidate Matching
            </h2>
            <p className="text-sm md:text-base text-muted-foreground">
              Matched candidates based on verified credentials, interview scores, and skills
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={fetchMatches} variant="outline" size="sm" className="gap-1 flex-1 md:flex-none" data-testid="button-refresh-matches">
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="default" size="sm" className="gap-1 flex-1 md:flex-none" disabled={autoLinking} data-testid="button-quick-match">
                  {autoLinking ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  <span>Quick Match</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => runAutoLink(85)} data-testid="auto-link-85">
                  Score 85+ (Excellent only)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => runAutoLink(70)} data-testid="auto-link-70">
                  Score 70+ (Good & above)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => runAutoLink(50)} data-testid="auto-link-50">
                  Score 50+ (All potential)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 md:mb-6">
          <div>
            <label className="text-sm font-medium mb-1 block">Filter by Role</label>
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger data-testid="select-match-role">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {uniqueRoles.map(role => (
                  <SelectItem key={role.id} value={role.id}>{role.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Minimum Score</label>
            <Select value={minScore.toString()} onValueChange={(v) => setMinScore(parseInt(v))}>
              <SelectTrigger data-testid="select-min-score">
                <SelectValue placeholder="0" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">All Scores</SelectItem>
                <SelectItem value="50">50+ (Potential)</SelectItem>
                <SelectItem value="70">70+ (Good)</SelectItem>
                <SelectItem value="85">85+ (Excellent)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {filteredMatches.length === 0 ? (
          <div className="text-center py-8 md:py-12">
            <Shield className="w-10 h-10 md:w-12 md:h-12 text-muted-foreground mx-auto mb-3 md:mb-4" />
            <h3 className="text-base md:text-lg font-medium mb-2">No Matches Found</h3>
            <p className="text-sm text-muted-foreground px-4">
              {matches.length === 0 
                ? "Candidates need completed interviews or verified credentials to be matched."
                : "No candidates match your current filters. Try adjusting the criteria."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filteredMatches.map((match, idx) => {
              const scoreBadge = getScoreBadge(match.matchScore);
              const matchKey = `${match.candidateId}-${match.roleId}`;
              const isAdded = addedMatches.has(matchKey);
              const isAdding = addingToPipeline === matchKey;
              return (
                <Card key={`${match.candidateId}-${match.roleId}-${idx}`} className={`p-3 md:p-4 hover:shadow-lg transition-shadow ${isAdded ? 'border-green-500/50 bg-green-50/10' : ''}`} data-testid={`match-card-${match.candidateId}-${match.roleId}`}>
                  <div className="flex items-start justify-between mb-2 md:mb-3">
                    <div className="flex-1 min-w-0 mr-2">
                      <h4 className="font-bold text-base md:text-lg truncate">{match.candidateName}</h4>
                      <p className="text-xs md:text-sm text-muted-foreground truncate">{match.candidateEmail}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-xl md:text-2xl font-bold ${(match.matchScore ?? 0) >= 70 ? 'text-green-600' : (match.matchScore ?? 0) >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                        {Number.isFinite(match.matchScore) ? match.matchScore : 0}%
                      </div>
                      <Badge className={`${scoreBadge.className} text-xs`}>{scoreBadge.label}</Badge>
                    </div>
                  </div>

                  <div className="mb-2 md:mb-3 p-2 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Matched for</p>
                    <p className="font-medium text-xs md:text-sm truncate">{match.roleTitle}</p>
                  </div>

                  <div className="space-y-1.5 mb-3 md:mb-4">
                    <div className="flex justify-between text-xs md:text-sm">
                      <span className="text-muted-foreground">Interview</span>
                      <span className="font-medium">{match.breakdown.interviewScore}/{40}</span>
                    </div>
                    <div className="w-full h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${(match.breakdown.interviewScore / 40) * 100}%` }} />
                    </div>
                    
                    <div className="flex justify-between text-xs md:text-sm">
                      <span className="text-muted-foreground">Credentials</span>
                      <span className="font-medium">{match.breakdown.credentialScore}/{25}</span>
                    </div>
                    <div className="w-full h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-green-500" style={{ width: `${(match.breakdown.credentialScore / 25) * 100}%` }} />
                    </div>
                    
                    <div className="flex justify-between text-xs md:text-sm">
                      <span className="text-muted-foreground">Skills</span>
                      <span className="font-medium">{match.breakdown.skillsScore}/{35}</span>
                    </div>
                    <div className="w-full h-1.5 md:h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500" style={{ width: `${(match.breakdown.skillsScore / 35) * 100}%` }} />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-2 md:mb-3">
                    {match.verifiedCredentials > 0 && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 text-xs py-0.5">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {match.verifiedCredentials} Verified
                      </Badge>
                    )}
                    {match.interviewOverallScore && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs py-0.5">
                        <Video className="w-3 h-3 mr-1" />
                        {match.interviewOverallScore}
                      </Badge>
                    )}
                    {match.hasDealBreakerIssue && (
                      <Badge variant="outline" className="bg-red-50 text-red-700 text-xs py-0.5">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Review
                      </Badge>
                    )}
                    {isAdded && (
                      <Badge variant="outline" className="bg-green-100 text-green-800 text-xs py-0.5">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        In Pipeline
                      </Badge>
                    )}
                  </div>

                  {match.matchedSkills.length > 0 && (
                    <div className="mb-2 md:mb-3">
                      <p className="text-xs text-muted-foreground mb-1">Matched Skills</p>
                      <div className="flex flex-wrap gap-1">
                        {match.matchedSkills.slice(0, 3).map((skill, i) => (
                          <Badge key={i} variant="secondary" className="text-xs py-0.5">{skill}</Badge>
                        ))}
                        {match.matchedSkills.length > 3 && (
                          <Badge variant="outline" className="text-xs py-0.5">+{match.matchedSkills.length - 3}</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2">
                    {!isAdded ? (
                      <Button 
                        size="sm" 
                        variant="default" 
                        className="w-full gap-1 text-xs md:text-sm" 
                        onClick={() => addToPipeline(match.candidateId, match.roleId)}
                        disabled={isAdding}
                        data-testid={`button-add-pipeline-${match.candidateId}-${match.roleId}`}
                      >
                        {isAdding ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Plus className="w-3 h-3" />
                        )}
                        Add to Pipeline
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="w-full gap-1 text-xs md:text-sm text-green-600" disabled>
                        <CheckCircle className="w-3 h-3" />
                        Added
                      </Button>
                    )}
                    <Link href={`/dossier/${match.candidateId}`} className="w-full sm:w-auto">
                      <Button size="sm" variant="outline" className="w-full gap-1 text-xs md:text-sm" data-testid={`button-view-dossier-${match.candidateId}`}>
                        <FileText className="w-3 h-3" />
                        <span className="sm:hidden">Profile</span>
                        <span className="hidden sm:inline">View Profile</span>
                      </Button>
                    </Link>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-4 md:mt-6 text-center text-xs md:text-sm text-muted-foreground">
          Showing {filteredMatches.length} of {matches.length} matches
        </div>
      </Card>

      <Card className="p-4 md:p-6">
        <h3 className="font-bold text-base md:text-lg mb-3 md:mb-4">How Matching Works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <div className="p-3 md:p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <Video className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
              <span className="font-medium text-sm md:text-base text-blue-900">Interview (40%)</span>
            </div>
            <p className="text-xs md:text-sm text-blue-700">AI-evaluated interview performance including role fit and communication.</p>
          </div>
          <div className="p-3 md:p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <Award className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
              <span className="font-medium text-sm md:text-base text-green-900">Credentials (25%)</span>
            </div>
            <p className="text-xs md:text-sm text-green-700">Verified certifications from Credly, LinkedIn Learning, and blockchain.</p>
          </div>
          <div className="p-3 md:p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <Briefcase className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
              <span className="font-medium text-sm md:text-base text-purple-900">Skills (35%)</span>
            </div>
            <p className="text-xs md:text-sm text-purple-700">Skills alignment with role requirements and deal-breakers.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

interface DossiersTabProps {
  candidates: any[];
  onSendDossier: (app: Application) => void;
  reviewingInterview: InterviewRecord | null;
  setReviewingInterview: Dispatch<SetStateAction<InterviewRecord | null>>;
  reviewForm: { recommendation: string; agentNotes: string };
  setReviewForm: Dispatch<SetStateAction<{ recommendation: string; agentNotes: string }>>;
  reviewErrors: { recommendation?: string; agentNotes?: string };
  setReviewErrors: Dispatch<SetStateAction<{ recommendation?: string; agentNotes?: string }>>;
  dossierResult: { url: string; score?: number; recommendation: string } | null;
  setDossierResult: Dispatch<SetStateAction<{ url: string; score?: number; recommendation: string } | null>>;
  dossierError: string | null;
  setDossierError: Dispatch<SetStateAction<string | null>>;
  openReview: (interview: InterviewRecord) => void;
}

function DossiersTab({
  candidates,
  onSendDossier,
  reviewingInterview,
  setReviewingInterview,
  reviewForm,
  setReviewForm,
  reviewErrors,
  setReviewErrors,
  dossierResult,
  setDossierResult,
  dossierError,
  setDossierError,
  openReview,
}: DossiersTabProps) {
  const [dossiers, setDossiers] = useState<DossierRecord[]>([]);
  const [interviews, setInterviews] = useState<InterviewRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingDossier, setEditingDossier] = useState<DossierRecord | null>(null);
  const [editForm, setEditForm] = useState({
    executiveSummary: "",
    agentNotes: "",
    verificationNotes: "",
    overallScore: 0,
    transcriptShareLevel: "excerpts",
  });
  const [saving, setSaving] = useState(false);

  // Review & complete dossier state (reviewingInterview, reviewForm, reviewErrors, dossierResult, dossierError lifted to AgentDashboard)


  const downloadDossierPdf = (dossier: DossierRecord) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = 20;
    
    // Company branding header
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(79, 70, 229);
    doc.text("VEXIS", margin, y + 8);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text("TALENT AGENCY", margin + 32, y + 8);
    
    // Header - Right aligned date
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`, pageWidth - margin, y + 5, { align: "right" });
    doc.text("CONFIDENTIAL", pageWidth - margin, y + 10, { align: "right" });
    
    y += 25;
    
    // Divider line
    doc.setDrawColor(79, 70, 229);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
    y += 15;
    
    // Document Title
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text("Candidate Talent Dossier", margin, y);
    y += 12;
    
    // Candidate Header Card
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, y, contentWidth, 35, 3, 3, "F");
    y += 10;
    
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 30, 30);
    doc.text(dossier.candidateName || "Candidate", margin + 10, y);
    y += 8;
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Position: ${dossier.roleTitle || "N/A"}`, margin + 10, y);
    y += 6;
    
    // Score badge
    const scoreX = pageWidth - margin - 40;
    const scoreY = y - 18;
    const score = dossier.overallScore || 0;
    const scoreColor = score >= 80 ? [16, 185, 129] : score >= 60 ? [245, 158, 11] : [239, 68, 68];
    doc.setFillColor(scoreColor[0], scoreColor[1], scoreColor[2]);
    doc.roundedRect(scoreX, scoreY, 30, 20, 3, 3, "F");
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(`${score}%`, scoreX + 15, scoreY + 13, { align: "center" });
    
    y += 20;
    
    // Section helper function
    const addSection = (title: string, content: string | string[], isList = false) => {
      if (y > pageHeight - 40) { doc.addPage(); y = 25; }
      
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(79, 70, 229);
      doc.text(title, margin, y);
      y += 8;
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);
      
      if (isList && Array.isArray(content)) {
        content.forEach(item => {
          if (y > pageHeight - 20) { doc.addPage(); y = 25; }
          const lines = doc.splitTextToSize(`•  ${item}`, contentWidth - 10);
          doc.text(lines, margin + 5, y);
          y += lines.length * 5 + 2;
        });
      } else {
        const text = Array.isArray(content) ? content.join(", ") : content;
        const lines = doc.splitTextToSize(text, contentWidth);
        doc.text(lines, margin, y);
        y += lines.length * 5;
      }
      y += 8;
    };
    
    // Executive Summary
    if (dossier.dossierJson?.executiveSummary) {
      addSection("Executive Summary", dossier.dossierJson.executiveSummary);
    }
    
    // Professional Profile
    if (dossier.dossierJson?.professionalProfile) {
      const profile = dossier.dossierJson.professionalProfile;
      
      if (profile.headline) {
        addSection("Professional Headline", profile.headline);
      }
      if (profile.coreCompetencies?.length) {
        addSection("Core Competencies", profile.coreCompetencies.join("  |  "));
      }
      if (profile.experienceHighlights?.length) {
        addSection("Experience Highlights", profile.experienceHighlights, true);
      }
    }
    
    // Interview Performance
    if (dossier.dossierJson?.interviewPerformance) {
      const perf = dossier.dossierJson.interviewPerformance;
      
      if (perf.communicationStyle) {
        addSection("Communication Style", perf.communicationStyle);
      }
      if (perf.standoutMoments?.length) {
        addSection("Interview Highlights", perf.standoutMoments, true);
      }
    }
    
    // Agent Assessment
    if (dossier.agentNotes) {
      addSection("Agent Assessment", dossier.agentNotes);
    }
    
    // Verification Status
    if (dossier.verificationNotes) {
      addSection("Verification Notes", dossier.verificationNotes);
    }
    
    // Footer on each page
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: "center" });
      doc.text("VEXIS Talent Agency - Confidential", margin, pageHeight - 10);
    }
    
    // Save
    const fileName = `VEXIS-Dossier-${dossier.candidateName?.replace(/\s+/g, "-") || dossier.id}.pdf`;
    doc.save(fileName);
    toast.success("Professional dossier downloaded");
  };

  const exportDossierJson = async (dossier: DossierRecord) => {
    if (!dossier.candidateId) {
      toast.error("Candidate ID not found for this dossier");
      return;
    }
    
    try {
      const response = await apiRequest("GET", `/api/dossier-sync/candidate/${dossier.candidateId}/export`);
      if (!response.ok) {
        throw new Error("Failed to export dossier data");
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `candidate-${dossier.candidateId}-dossier.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Dossier data exported as JSON");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export dossier data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dossiersRes, interviewsRes] = await Promise.all([
        apiRequest("GET", "/api/agent/dossiers"),
        apiRequest("GET", "/api/agent/interviews"),
      ]);
      
      if (dossiersRes.ok) {
        const dossiersData = await dossiersRes.json();
        setDossiers(dossiersData);
      }
      
      if (interviewsRes.ok) {
        const interviewsData = await interviewsRes.json();
        setInterviews(interviewsData.filter((i: InterviewRecord) => i.status === "completed"));
      }
    } catch (error) {
      console.error("Failed to load dossiers data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (dossier: DossierRecord) => {
    setEditingDossier(dossier);
    setEditForm({
      executiveSummary: dossier.dossierJson?.executiveSummary || "",
      agentNotes: dossier.agentNotes || "",
      verificationNotes: dossier.verificationNotes || "",
      overallScore: dossier.overallScore || 0,
      transcriptShareLevel: dossier.transcriptShareLevel || "excerpts",
    });
  };

  const saveDossier = async () => {
    if (!editingDossier) return;
    
    setSaving(true);
    try {
      const updatedDossierJson = {
        ...editingDossier.dossierJson,
        executiveSummary: editForm.executiveSummary,
      };
      
      const res = await apiRequest("PUT", `/api/agent/dossiers/${editingDossier.id}`, {
          dossierJson: updatedDossierJson,
          agentNotes: editForm.agentNotes,
          verificationNotes: editForm.verificationNotes,
          overallScore: editForm.overallScore,
          transcriptShareLevel: editForm.transcriptShareLevel,
        });
      
      if (res.ok) {
        toast.success("Dossier updated successfully");
        setEditingDossier(null);
        fetchData();
      } else {
        toast.error("Failed to update dossier");
      }
    } catch (error) {
      toast.error("Failed to update dossier");
    } finally {
      setSaving(false);
    }
  };

  const pendingReview = interviews.filter(i => 
    i.status === "completed" && !dossiers.some(d => d.interviewId === i.id)
  );

  // Section A: candidates whose latest application is in dossier_ready stage
  const dossierReadyItems = candidates
    .map(c => {
      const app = c.applications?.find((a: any) => a.pipeline_stage === "dossier_ready");
      return app ? { candidate: c, app } : null;
    })
    .filter(Boolean) as { candidate: any; app: Application }[];

  // Section B: candidates in outcome stage who have an associated dossier record
  const sentDossierItems = candidates
    .map(c => {
      const app = c.applications?.find((a: any) => a.pipeline_stage === "outcome");
      if (!app) return null;
      const dossier = dossiers.find(d => d.applicationId === app.id || d.candidateId === c.id);
      return dossier ? { candidate: c, app, dossier } : null;
    })
    .filter(Boolean) as { candidate: any; app: Application; dossier: DossierRecord }[];

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-muted-foreground">Loading dossiers...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dossiers</h1>
        <p className="text-muted-foreground text-sm mt-1">Send assessed candidate profiles to your clients. Dossiers are generated automatically when a candidate completes their assessment.</p>
      </div>

      {/* Section A: Ready to Send */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600" />
              Ready to Send
              {dossierReadyItems.length > 0 && (
                <Badge className="ml-2 bg-green-500 text-white animate-pulse">{dossierReadyItems.length}</Badge>
              )}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">Dossiers approved and ready to send to your client.</p>
          </div>
        </div>
        {dossierReadyItems.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No dossiers ready to send yet.</p>
            <p className="text-xs text-muted-foreground mt-1">When a candidate reaches Stage 05 in the Assessment Pipeline, their dossier will appear here ready to send to your client.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dossierReadyItems.map(({ candidate, app }) => {
                  const dossier = dossiers.find(d =>
                    String(d.applicationId) === String(app.id) ||
                    String(d.candidateId) === String(candidate.id)
                  );
                  return (
                    <TableRow key={app.id} data-testid={`row-dossier-ready-${app.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{candidate.fullName || candidate.full_name || app.candidate_name || "Unknown"}</p>
                          {(candidate.email || app.candidate_email) && (
                            <p className="text-xs text-muted-foreground">{candidate.email || app.candidate_email}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{dossier?.roleTitle || "Unknown Role"}</TableCell>
                      <TableCell>
                        {dossier?.overallScore !== undefined ? (
                          <Badge variant={dossier.overallScore >= 70 ? "default" : "secondary"}>{dossier.overallScore}%</Badge>
                        ) : (
                          <Badge variant="outline">N/A</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => onSendDossier(app)}
                          data-testid={`button-send-dossier-${app.id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Send Dossier
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Section B: Sent Dossiers */}
      <Card className="p-6">
        <div className="mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-primary" />
            Sent Dossiers ({sentDossierItems.length})
          </h2>
          <p className="text-muted-foreground text-sm mt-1">Candidates progressed to outcome stage with a dossier on file.</p>
        </div>
        {sentDossierItems.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <CheckCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No sent dossiers yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Dossiers sent to clients will be stored here for your records.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sentDossierItems.map(({ candidate, app, dossier }) => (
                  <TableRow key={app.id} data-testid={`row-sent-dossier-${app.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{candidate.fullName || candidate.full_name || app.candidate_name || "Unknown"}</p>
                        {(candidate.email || app.candidate_email) && (
                          <p className="text-xs text-muted-foreground">{candidate.email || app.candidate_email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{dossier.roleTitle || "Unknown Role"}</TableCell>
                    <TableCell>
                      <Badge variant={dossier.overallScore && dossier.overallScore >= 70 ? "default" : "secondary"}>
                        {dossier.overallScore !== undefined ? `${dossier.overallScore}%` : "N/A"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {dossier.generatedAt ? new Date(dossier.generatedAt).toLocaleDateString() : "N/A"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {(dossier.externalDossierUrl || dossier.externalDossierId) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(dossier.externalDossierUrl || `https://dossier.vexistalentagency.com/d/${dossier.externalDossierId}`, "_blank")}
                            data-testid={`button-view-dossier-${dossier.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Dossier
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => downloadDossierPdf(dossier)} data-testid={`button-download-dossier-${dossier.id}`} title="Download PDF">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditModal(dossier)} data-testid={`button-edit-dossier-${dossier.id}`} title="Edit">
                          <FileText className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {pendingReview.length > 0 && (
        <Card className="p-6 border-blue-500/30 bg-blue-500/10">
          <div className="mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2 text-white">
              <Clock className="w-5 h-5 text-blue-400" />
              Awaiting Dossier Creation ({pendingReview.length})
            </h2>
            <p className="text-muted-foreground text-sm mt-1">Completed interviews that have not yet had a dossier created.</p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingReview.map((interview) => (
                  <TableRow key={interview.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{interview.candidateName || "Unknown"}</p>
                        {interview.candidateEmail && (
                          <p className="text-xs text-muted-foreground">{interview.candidateEmail}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{interview.roleTitle || "Unknown Role"}</TableCell>
                    <TableCell>
                      {interview.completedAt ? new Date(interview.completedAt).toLocaleDateString() : "N/A"}
                    </TableCell>
                    <TableCell>
                      {interview.overallScore !== undefined ? (
                        <Badge variant={interview.overallScore >= 70 ? "default" : "secondary"}>{interview.overallScore}%</Badge>
                      ) : (
                        <Badge variant="outline">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="default" onClick={() => openReview(interview)} data-testid={`button-review-interview-${interview.id}`}>
                        <Eye className="w-4 h-4 mr-1" />
                        Review & Create Dossier
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {editingDossier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingDossier(null)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Edit Dossier
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setEditingDossier(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="mb-4 p-3 bg-muted rounded-lg">
              <p className="font-medium">{editingDossier.candidateName || "Unknown Candidate"}</p>
              <p className="text-sm text-muted-foreground">{editingDossier.roleTitle || "Unknown Role"}</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Executive Summary</label>
                <Textarea
                  value={editForm.executiveSummary}
                  onChange={(e) => setEditForm({...editForm, executiveSummary: e.target.value})}
                  placeholder="Brief overview of the candidate..."
                  rows={4}
                  data-testid="input-executive-summary"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Agent Notes (Internal)</label>
                <Textarea
                  value={editForm.agentNotes}
                  onChange={(e) => setEditForm({...editForm, agentNotes: e.target.value})}
                  placeholder="Internal notes for agent reference..."
                  rows={3}
                  data-testid="input-agent-notes"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Verification Notes</label>
                <Textarea
                  value={editForm.verificationNotes}
                  onChange={(e) => setEditForm({...editForm, verificationNotes: e.target.value})}
                  placeholder="Notes about verification findings..."
                  rows={2}
                  data-testid="input-verification-notes"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Overall Score</label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={editForm.overallScore}
                    onChange={(e) => setEditForm({...editForm, overallScore: parseInt(e.target.value) || 0})}
                    data-testid="input-overall-score"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Transcript Share Level</label>
                  <Select
                    value={editForm.transcriptShareLevel}
                    onValueChange={(value) => setEditForm({...editForm, transcriptShareLevel: value})}
                  >
                    <SelectTrigger data-testid="select-transcript-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full Transcript</SelectItem>
                      <SelectItem value="excerpts">Selected Excerpts</SelectItem>
                      <SelectItem value="none">No Transcript</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditingDossier(null)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={saveDossier} disabled={saving} className="flex-1 gap-2" data-testid="button-save-dossier">
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Save Changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

    </div>
  );
}

// ===== TODAY TAB COMPONENT =====
interface TodayData {
  priorityCounts: {
    interviewsToReview: number;
    candidatesAwaitingApproval: number;
    matchesPending: number;
    employerDecisionsOutstanding: number;
  };
  actionQueue: Array<{
    type: string;
    label: string;
    candidateName: string;
    roleName?: string;
    timeSince: string;
    ctaLabel: string;
    deepLink: string;
    entityId: number;
  }>;
  exceptions: Array<{
    type: string;
    reason: string;
    candidateName?: string;
    roleName?: string;
    entityId: number;
    fixAction: string;
    deepLink: string;
  }>;
  completedToday: {
    interviewsReviewed: number;
    candidatesApproved: number;
    candidatesDeclined: number;
    matchesCreated: number;
    shortlistsSent: number;
    dossiersPublished: number;
  };
  totalActions: number;
}

const ONBOARDING_STEPS = [
  {
    id: "connect_ats",
    title: "Connect Your ATS",
    description: "Link your applicant tracking system so candidates can be sent directly to Vexis for assessment. Takes 2 minutes.",
    button: "Connect Your ATS →",
    tab: "settings" as AgentTab,
  },
  {
    id: "create_role_brief",
    title: "Create your first Role Brief",
    description: "Build the question set for your first role. 5-7 questions tailored to what the role actually requires - not generic templates.",
    button: "Create Role Brief →",
    tab: "roles" as AgentTab,
  },
  {
    id: "send_interview_link",
    title: "Send your first interview link",
    description: "Add a candidate and send them their personalised interview token link. They'll receive it by email and SMS - no app needed.",
    button: "Add a Candidate →",
    tab: "registrations" as AgentTab,
  },
  {
    id: "review_dossier",
    title: "Review and create your first dossier",
    description: "Once a candidate submits their interview, review their scored transcript and create a boardroom-ready dossier in one click.",
    button: "Go to Review Queue →",
    tab: "pipeline" as AgentTab,
  },
];

function OnboardingChecklist({ setActiveTab }: { setActiveTab: (tab: AgentTab) => void }) {
  const storageKey = "vexis_onboarding_completed";
  const dismissKey = "vexis_onboarding_dismissed";

  const [completed, setCompleted] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
  });
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(dismissKey) === "true");

  const allDone = ONBOARDING_STEPS.every(s => completed.includes(s.id));

  useEffect(() => {
    if (allDone) {
      const t = setTimeout(() => { setDismissed(true); localStorage.setItem(dismissKey, "true"); }, 3000);
      return () => clearTimeout(t);
    }
  }, [allDone]);

  if (dismissed) return null;

  const toggleStep = (id: string) => {
    setCompleted(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div className="border border-primary/20 rounded-xl p-4 md:p-5 bg-primary/5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-base md:text-lg">Get started — 4 steps to your first assessment</h2>
          <p className="text-xs text-muted-foreground mt-0.5">{completed.length} of {ONBOARDING_STEPS.length} complete</p>
        </div>
        <button
          onClick={() => { setDismissed(true); localStorage.setItem(dismissKey, "true"); }}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="onboarding-dismiss"
        >
          Dismiss
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ONBOARDING_STEPS.map((step, idx) => {
          const done = completed.includes(step.id);
          return (
            <div
              key={step.id}
              className={`flex gap-3 p-3 rounded-lg border transition-colors ${done ? "border-green-500/30 bg-green-500/5" : "border-border bg-background"}`}
            >
              <button
                onClick={() => toggleStep(step.id)}
                className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${done ? "bg-green-500 border-green-500" : "border-muted-foreground hover:border-primary"}`}
                data-testid={`onboarding-check-${step.id}`}
              >
                {done && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${done ? "line-through text-muted-foreground" : ""}`}>
                  {idx + 1}. {step.title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{step.description}</p>
                {!done && (
                  <button
                    className="mt-2 text-xs font-medium text-primary hover:underline"
                    onClick={() => { setActiveTab(step.tab); }}
                    data-testid={`onboarding-btn-${step.id}`}
                  >
                    {step.button}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {allDone && (
        <p className="text-center text-sm text-green-600 font-medium">All steps complete — this checklist will close shortly.</p>
      )}
    </div>
  );
}

function TodayTab({ setActiveTab, priorityItems, onOpenJobQuestions, onResendInvite, userName }: {
  setActiveTab: (tab: AgentTab) => void;
  priorityItems?: {
    underReview: Array<{ applicationId: number; candidateName: string; roleTitle: string; hoursInStage: number }>;
    needsReview: Array<{ applicationId: number; candidateName: string; roleTitle: string; pipelineStage: string }>;
    expiringLinks: Array<{ applicationId: number; candidateName: string; hoursLeft: number }>;
    rolesNeedingQuestions: Array<{ roleId: number; title: string }>;
  };
  onOpenJobQuestions?: (roleId: number) => void;
  onResendInvite?: (applicationId: number) => void;
  userName?: string;
}) {
  const [data, setData] = useState<TodayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTodayData();
  }, []);

  const fetchTodayData = async () => {
    try {
      setLoading(true);
      const res = await apiRequest("GET", "/api/agent/today");
      if (!res.ok) throw new Error("Failed to load data");
      const result = await res.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError("Unable to load today's tasks");
      console.error("Today tab error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeepLink = (deepLink: string) => {
    const [tab] = deepLink.split("?");
    setActiveTab(tab as AgentTab);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading your daily tasks...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-muted-foreground">{error || "Something went wrong"}</p>
          <Button onClick={fetchTodayData} className="mt-4" variant="outline" data-testid="today-retry-btn">
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  const { priorityCounts, actionQueue, exceptions, completedToday } = data;
  const hasNoActions = actionQueue.length === 0 && exceptions.length === 0;
  const totalCompleted = completedToday.interviewsReviewed + 
    completedToday.candidatesApproved + 
    completedToday.candidatesDeclined + 
    completedToday.matchesCreated + 
    completedToday.shortlistsSent +
    completedToday.dossiersPublished;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = userName ? userName.split(" ")[0] : "";

  const hasPriorityItems = priorityItems && (
    priorityItems.underReview.length > 0 ||
    priorityItems.needsReview.length > 0 ||
    priorityItems.expiringLinks.length > 0 ||
    priorityItems.rolesNeedingQuestions.length > 0
  );

  return (
    <div className="space-y-6">
      {/* Time-aware greeting */}
      <div>
        <h1 className="text-2xl font-bold text-foreground" data-testid="today-greeting">
          {greeting}{firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {new Date().toLocaleDateString("en-AU", { weekday: "long", month: "long", day: "numeric" })}
        </p>
      </div>

      {/* ACTION REQUIRED — Priority section */}
      {priorityItems && hasPriorityItems && (
        <div className="space-y-2">
          <h2 className="text-base font-semibold text-foreground">Action Required</h2>
          <div className="space-y-2">
            {priorityItems.underReview.map(item => (
              <div key={`ur-${item.applicationId}`} className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-blue-500/10 border border-blue-500/20" data-testid={`priority-under-review-${item.applicationId}`}>
                <span className="text-sm text-foreground font-medium">{item.candidateName} — <span className="text-muted-foreground font-normal">{item.roleTitle}</span></span>
                <button
                  onClick={() => setActiveTab("pipeline")}
                  className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white pulse-blue hover:bg-blue-700 transition-colors"
                  data-testid={`button-priority-review-${item.applicationId}`}
                >
                  Review Now
                </button>
              </div>
            ))}
            {priorityItems.needsReview.map(item => (
              <div key={`nr-${item.applicationId}`} className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid={`priority-needs-review-${item.applicationId}`}>
                <span className="text-sm text-foreground font-medium">{item.candidateName} — <span className="text-muted-foreground font-normal">{item.roleTitle}</span></span>
                <button
                  onClick={() => setActiveTab("pipeline")}
                  className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500 text-white pulse-amber hover:bg-amber-600 transition-colors"
                  data-testid={`button-priority-needs-review-${item.applicationId}`}
                >
                  Review Now →
                </button>
              </div>
            ))}
            {priorityItems.expiringLinks.map(item => (
              <div key={`el-${item.applicationId}`} className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-orange-500/10 border border-orange-500/20" data-testid={`priority-expiring-${item.applicationId}`}>
                <span className="text-sm text-foreground font-medium">{item.candidateName} — <span className="text-muted-foreground font-normal">link expires in {item.hoursLeft}h</span></span>
                <button
                  onClick={() => onResendInvite?.(item.applicationId)}
                  className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-500 text-white pulse-amber hover:bg-orange-600 transition-colors"
                  data-testid={`button-priority-resend-${item.applicationId}`}
                >
                  Resend Link
                </button>
              </div>
            ))}
            {priorityItems.rolesNeedingQuestions.map(item => (
              <div key={`rq-${item.roleId}`} className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid={`priority-role-questions-${item.roleId}`}>
                <span className="text-sm text-foreground font-medium">Role needs questions: <span className="text-muted-foreground font-normal">{item.title}</span></span>
                <button
                  onClick={() => onOpenJobQuestions?.(item.roleId)}
                  className="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-500 text-white pulse-amber hover:bg-amber-600 transition-colors"
                  data-testid={`button-priority-setup-questions-${item.roleId}`}
                >
                  Set Up Questions
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {priorityItems && !hasPriorityItems && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-green-500/10 border border-green-500/20" data-testid="priority-all-clear">
          <span className="text-sm font-medium text-green-700 dark:text-green-400">✓ All clear — nothing needs attention right now</span>
        </div>
      )}

      {/* Onboarding Checklist */}
      <OnboardingChecklist setActiveTab={setActiveTab} />

      {/* Sub-header with refresh */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">
          Here's what needs your attention today — in priority order.
        </p>
        <Button variant="outline" size="sm" onClick={fetchTodayData} className="gap-2" data-testid="today-refresh-btn">
          <RefreshCw className="w-4 h-4" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* SECTION 1: Priority Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card 
          className="p-4 cursor-pointer hover:border-primary/50 transition-all bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20"
          onClick={() => setActiveTab("pipeline")}
          data-testid="today-card-interviews"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Interviews to Review</p>
              <p className="text-3xl font-bold mt-1 text-blue-600 dark:text-blue-400">{priorityCounts.interviewsToReview}</p>
            </div>
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Video className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Completed, awaiting review</p>
        </Card>

        <Card 
          className="p-4 cursor-pointer hover:border-primary/50 transition-all bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20"
          onClick={() => setActiveTab("approval")}
          data-testid="today-card-approval"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Awaiting Approval</p>
              <p className="text-3xl font-bold mt-1 text-purple-600 dark:text-purple-400">{priorityCounts.candidatesAwaitingApproval}</p>
            </div>
            <div className="p-2 rounded-lg bg-purple-500/20">
              <UserCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Ready for decision</p>
        </Card>

        <Card 
          className="p-4 cursor-pointer hover:border-primary/50 transition-all bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20"
          onClick={() => setActiveTab("matching")}
          data-testid="today-card-matching"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Matches Pending</p>
              <p className="text-3xl font-bold mt-1 text-green-600 dark:text-green-400">{priorityCounts.matchesPending}</p>
            </div>
            <div className="p-2 rounded-lg bg-green-500/20">
              <GitMerge className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Approved, needs matching</p>
        </Card>

        <Card 
          className="p-4 cursor-pointer hover:border-primary/50 transition-all bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20"
          onClick={() => setActiveTab("shortlists")}
          data-testid="today-card-decisions"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Employer Decisions</p>
              <p className="text-3xl font-bold mt-1 text-orange-600 dark:text-orange-400">{priorityCounts.employerDecisionsOutstanding}</p>
            </div>
            <div className="p-2 rounded-lg bg-orange-500/20">
              <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Awaiting 3+ days</p>
        </Card>
      </div>

      {/* SECTION 2: Action Queue */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-primary" />
            Review queue
          </h2>
          {actionQueue.length > 0 && (
            <Badge variant="secondary">{actionQueue.length} action{actionQueue.length !== 1 ? "s" : ""}</Badge>
          )}
        </div>

        {hasNoActions ? (
          <Card className="p-8 text-center border-green-500/30 bg-green-500/5">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="font-semibold text-green-700 dark:text-green-400">You're all caught up.</p>
            <p className="text-muted-foreground text-sm mt-2 max-w-sm mx-auto">No candidates waiting for your attention right now. Check the Pipeline tab to follow up on active roles, or send new assessment links.</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => setActiveTab("pipeline")} data-testid="today-empty-pipeline-btn">
              View Pipeline
            </Button>
          </Card>
        ) : (
          <div className="space-y-2">
            {actionQueue.slice(0, 10).map((action, idx) => (
              <Card 
                key={`${action.type}-${action.entityId}`}
                className="p-4 hover:border-primary/30 transition-all"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs shrink-0">
                        {action.type === "review_interview" && "Review"}
                        {action.type === "approve_candidate" && "Approve"}
                        {action.type === "match_candidate" && "Match"}
                        {action.type === "follow_up_employer" && "Follow Up"}
                      </Badge>
                      <span className="font-medium truncate">{action.candidateName}</span>
                      {action.roleName && (
                        <span className="text-muted-foreground text-sm truncate">
                          for {action.roleName}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {action.timeSince}
                    </p>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => handleDeepLink(action.deepLink)}
                    className="shrink-0 w-full sm:w-auto"
                    data-testid={`today-action-${idx}`}
                  >
                    {action.ctaLabel}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* SECTION 3: Watch List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Watch list - may need a nudge
          </h2>
          {exceptions.length > 0 && <Badge variant="destructive">{exceptions.length}</Badge>}
        </div>
        {exceptions.length === 0 ? (
          <p className="text-sm text-muted-foreground px-1">Nothing on the watch list.</p>
        ) : (

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {exceptions.slice(0, 8).map((exception, idx) => (
              <Card 
                key={`${exception.type}-${exception.entityId}`}
                className="p-3 border-amber-500/30 bg-amber-500/5"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {exception.candidateName || exception.roleName}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      {exception.reason}
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleDeepLink(exception.deepLink)}
                    className="shrink-0 text-xs h-7"
                    data-testid={`today-exception-${idx}`}
                  >
                    {exception.fixAction}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* SECTION 4: Recent Activity */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          Recent activity
        </h2>

        <Card className="p-4">
          {totalCompleted === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-2">
              No activity in the last 24 hours.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {completedToday.interviewsReviewed > 0 && (
                <div className="text-center" data-testid="completed-interviews-reviewed">
                  <p className="text-2xl font-bold text-green-600" data-testid="count-interviews-reviewed">{completedToday.interviewsReviewed}</p>
                  <p className="text-xs text-muted-foreground">Interviews Reviewed</p>
                </div>
              )}
              {completedToday.candidatesApproved > 0 && (
                <div className="text-center" data-testid="completed-candidates-approved">
                  <p className="text-2xl font-bold text-green-600" data-testid="count-candidates-approved">{completedToday.candidatesApproved}</p>
                  <p className="text-xs text-muted-foreground">Candidates Approved</p>
                </div>
              )}
              {completedToday.candidatesDeclined > 0 && (
                <div className="text-center" data-testid="completed-candidates-declined">
                  <p className="text-2xl font-bold text-red-500" data-testid="count-candidates-declined">{completedToday.candidatesDeclined}</p>
                  <p className="text-xs text-muted-foreground">Candidates Declined</p>
                </div>
              )}
              {completedToday.matchesCreated > 0 && (
                <div className="text-center" data-testid="completed-matches-created">
                  <p className="text-2xl font-bold text-blue-600" data-testid="count-matches-created">{completedToday.matchesCreated}</p>
                  <p className="text-xs text-muted-foreground">Matches Created</p>
                </div>
              )}
              {completedToday.shortlistsSent > 0 && (
                <div className="text-center" data-testid="completed-shortlists-sent">
                  <p className="text-2xl font-bold text-purple-600" data-testid="count-shortlists-sent">{completedToday.shortlistsSent}</p>
                  <p className="text-xs text-muted-foreground">Shortlists Sent</p>
                </div>
              )}
              {completedToday.dossiersPublished > 0 && (
                <div className="text-center" data-testid="completed-dossiers-published">
                  <p className="text-2xl font-bold text-cyan-600" data-testid="count-dossiers-published">{completedToday.dossiersPublished}</p>
                  <p className="text-xs text-muted-foreground">Dossiers Published</p>
                </div>
              )}
              {totalCompleted === 0 && (
                <div className="col-span-full text-center py-2">
                  <p className="text-muted-foreground text-sm">Start completing tasks to see your progress</p>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}


interface Agent {
  id: number;
  email: string;
  fullName: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

function AgentManagementSection() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAgent, setNewAgent] = useState({ email: "", fullName: "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchAgents = async () => {
    try {
      const res = await apiRequest("GET", "/api/agent/agents");
      if (res.ok) {
        const data = await res.json();
        setAgents(data);
      }
    } catch (err) {
      console.error("Failed to fetch agents:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const handleAddAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    try {
      const res = await apiRequest("POST", "/api/agent/agents", newAgent);
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to add agent");
      }
      
      setSuccess("Agent added successfully");
      setNewAgent({ email: "", fullName: "", password: "" });
      setShowAddForm(false);
      fetchAgents();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (agentId: number) => {
    try {
      const res = await apiRequest("PATCH", `/api/agent/agents/${agentId}/toggle`);
      
      if (res.ok) {
        fetchAgents();
      }
    } catch (err) {
      console.error("Failed to toggle agent:", err);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Users className="w-4 h-4" />
          Agent Management
        </h3>
        <Button 
          size="sm" 
          onClick={() => setShowAddForm(!showAddForm)}
          data-testid="button-add-agent"
        >
          <UserPlus className="w-4 h-4 mr-1" />
          Add Agent
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-600 dark:text-green-400 text-sm">
          {success}
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAddAgent} className="mb-6 p-4 bg-muted/50 rounded-lg space-y-3">
          <div>
            <label className="text-sm font-medium">Full Name</label>
            <Input
              value={newAgent.fullName}
              onChange={(e) => setNewAgent({ ...newAgent, fullName: e.target.value })}
              placeholder="John Smith"
              required
              data-testid="input-agent-name"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <Input
              type="email"
              value={newAgent.email}
              onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
              placeholder="agent@vexistalentagency.com"
              required
              data-testid="input-agent-email"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Password</label>
            <Input
              type="password"
              value={newAgent.password}
              onChange={(e) => setNewAgent({ ...newAgent, password: e.target.value })}
              placeholder="Minimum 6 characters"
              required
              minLength={6}
              data-testid="input-agent-password"
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" data-testid="button-save-agent">
              Save Agent
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {loading ? (
          <p className="text-muted-foreground text-sm">Loading agents...</p>
        ) : agents.length === 0 ? (
          <p className="text-muted-foreground text-sm">No agents found</p>
        ) : (
          agents.map((agent) => (
            <div 
              key={agent.id} 
              className={`flex items-center justify-between p-3 rounded-lg border ${
                agent.isActive ? "bg-card" : "bg-muted/50 opacity-60"
              }`}
              data-testid={`agent-row-${agent.id}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{agent.fullName}</span>
                  <Badge variant={agent.role === "ADMIN" ? "default" : "secondary"} className="text-xs">
                    {agent.role === "ADMIN" ? "Admin" : "Agent"}
                  </Badge>
                  {!agent.isActive && (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      Inactive
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{agent.email}</p>
                {agent.lastLoginAt && (
                  <p className="text-xs text-muted-foreground">
                    Last login: {new Date(agent.lastLoginAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              {agent.role !== "ADMIN" && (
                <Button
                  size="sm"
                  variant={agent.isActive ? "destructive" : "outline"}
                  onClick={() => handleToggleActive(agent.id)}
                  data-testid={`button-toggle-agent-${agent.id}`}
                >
                  {agent.isActive ? "Deactivate" : "Activate"}
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Agents can log in at /login with their email and password. Deactivated agents cannot log in.
      </p>
    </Card>
  );
}

interface IntegrationHealthRow {
  connectionId: string;
  organisationId: number;
  displayName: string;
  connectionStatus: string;
  tokenExpiresAt: string | null;
  lastWriteBack: string | null;
  rateLimitRemaining: number | null;
  lastError: string | null;
  lastTokenRefresh: string | null;
}

function IntegrationHealthPanel() {
  const [rows, setRows] = useState<IntegrationHealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState<string | null>(null);

  function timeAgoOrNever(iso: string | null): string {
    if (!iso) return "Never";
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return `${Math.floor(diff / 60000)}m ago`;
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  function expiresIn(iso: string | null): { label: string; critical: boolean } {
    if (!iso) return { label: "Unknown", critical: true };
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return { label: "Expired", critical: true };
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const label = hours < 1 ? `${mins}m` : hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
    return { label, critical: hours < 1 };
  }

  async function load() {
    try {
      const r = await apiRequest("GET", "/api/ats/admin/connections");
      if (r.ok) {
        const data = await r.json();
        setRows(data.connections || []);
      }
    } finally {
      setLoading(false);
    }
  }

  async function refreshToken(connectionId: string) {
    setRefreshing(connectionId);
    try {
      await apiRequest("POST", `/api/ats/admin/connections/${connectionId}/refresh`);
      await load();
    } finally {
      setRefreshing(null);
    }
  }

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, []);

  const criticalConnections = rows.filter((r) => {
    if (r.connectionStatus === "error") return true;
    const wb = r.lastWriteBack ? (Date.now() - new Date(r.lastWriteBack).getTime()) / 3600000 : Infinity;
    if (wb > 72) return true;
    const rl = r.rateLimitRemaining;
    if (rl !== null && rl < 10) return true;
    return false;
  });

  return (
    <Card className="p-6 mt-4" data-testid="integration-health-panel">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setOpen(!open)}>
        <h3 className="font-semibold flex items-center gap-2">
          <Share2 className="w-4 h-4" />
          Integration Health
          {criticalConnections.length > 0 && (
            <Badge variant="destructive" className="ml-2 text-xs">
              {criticalConnections.length} issue{criticalConnections.length > 1 ? "s" : ""}
            </Badge>
          )}
        </h3>
        <span className="text-muted-foreground text-xs">{open ? "Hide" : "Show"}</span>
      </div>

      {open && (
        <div className="mt-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active connections across any organisation.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="integration-health-table">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-left py-2 pr-4">Org</th>
                    <th className="text-left py-2 pr-4">Platform</th>
                    <th className="text-left py-2 pr-4">Status</th>
                    <th className="text-left py-2 pr-4">Token expires</th>
                    <th className="text-left py-2 pr-4">Last write-back</th>
                    <th className="text-left py-2 pr-4">Rate limit</th>
                    <th className="text-left py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const exp = expiresIn(row.tokenExpiresAt);
                    const wb = row.lastWriteBack ? (Date.now() - new Date(row.lastWriteBack).getTime()) / 3600000 : Infinity;
                    const rl = row.rateLimitRemaining;
                    const tokenExpiring = row.tokenExpiresAt && (new Date(row.tokenExpiresAt).getTime() - Date.now()) < 2 * 3600 * 1000;
                    return (
                      <tr key={row.connectionId} className="border-b border-border/40 hover:bg-muted/20">
                        <td className="py-2 pr-4 font-mono text-muted-foreground">{row.organisationId}</td>
                        <td className="py-2 pr-4 font-medium">{row.displayName}</td>
                        <td className="py-2 pr-4">
                          <Badge variant={row.connectionStatus === "active" ? "default" : row.connectionStatus === "error" ? "destructive" : "secondary"} className="text-xs">
                            {row.connectionStatus}
                          </Badge>
                          {row.lastError && (
                            <p className="text-destructive mt-0.5 text-xs max-w-[200px] truncate" title={row.lastError}>{row.lastError}</p>
                          )}
                        </td>
                        <td className={`py-2 pr-4 ${exp.critical ? "text-destructive font-semibold" : ""}`}>{exp.label}</td>
                        <td className={`py-2 pr-4 ${wb > 48 ? "text-destructive" : ""}`}>{timeAgoOrNever(row.lastWriteBack)}</td>
                        <td className={`py-2 pr-4 ${rl !== null && rl < 20 ? "text-amber-500" : ""}`}>
                          {rl !== null ? `${rl}%` : "N/A"}
                        </td>
                        <td className="py-2">
                          {tokenExpiring && (
                            <Button size="sm" variant="outline" className="text-xs h-6 px-2"
                              disabled={refreshing === row.connectionId}
                              onClick={() => refreshToken(row.connectionId)}
                              data-testid={`btn-refresh-token-${row.connectionId}`}>
                              {refreshing === row.connectionId ? "..." : "Refresh"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground mt-3">Auto-refreshes every 60 seconds.</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

const DEFAULT_QUESTION_TEXTS = [
  "Tell us about your background and what drew you to this role.",
  "Describe a challenging situation you faced at work and how you resolved it.",
  "What does success look like to you in this type of role?",
  "How do you manage competing priorities when everything feels urgent?",
  "What questions do you have about the role or the organisation?",
];

export default function AgentDashboard() {
  const { user, isLoading: authLoading, logout } = useRequireAuth(["ADMIN", "RECRUITMENT_AGENT"]);
  const [location, setLocation] = useLocation();
  const planEnforcement = usePlan();

  const { isOpen: tourOpen, shouldAutoStart, startTour, closeTour, completeTour, resetCounter } = useOnboardingTour();
  
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"table" | "kanban">("kanban");
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [verification, setVerification] = useState<AgentVerification>({
    identity_verified: false,
    right_to_work_verified: false,
    authenticity_verified: false,
    agent_notes: "",
    agent_summary: "",
    flagged_issues: [],
  });
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [interviewVideos, setInterviewVideos] = useState<InterviewVideo[]>([]);
  const [interviewVideoStatus, setInterviewVideoStatus] = useState<InterviewVideoStatus | null>(null);
  const [interviewSessionId, setInterviewSessionId] = useState<string | null>(null);
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  const [confirmDeleteVideoId, setConfirmDeleteVideoId] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [deletingAllVideos, setDeletingAllVideos] = useState(false);
  const [activeTab, setActiveTab] = useState<AgentTab>("overview");
  const [helpSubTab, setHelpSubTab] = useState<"howItWorks" | "faq">("howItWorks");
  const [atsBannerError, setAtsBannerError] = useState<boolean>(false);
  const [analytics, setAnalytics] = useState<{
    activeRoles: number;
    totalCandidates: number;
    pendingReview: number;
    completedInterviews: number;
    publishedDossiers: number;
    shortlistedCandidates: number;
    pipelineStats: Record<string, number>;
  } | null>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [editJobForm, setEditJobForm] = useState({
    title: "",
    location: "",
    status: "",
    description: "",
    salary_range: "",
    date_required: "",
    employment_type: "",
    work_mode: "",
  });
  const [savingJob, setSavingJob] = useState(false);
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [showNewCandidateDialog, setShowNewCandidateDialog] = useState(false);
  const [showRoleLimitModal, setShowRoleLimitModal] = useState(false);
  const [showCandidateLimitModal, setShowCandidateLimitModal] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({
    candidate_submitted: true,
    ai_scoring_complete: true,
    reminder_48h: true,
    role_expiring: true,
    billing_renewal: true,
  });
  const [notifPrefsLoaded, setNotifPrefsLoaded] = useState(false);
  const [savingNotifPref, setSavingNotifPref] = useState<string | null>(null);
  const [defaultSettings, setDefaultSettings] = useState({ interviewDurationSeconds: 60, inviteExpiryHours: 48, defaultQuestionCount: 5 });
  const [defaultSettingsLoaded, setDefaultSettingsLoaded] = useState(false);
  const [savingDefaultSettings, setSavingDefaultSettings] = useState(false);
  // Team Members (Settings tab)
  const [teamMembers, setTeamMembers] = useState<Array<{ id: number; email: string; role: string }>>([]);
  const [teamMeta, setTeamMeta] = useState<{ currentSeats: number; maxSeats: number | null; seatLimitExceeded: boolean; tierDisplayName: string | null }>({ currentSeats: 0, maxSeats: null, seatLimitExceeded: false, tierDisplayName: null });
  const [teamLoaded, setTeamLoaded] = useState(false);
  const [teamInviteEmail, setTeamInviteEmail] = useState("");
  const [teamInviteName, setTeamInviteName] = useState("");
  const [teamInviting, setTeamInviting] = useState(false);
  const [teamInviteError, setTeamInviteError] = useState<string | null>(null);
  const [teamInviteSuccess, setTeamInviteSuccess] = useState<string | null>(null);
  const [newCandidate, setNewCandidate] = useState({ fullName: "", email: "", phone: "", resumeText: "" });
  const [creatingCandidate, setCreatingCandidate] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState<any | null>(null);
  const [editCandidateForm, setEditCandidateForm] = useState({ fullName: "", email: "", phone: "", resumeText: "" });
  const [savingCandidate, setSavingCandidate] = useState(false);
  const [deletingCandidateId, setDeletingCandidateId] = useState<number | null>(null);
  const [confirmDeleteCandidate, setConfirmDeleteCandidate] = useState<{id: number; name: string} | null>(null);
  const [confirmDeleteJob, setConfirmDeleteJob] = useState<{id: string; title: string} | null>(null);
  const [confirmDeleteEnquiry, setConfirmDeleteEnquiry] = useState<{id: number; type: string} | null>(null);
  const [confirmDeleteApplication, setConfirmDeleteApplication] = useState<{id: number; name: string} | null>(null);
  const [sendDossierModal, setSendDossierModal] = useState<{ app: Application; step: "confirm" | "email_prompt" | "sending" | "done" | "error"; email?: string; sentAt?: string; errorMsg?: string } | null>(null);
  const [emailOverride, setEmailOverride] = useState("");
  const [outcomeModal, setOutcomeModal] = useState<{ app: Application; step: "select" | "submitting" | "done"; outcome?: string; topicNote?: string } | null>(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<Set<number>>(new Set());
  const [feedbackRating, setFeedbackRating] = useState<Record<number, number>>({});
  const [feedbackHover, setFeedbackHover] = useState<Record<number, number>>({});
  const [feedbackComments, setFeedbackComments] = useState<Record<number, string>>({});
  const [interviews, setInterviews] = useState<InterviewRecord[]>([]);
  const [feedbackSaving, setFeedbackSaving] = useState<Set<number>>(new Set());
  const [reviewingInterview, setReviewingInterview] = useState<InterviewRecord | null>(null);
  const [reviewForm, setReviewForm] = useState({ recommendation: "", agentNotes: "" });
  const [reviewErrors, setReviewErrors] = useState<{ recommendation?: string; agentNotes?: string }>({});
  const [dossierResult, setDossierResult] = useState<{ url: string; score?: number; recommendation: string } | null>(null);
  const [dossierError, setDossierError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);

  const openReview = (interview: InterviewRecord) => {
    console.log('[review] button clicked — interviewId:', interview.id, 'applicationId:', interview.applicationId);
    setReviewingInterview(interview);
    setReviewForm({ recommendation: "", agentNotes: "" });
    setReviewErrors({});
    setDossierResult(null);
    setDossierError(null);
    if (interview.applicationId) {
      apiRequest("PATCH", `/api/agent/applications/${interview.applicationId}/pipeline-stage`, { pipelineStage: "under_review" })
        .catch((err) => console.warn('[review] Could not pre-set under_review stage:', err));
    }
  };

  const closeReview = () => {
    setReviewingInterview(null);
    setDossierResult(null);
    setDossierError(null);
    setCompleting(false);
  };

  const completeDossier = async () => {
    if (!reviewingInterview) return;
    const errors: { recommendation?: string; agentNotes?: string } = {};
    if (!reviewForm.recommendation) errors.recommendation = "Please select a recommendation.";
    if (!reviewForm.agentNotes || reviewForm.agentNotes.trim().length < 5) errors.agentNotes = "Notes are required (minimum 5 characters).";
    if (Object.keys(errors).length > 0) { setReviewErrors(errors); return; }
    setReviewErrors({});
    setCompleting(true);
    setDossierError(null);
    const url = `/api/agent/interviews/${reviewingInterview.id}/dossier/complete-and-sync`;
    console.log('[review] submitting to:', url, '| interviewId:', reviewingInterview.id, '| applicationId:', reviewingInterview.applicationId);
    try {
      const res = await apiRequest("POST", url, { recommendation: reviewForm.recommendation, agentNotes: reviewForm.agentNotes });
      console.log('[review] API response status:', res.status);
      const data = await res.json();
      console.log('[review] API response body:', JSON.stringify(data));
      if (res.ok) {
        setDossierResult({ url: data.externalDossierUrl, score: data.overallScore, recommendation: data.recommendation });
        fetchCandidates();
      } else if (res.status === 502 && data.dossierSaved) {
        setDossierError(data.error || "Dossier site unavailable — your review has been saved. Try again in a moment.");
      } else {
        setDossierError(data.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error('[review] fetch threw:', err);
      setDossierError("Dossier site unavailable — your review has been saved. Try creating the dossier again in a moment.");
    } finally {
      setCompleting(false);
    }
  };

  const submitRecruiterFeedback = async (appId: number) => {
    const rating = feedbackRating[appId];
    if (!rating) return;
    setFeedbackSaving(prev => new Set([...prev, appId]));
    try {
      await apiRequest("POST", "/api/agent/feedback", { applicationId: appId, rating, comments: feedbackComments[appId]?.trim() || null });
      setFeedbackSubmitted(prev => new Set([...prev, appId]));
    } catch {
      setFeedbackSubmitted(prev => new Set([...prev, appId]));
    } finally {
      setFeedbackSaving(prev => { const s = new Set(prev); s.delete(appId); return s; });
    }
  };
  const [outcomeTopicNote, setOutcomeTopicNote] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [candidateVideoModal, setCandidateVideoModal] = useState<{
    candidateId: number;
    candidateName: string;
    interviews: Array<{
      interviewId: number;
      applicationId: number;
      roleTitle: string;
      status: string;
      completedAt: string | null;
      videos: Array<{
        questionId: number;
        questionText: string;
        storagePath: string;
        downloadUrl: string;
        timestamp: string;
      }>;
    }>;
  } | null>(null);
  const [loadingCandidateVideos, setLoadingCandidateVideos] = useState(false);
  const [enquiries, setEnquiries] = useState<ContactEnquiry[]>([]);
  const [loadingEnquiries, setLoadingEnquiries] = useState(false);
  const [enquiryFilter, setEnquiryFilter] = useState<"all" | "employer" | "candidate">("all");
  const [invites, setInvites] = useState<any[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [selectedRoleForInvite, setSelectedRoleForInvite] = useState<string>("");
  const [inviteExpiryHours, setInviteExpiryHours] = useState(72);
  const [inviteQuestionCount, setInviteQuestionCount] = useState(5);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [generatedInviteLink, setGeneratedInviteLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sendingInviteIds, setSendingInviteIds] = useState<Set<string>>(new Set());
  const [selectedJobForQuestions, setSelectedJobForQuestions] = useState<Job | null>(null);
  const [jobQuestions, setJobQuestions] = useState<any[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [regeneratingQuestions, setRegeneratingQuestions] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<any | null>(null);
  const [editQuestionForm, setEditQuestionForm] = useState({ questionText: "", questionType: "", category: "", timeLimit: 30 });
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [newQuestionForm, setNewQuestionForm] = useState({ questionText: "", questionType: "behavioral", category: "general", timeLimit: 30 });
  const [addingQuestion, setAddingQuestion] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [questionSaveBanner, setQuestionSaveBanner] = useState(false);
  const [questionSaveBannerTimer, setQuestionSaveBannerTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [editJobTab, setEditJobTab] = useState<"details" | "questions">("details");
  const [jobQuestionCounts, setJobQuestionCounts] = useState<Record<string, { count: number; hasCustom: boolean }>>({});
  const [navBadgeCounts, setNavBadgeCounts] = useState({ pipelineReview: 0, dossierReady: 0, rolesNeedingQuestions: 0 });
  const [navPriorityItems, setNavPriorityItems] = useState<{
    underReview: Array<{ applicationId: number; candidateName: string; roleTitle: string; hoursInStage: number }>;
    needsReview: Array<{ applicationId: number; candidateName: string; roleTitle: string; pipelineStage: string }>;
    expiringLinks: Array<{ applicationId: number; candidateName: string; hoursLeft: number }>;
    rolesNeedingQuestions: Array<{ roleId: number; title: string }>;
  }>({ underReview: [], needsReview: [], expiringLinks: [], rolesNeedingQuestions: [] });

  const showQuestionBanner = () => {
    setQuestionSaveBanner(true);
    if (questionSaveBannerTimer) clearTimeout(questionSaveBannerTimer);
    const t = setTimeout(() => setQuestionSaveBanner(false), 10000);
    setQuestionSaveBannerTimer(t);
  };

  const openEditJobModal = (job: Job) => {
    setEditingJob(job);
    setEditJobForm({
      title: job.title || "",
      location: job.location || "",
      status: job.status || "draft",
      description: job.description || "",
      salary_range: job.salary_range || "",
      date_required: job.date_required ? job.date_required.split('T')[0] : "",
      employment_type: job.employment_type || "",
      work_mode: job.work_mode || "",
    });
    const qCount = jobQuestionCounts[String(job.id)]?.count ?? 0;
    setEditJobTab(qCount === 0 ? "questions" : "details");
    setSelectedJobForQuestions(job);
    setLoadingQuestions(true);
    apiRequest("GET", `/api/agent/jobs/${job.id}/questions`)
      .then(r => r.json())
      .then(data => setJobQuestions(data))
      .catch(() => {})
      .finally(() => setLoadingQuestions(false));
  };

  const saveJob = async () => {
    if (!editingJob) return;
    
    setSavingJob(true);
    try {
      // FIXED: PATCH /api/agent/jobs/:jobId now accessible to RECRUITMENT_AGENT
      await apiRequest("PATCH", `/api/agent/jobs/${editingJob.id}`, {
        title: editJobForm.title,
        location: editJobForm.location,
        status: editJobForm.status,
        description: editJobForm.description,
        salaryRange: editJobForm.salary_range,
        dateRequired: editJobForm.date_required || null,
        employmentType: editJobForm.employment_type,
        workMode: editJobForm.work_mode,
      });
      toast.success("Job updated successfully");
      setEditingJob(null);
      fetchJobs();
    } catch (error) {
      console.error("Failed to save job:", error);
      toast.error("Failed to update job");
    } finally {
      setSavingJob(false);
    }
  };

  const openQuestionsModal = async (job: Job) => {
    setSelectedJobForQuestions(job);
    setEditingQuestion(null);
    setShowAddQuestion(false);
    setLoadingQuestions(true);
    try {
      // FIXED: GET /api/agent/jobs/:jobId/questions now accessible to RECRUITMENT_AGENT
      const res = await apiRequest("GET", `/api/agent/jobs/${job.id}/questions`);
      const data = await res.json();
      setJobQuestions(data);
    } catch (error) {
      console.error("Failed to fetch questions:", error);
      toast.error("Failed to load questions");
    } finally {
      setLoadingQuestions(false);
    }
  };

  const regenerateQuestions = async () => {
    if (!selectedJobForQuestions) return;
    setRegeneratingQuestions(true);
    try {
      const res = await apiRequest("POST", `/api/agent/jobs/${selectedJobForQuestions.id}/questions/regenerate`);
      if (res.ok) {
        const data = await res.json();
        setJobQuestions(data.questions);
        toast.success("Questions regenerated successfully");
      } else {
        toast.error("Failed to regenerate questions");
      }
    } catch (error) {
      console.error("Failed to regenerate questions:", error);
      toast.error("Failed to regenerate questions");
    } finally {
      setRegeneratingQuestions(false);
    }
  };

  const addQuestion = async () => {
    if (!selectedJobForQuestions || !newQuestionForm.questionText.trim()) return;
    setAddingQuestion(true);
    try {
      const res = await apiRequest("POST", `/api/agent/jobs/${selectedJobForQuestions.id}/questions`, newQuestionForm);
      if (res.ok) {
        const newQ = await res.json();
        const updatedQuestions = [...jobQuestions, newQ];
        setJobQuestions(updatedQuestions);
        setNewQuestionForm({ questionText: "", questionType: "behavioral", category: "general", timeLimit: 30 });
        setShowAddQuestion(false);
        setJobQuestionCounts(prev => ({ ...prev, [String(selectedJobForQuestions.id)]: { count: updatedQuestions.length, hasCustom: updatedQuestions.some(q => !DEFAULT_QUESTION_TEXTS.includes(q.questionText?.trim())) } }));
        showQuestionBanner();
      } else {
        toast.error("Failed to add question");
      }
    } catch (error) {
      toast.error("Failed to add question");
    } finally {
      setAddingQuestion(false);
    }
  };

  const updateQuestion = async () => {
    if (!selectedJobForQuestions || !editingQuestion) return;
    setSavingQuestion(true);
    try {
      const res = await apiRequest("PATCH", `/api/agent/jobs/${selectedJobForQuestions.id}/questions/${editingQuestion.id}`, editQuestionForm);
      if (res.ok) {
        const updatedQ = await res.json();
        const updatedList = jobQuestions.map(q => q.id === editingQuestion.id ? updatedQ : q);
        setJobQuestions(updatedList);
        setJobQuestionCounts(prev => ({ ...prev, [String(selectedJobForQuestions.id)]: { count: updatedList.length, hasCustom: updatedList.some(q => !DEFAULT_QUESTION_TEXTS.includes(q.questionText?.trim())) } }));
        setEditingQuestion(null);
        showQuestionBanner();
      } else {
        toast.error("Failed to update question");
      }
    } catch (error) {
      toast.error("Failed to update question");
    } finally {
      setSavingQuestion(false);
    }
  };

  const deleteQuestion = async (questionId: number) => {
    if (!selectedJobForQuestions) return;
    try {
      const res = await apiRequest("DELETE", `/api/agent/jobs/${selectedJobForQuestions.id}/questions/${questionId}`);
      if (res.ok) {
        const remaining = jobQuestions.filter(q => q.id !== questionId);
        setJobQuestions(remaining);
        setJobQuestionCounts(prev => ({ ...prev, [String(selectedJobForQuestions.id)]: { count: remaining.length, hasCustom: remaining.some(q => !DEFAULT_QUESTION_TEXTS.includes(q.questionText?.trim())) } }));
        toast.success("Question deleted");
      } else {
        toast.error("Failed to delete question");
      }
    } catch (error) {
      toast.error("Failed to delete question");
    }
  };

  const deleteJob = async (jobId: string, _source?: 'postgresql') => {
    setConfirmDeleteJob(null);
    setDeletingJobId(jobId);
    try {
      const res = await apiRequest("DELETE", `/api/agent/jobs/${jobId}`);
      
      if (res.ok) {
        toast.success("Job deleted successfully");
        fetchJobs();
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.error || "Failed to delete job");
      }
    } catch (error) {
      console.error("Failed to delete job:", error);
      toast.error("Failed to delete job");
    } finally {
      setDeletingJobId(null);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchAnalytics();
    fetchCandidates();
    fetchEnquiries();
    fetchInvites();
  }, []);

  useEffect(() => {
    const fetchQuestionCounts = async () => {
      try {
        const res = await apiRequest("GET", "/api/agent/jobs/question-counts");
        if (res.ok) setJobQuestionCounts(await res.json());
      } catch {}
    };
    fetchQuestionCounts();
  }, [jobs]);

  useEffect(() => {
    const fetchPrioritySummary = async () => {
      try {
        const res = await apiRequest("GET", "/api/agent/priority-summary");
        if (res.ok) {
          const data = await res.json();
          setNavBadgeCounts(data.navCounts ?? { pipelineReview: 0, dossierReady: 0, rolesNeedingQuestions: 0 });
          setNavPriorityItems(data.priorityItems ?? { underReview: [], needsReview: [], expiringLinks: [], rolesNeedingQuestions: [] });
        }
      } catch {}
    };
    fetchPrioritySummary();
    const interval = setInterval(fetchPrioritySummary, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      toast.success("Welcome to Vexis — your subscription is active.");
      const clean = new URL(window.location.href);
      clean.searchParams.delete("checkout");
      window.history.replaceState(null, "", clean.pathname + (clean.search || ""));
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab && ["today", "overview", "registrations", "pipeline", "approval", "roles", "matching", "shortlists", "hired", "jobs", "candidates", "dossiers", "scoring", "matches", "invites", "settings", "howto", "guide"].includes(tab)) {
      setActiveTab(tab as any);
    }
  }, [location]);

  const handledEditQsRef = useRef(false);
  useEffect(() => {
    if (handledEditQsRef.current || jobs.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const editQsId = params.get("editQuestions");
    if (!editQsId) return;
    const targetJob = jobs.find(j => String(j.id) === editQsId);
    if (targetJob) {
      handledEditQsRef.current = true;
      openQuestionsModal(targetJob);
      const clean = new URL(window.location.href);
      clean.searchParams.delete("editQuestions");
      window.history.replaceState(null, "", clean.pathname + (clean.search || ""));
    }
  }, [jobs]);

  useEffect(() => {
    if (activeTab === "invites") {
      fetchJobs();
    }
    if (activeTab === "registrations") {
      fetchEnquiries();
    }
  }, [activeTab]);

  // Auto-start tour for new users who haven't completed or dismissed it
  useEffect(() => {
    if (!loading && shouldAutoStart) {
      const timer = setTimeout(() => {
        startTour();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loading, shouldAutoStart, startTour]);

  const fetchTeamMembers = useCallback(() => {
    fetch("/api/agent/team/members", { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setTeamMembers(data.members ?? []);
        setTeamMeta({
          currentSeats: data.currentSeats ?? 0,
          maxSeats: data.maxSeats ?? null,
          seatLimitExceeded: data.seatLimitExceeded ?? false,
          tierDisplayName: data.tierDisplayName ?? null,
        });
        setTeamLoaded(true);
      })
      .catch(() => setTeamLoaded(true));
  }, []);

  useEffect(() => {
    if (activeTab === "settings" && !notifPrefsLoaded) {
      fetch("/api/agent/notification-preferences", { credentials: "include" })
        .then(r => r.json())
        .then(data => { setNotifPrefs(data); setNotifPrefsLoaded(true); })
        .catch(() => {});
    }
    if (activeTab === "settings" && !defaultSettingsLoaded) {
      fetch("/api/agent/default-settings", { credentials: "include" })
        .then(r => r.json())
        .then(data => { setDefaultSettings(data); setDefaultSettingsLoaded(true); })
        .catch(() => {});
    }
    if (activeTab === "settings" && !teamLoaded) {
      fetchTeamMembers();
    }
  }, [activeTab, notifPrefsLoaded, defaultSettingsLoaded, teamLoaded, fetchTeamMembers]);

  const saveNotifPref = useCallback(async (key: string, enabled: boolean) => {
    setSavingNotifPref(key);
    setNotifPrefs(prev => ({ ...prev, [key]: enabled }));
    try {
      await fetch("/api/agent/notification-preferences", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preference: key, enabled }),
      });
      toast.success("Saved");
    } catch {
      toast.error("Failed to save preference");
      setNotifPrefs(prev => ({ ...prev, [key]: !enabled }));
    } finally {
      setSavingNotifPref(null);
    }
  }, []);

  const saveDefaultSettings = useCallback(async () => {
    setSavingDefaultSettings(true);
    try {
      await fetch("/api/agent/default-settings", {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(defaultSettings),
      });
      toast.success("Default settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSavingDefaultSettings(false);
    }
  }, [defaultSettings]);

  const fetchEnquiries = async () => {
    setLoadingEnquiries(true);
    try {
      const res = await apiRequest("GET", "/api/agent/enquiries");
      if (res.ok) {
        const data = await res.json();
        setEnquiries(data);
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.error || "Failed to load enquiries");
      }
    } catch (error) {
      toast.error("Could not load enquiries");
    } finally {
      setLoadingEnquiries(false);
    }
  };

  const updateEnquiryStatus = async (id: number, status: string) => {
    try {
      const res = await apiRequest("PATCH", `/api/agent/enquiries/${id}`, { status });
      if (res.ok) {
        toast.success("Enquiry status updated");
        fetchEnquiries();
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.error || "Failed to update enquiry status");
      }
    } catch (error) {
      toast.error("Failed to update enquiry");
    }
  };

  const deleteEnquiry = async (id: number) => {
    setConfirmDeleteEnquiry(null);
    try {
      const res = await apiRequest("DELETE", `/api/agent/enquiries/${id}`);
      if (res.ok) {
        toast.success("Enquiry deleted");
        fetchEnquiries();
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.error || "Failed to delete enquiry");
      }
    } catch (error) {
      toast.error("Failed to delete enquiry");
    }
  };

  const filteredEnquiries = enquiries.filter(e => {
    if (enquiryFilter === "all") return true;
    return e.enquiryType === enquiryFilter;
  });

  const fetchInvites = async () => {
    setLoadingInvites(true);
    try {
      const res = await apiRequest("GET", "/api/agent/role-invites");
      if (res.ok) {
        const data = await res.json();
        setInvites(data);
      } else {
        console.error("Failed to load invites");
      }
    } catch (error) {
      console.error("Could not load invites:", error);
    } finally {
      setLoadingInvites(false);
    }
  };

  const fetchCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const [res, interviewsRes] = await Promise.all([
        apiRequest("GET", "/api/agent/candidates"),
        apiRequest("GET", "/api/agent/interviews"),
      ]);
      if (res.ok) {
        const data = await res.json();
        setCandidates(data);
      }
      if (interviewsRes.ok) {
        const interviewsData = await interviewsRes.json();
        setInterviews(interviewsData.filter((i: InterviewRecord) => i.status === "completed"));
      }
    } catch (error) {
      console.log("Could not load candidates");
    } finally {
      setLoadingCandidates(false);
    }
  };

  const viewCandidateVideos = async (candidateId: number, candidateName: string) => {
    setLoadingCandidateVideos(true);
    try {
      const res = await apiRequest("GET", `/api/agent/candidates/${candidateId}/videos`);
      if (res.ok) {
        const data = await res.json();
        setCandidateVideoModal({
          candidateId: data.candidateId,
          candidateName: data.candidateName || candidateName,
          interviews: data.interviews || [],
        });
      } else {
        toast.error("Could not load interview videos");
      }
    } catch (error) {
      toast.error("Failed to load videos");
    } finally {
      setLoadingCandidateVideos(false);
    }
  };

  const createCandidate = async () => {
    if (!newCandidate.fullName || !newCandidate.email) {
      toast.error("Name and email are required");
      return;
    }
    setCreatingCandidate(true);
    try {
      const res = await apiRequest("POST", "/api/agent/candidates", newCandidate);
      if (res.ok) {
        toast.success("Candidate created successfully");
        setShowNewCandidateDialog(false);
        setNewCandidate({ fullName: "", email: "", phone: "", resumeText: "" });
        fetchCandidates();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "Failed to create candidate");
      }
    } catch (error) {
      toast.error("Failed to create candidate");
    } finally {
      setCreatingCandidate(false);
    }
  };

  const openEditCandidateModal = (candidate: any) => {
    setEditingCandidate(candidate);
    setEditCandidateForm({
      fullName: candidate.fullName || "",
      email: candidate.email || "",
      phone: candidate.phone || "",
      resumeText: candidate.resumeText || "",
    });
  };

  const saveCandidate = async () => {
    if (!editingCandidate) return;
    setSavingCandidate(true);
    try {
      const res = await apiRequest("PATCH", `/api/agent/candidates/${editingCandidate.id}`, editCandidateForm);
      if (res.ok) {
        toast.success("Candidate updated successfully");
        setEditingCandidate(null);
        fetchCandidates();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "Failed to update candidate");
      }
    } catch (error) {
      toast.error("Failed to update candidate");
    } finally {
      setSavingCandidate(false);
    }
  };

  const deleteCandidate = async (id: number) => {
    setDeletingCandidateId(id);
    setConfirmDeleteCandidate(null);
    try {
      const res = await apiRequest("DELETE", `/api/agent/candidates/${id}`);
      if (res.ok) {
        toast.success("Candidate deleted successfully");
        fetchCandidates();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.error || "Failed to delete candidate");
      }
    } catch (error) {
      toast.error("Failed to delete candidate");
    } finally {
      setDeletingCandidateId(null);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await apiRequest("GET", "/api/agent/analytics/overview");
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.log("Could not load analytics");
    }
  };

  const fetchJobs = async () => {
    try {
      // Fetch only PostgreSQL roles with applications (Supabase removed)
      const pgRes = await apiRequest("GET", "/api/agent/jobs");
      
      let allJobs: Job[] = [];
      
      // Process PostgreSQL roles with applications  
      if (pgRes.ok) {
        const pgData = await pgRes.json();
        console.log("PostgreSQL roles loaded:", pgData.length);
        const pgJobs = pgData.map((role: any) => ({
          id: role.id.toString(),
          title: role.title,
          location: role.location || '',
          employment_type: role.employmentType || '',
          work_mode: role.workMode || '',
          date_required: role.dateRequired || '',
          status: role.status || 'active',
          created_at: role.createdAt || '',
          source: 'postgresql',
          description: role.description || '',
          salary_range: role.salaryRange || role.salary_band || '',
          requirements: Array.isArray(role.requirements) ? role.requirements : [],
          employer_question_requirements: role.employerQuestionRequirements,
          jd_pdf_storage_path: role.jdDocumentId ? `doc-${role.jdDocumentId}` : undefined,
          applications: (role.applications || []).map((app: any) => {
            let stage = app.pipeline_stage || app.pipelineStage;
            if (!stage) {
              if (app.status === "hired") stage = "in_progress";
              else if (app.status === "shortlisted") stage = "link_sent";
              else stage = "initial_review";
            }
            return { 
              ...app, 
              id: app.id?.toString() || app.id,
              pipeline_stage: stage,
              candidate_name: app.candidateName || app.candidate_name,
              created_at: app.createdAt || app.created_at,
              source: 'postgresql',
            };
          }),
          applicants_total: role.applicationCount || 0,
        }));
        allJobs = [...allJobs, ...pgJobs];
      } else {
        console.warn("Failed to fetch PostgreSQL roles:", pgRes.status, await pgRes.text().catch(() => ""));
      }
      
      console.log("Total jobs loaded:", allJobs.length);
      setJobs(allJobs);
    } catch (error) {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const getJobsWithJDUploaded = () => {
    return jobs.filter(job => job.jd_pdf_storage_path);
  };

  const getActiveJobs = () => {
    return jobs.filter(job => ["active", "open", "published", "draft"].includes(job.status || ""));
  };

  const getExpiredJobs = () => {
    return jobs.filter(job => job.status === "expired" || job.status === "closed" || job.status === "filled");
  };

  const updateApplicationStatus = async (applicationId: string, newStatus: string, pipelineStage?: string, _source?: string) => {
    try {
      const res = await apiRequest("PATCH", `/api/agent/applications/${applicationId}/status`, { status: newStatus, pipeline_stage: pipelineStage });
      if (!res.ok) throw new Error("Failed to update status");
      toast.success(`Application updated`);
      fetchJobs();
    } catch (error) {
      toast.error("Failed to update application status");
    }
  };

  const moveToStage = (app: Application, newStage: string) => {
    const statusMap: Record<string, string> = {
      "initial_review": "pending",
      "link_sent": "invited",
      "in_progress": "in_progress",
      "submitted": "interview_completed",
      "ai_triage": "interview_completed",
      "under_review": "interview_completed",
      "dossier_ready": "interview_completed",
      "outcome": "interview_completed",
    };
    updateApplicationStatus(app.id, statusMap[newStage] || "pending", newStage, (app as any).source);
  };

  const openSendDossierModal = (app: Application) => {
    setEmailOverride("");
    setSendDossierModal({ app, step: "confirm" });
  };

  const executeSendDossier = async (app: Application, overrideEmail?: string) => {
    setSendDossierModal(prev => prev ? { ...prev, step: "sending" } : null);
    try {
      const res = await apiRequest("POST", `/api/agent/applications/${app.id}/send-dossier`, { overrideEmail: overrideEmail || undefined });
      const data = await res.json();
      if (res.ok) {
        setSendDossierModal(prev => prev ? { ...prev, step: "done", email: data.employerEmail, sentAt: data.sentAt } : null);
        fetchJobs();
        fetchCandidates();
      } else if (data.error === "no_employer_email") {
        setSendDossierModal(prev => prev ? { ...prev, step: "email_prompt" } : null);
      } else {
        setSendDossierModal(prev => prev ? { ...prev, step: "error", errorMsg: data.error || "Failed to send dossier" } : null);
      }
    } catch {
      setSendDossierModal(prev => prev ? { ...prev, step: "error", errorMsg: "Network error — please try again." } : null);
    }
  };

  const openOutcomeModal = (app: Application) => {
    setOutcomeTopicNote("");
    setOutcomeModal({ app, step: "select" });
  };

  const executeSetOutcome = async (app: Application, outcome: string) => {
    setOutcomeModal(prev => prev ? { ...prev, step: "submitting", outcome } : null);
    try {
      const res = await apiRequest("POST", `/api/agent/applications/${app.id}/set-outcome`, { outcome, interviewTopicNote: outcomeTopicNote });
      const data = await res.json();
      if (res.ok) {
        setOutcomeModal(prev => prev ? { ...prev, step: "done", outcome } : null);
        fetchJobs();
        fetchCandidates();
      } else if (res.status === 422 && data.currentStage && data.currentStage !== "dossier_ready") {
        toast.error("Please complete the review and create a dossier first before setting the outcome.");
        setOutcomeModal(null);
      } else {
        toast.error(data.error || "Failed to set outcome");
        setOutcomeModal(prev => prev ? { ...prev, step: "select" } : null);
      }
    } catch {
      toast.error("Network error — please try again.");
      setOutcomeModal(prev => prev ? { ...prev, step: "select" } : null);
    }
  };

  const isFollowUpDue = (sentAt?: string) => {
    if (!sentAt) return false;
    const sent = new Date(sentAt);
    let businessDays = 0;
    const now = new Date();
    const check = new Date(sent);
    while (check < now) {
      check.setDate(check.getDate() + 1);
      const day = check.getDay();
      if (day !== 0 && day !== 6) businessDays++;
    }
    return businessDays >= 5;
  };

  const deleteApplication = async (appId: number) => {
    setConfirmDeleteApplication(null);
    setCandidates(prev => prev.map(c => ({
      ...c,
      applications: (c.applications || []).filter((a: any) => a.id !== appId),
    })));
    try {
      const res = await apiRequest("DELETE", `/api/agent/applications/${appId}`);
      if (!res.ok) {
        throw new Error("Failed");
      }
      toast.success("Candidate removed from pipeline");
      fetchCandidates();
    } catch (error) {
      toast.error("Could not remove candidate — please try again");
      fetchCandidates();
    }
  };

  const resendInvite = async (applicationId: string | number) => {
    try {
      const res = await apiRequest("POST", `/api/agent/applications/${applicationId}/resend-invite`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to resend invite");
      }
      if (data.emailSent) {
        toast.success("Interview link resent to candidate");
      } else if (data.emailWarning) {
        toast.warning(data.emailWarning);
      } else {
        toast.success("Interview link regenerated — email not configured");
      }
      fetchJobs();
    } catch (error: any) {
      toast.error(error.message || "Failed to resend interview invite");
    }
  };

  const openAgentSidebar = async (app: Application) => {
    setSelectedApplication(app);
    // Start with safe defaults, then overwrite with server data if available.
    // Verification data lives in interview.agentNotesPrivate (JSON), NOT as DB columns
    // on the application — so the app object from the jobs list never has these fields.
    setVerification({
      identity_verified: false,
      right_to_work_verified: false,
      authenticity_verified: false,
      agent_notes: "",
      agent_summary: "",
      flagged_issues: [],
    });
    setSidebarOpen(true);
    // Fetch the persisted verification state from the dossier endpoint
    try {
      const res = await apiRequest("GET", `/api/agent/applications/${app.id}/dossier`);
      if (res.ok) {
        const data = await res.json();
        setVerification({
          identity_verified: data.identityVerified || false,
          right_to_work_verified: data.rightToWorkVerified || false,
          authenticity_verified: data.authenticityVerified || false,
          agent_notes: data.agentNotes || "",
          agent_summary: data.agentSummary || "",
          flagged_issues: Array.isArray(data.flaggedIssues) ? data.flaggedIssues : [],
        });
      }
    } catch {
      // No interview yet or network error — keep defaults, allow fresh entry
    }
  };

  const saveVerification = async () => {
    if (!selectedApplication) return;
    
    const isFullyVerified = verification.identity_verified && 
                            verification.right_to_work_verified && 
                            verification.authenticity_verified;
    
    try {
      const res = await apiRequest("PATCH", `/api/agent/applications/${selectedApplication.id}/verification`, {
          identity_verified: verification.identity_verified,
          right_to_work_verified: verification.right_to_work_verified,
          authenticity_verified: verification.authenticity_verified,
          agent_notes: verification.agent_notes,
          agent_summary: verification.agent_summary,
          human_verified: isFullyVerified,
          flagged_issues: verification.flagged_issues,
        });
      
      if (!res.ok) throw new Error("Failed to save");
      
      // Update local state immediately for better UX
      setJobs(prev => prev.map(job => ({
        ...job,
        applications: job.applications?.map(app => 
          app.id === selectedApplication.id 
            ? { 
                ...app, 
                ...verification,
                human_verified: isFullyVerified 
              } 
            : app
        ),
      })));
      
      toast.success(isFullyVerified ? "Candidate marked as Human-Verified" : "Verification notes saved");
      setSidebarOpen(false);
    } catch (error) {
      toast.error("Failed to save verification");
    }
  };

  const generateInviteLink = async (applicationId: string) => {
    if (planEnforcement.isAtCandidateLimit) {
      setShowCandidateLimitModal(true);
      return;
    }

    // Guard: prevent double-click while request is in flight
    if (sendingInviteIds.has(applicationId)) return;
    setSendingInviteIds(prev => new Set(prev).add(applicationId));

    try {
      const res = await apiRequest("POST", "/api/agent/invites", { applicationId: parseInt(applicationId) });
      const data = await res.json();

      if (res.status === 403 && data.error === "CANDIDATE_LIMIT_REACHED") {
        setShowCandidateLimitModal(true);
        return;
      }
      if (!res.ok) {
        const msg = data.error || "Failed to send interview link";
        console.error("[generateInviteLink] API error:", { applicationId, status: res.status, msg });
        toast.error(`Failed to send interview link. ${msg}`);
        return;
      }

      // Invite created and email dispatched — show success regardless of clipboard outcome
      const emailDesc = data.emailSent
        ? "Invitation email sent to candidate."
        : data.emailWarning
          ? data.emailWarning
          : "No email sent — check Communications settings.";

      toast.success("Interview link sent successfully", { description: emailDesc });

      // Clipboard copy is best-effort — failure must NOT mask a successful invite
      try {
        await navigator.clipboard.writeText(data.inviteUrl);
        setLinkCopied(true);
        toast.success("Link also copied to clipboard");
        setTimeout(() => setLinkCopied(false), 2500);
      } catch {
        // Clipboard unavailable (non-HTTPS, permissions denied, page not focused)
        // Show the URL so the recruiter can copy it manually
        toast.info("Clipboard unavailable — copy the link manually", {
          description: data.inviteUrl,
          duration: 10000,
        });
      }

      fetchJobs();
    } catch (error: any) {
      console.error("[generateInviteLink] Unexpected error:", { applicationId, error: error?.message });
      toast.error(error?.message || "Failed to send interview link. Please try again.");
    } finally {
      setSendingInviteIds(prev => {
        const next = new Set(prev);
        next.delete(applicationId);
        return next;
      });
    }
  };

  const generateRoleInviteLink = async (roleId: string | number) => {
    try {
      const res = await apiRequest("POST", "/api/agent/role-invites", { roleId: parseInt(String(roleId)) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to generate link");
      await navigator.clipboard.writeText(data.inviteUrl);
      toast.success("Link copied to clipboard", {
        description: `Shareable invite link for this role has been copied.`,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to generate interview link");
    }
  };

  const fetchInterviewVideos = async (applicationId: string, _source?: string) => {
    setLoadingVideos(true);
    setShowVideoPlayer(true);
    setVideoUrls({});
    setActiveVideoIndex(0);
    
    try {
      // Fetch from PostgreSQL only (Supabase removed)
      const res = await apiRequest("GET", `/api/agent/applications/${applicationId}/dossier`);
      if (!res.ok) {
        toast.error("No interview recording found for this candidate");
        setShowVideoPlayer(false);
        return;
      }
      
      const data = await res.json();
      
      if (data.interviewId && data.interviewVideos && data.interviewVideos.length > 0) {
        setInterviewSessionId(data.interviewId.toString());
        setInterviewVideoStatus(data.interviewVideoStatus ?? null);
        setInterviewVideos(data.interviewVideos.map((v: any) => ({
          questionId: String(v.responseId),
          question: v.question,
          responseId: String(v.responseId),
          videoDeleted: v.videoDeleted ?? false,
          videoDeletedAt: v.videoDeletedAt ?? null,
        })));
        
        const urls: Record<string, string> = {};
        for (const video of data.interviewVideos) {
          if (video.videoDeleted) continue;
          try {
            const urlRes = await apiRequest("GET", `/api/agent/applications/${applicationId}/video/${video.responseId}`);
            if (urlRes.ok) {
              const urlData = await urlRes.json();
              urls[String(video.responseId)] = urlData.url;
            }
          } catch (err) {
            console.error(`Failed to fetch video URL for response ${video.responseId}`);
          }
        }
        setVideoUrls(urls);
      } else {
        toast.error("No interview recording found for this candidate");
        setShowVideoPlayer(false);
      }
    } catch (error) {
      toast.error("Failed to load interview videos");
      setShowVideoPlayer(false);
    } finally {
      setLoadingVideos(false);
    }
  };

  const exportTalentPacket = (app: Application) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;
    
    // Header with logo placeholder
    doc.setFillColor(15, 23, 42); // slate-900
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("VEXIS", 20, 28);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Agent-Verified Talent Platform", 55, 28);
    
    // Human Verified Badge
    if (app.human_verified) {
      doc.setFillColor(34, 197, 94); // green-500
      doc.roundedRect(pageWidth - 60, 12, 50, 18, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("HUMAN VERIFIED", pageWidth - 55, 23);
    }
    
    yPos = 55;
    
    // Title
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Verified Talent Packet", 20, yPos);
    yPos += 10;
    
    // Date
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-NZ', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`, 20, yPos);
    yPos += 15;
    
    // Divider
    doc.setDrawColor(226, 232, 240);
    doc.line(20, yPos, pageWidth - 20, yPos);
    yPos += 15;
    
    // Verification Status Section
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(20, yPos - 5, pageWidth - 40, 45, 3, 3, 'F');
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Agent Verification Status", 25, yPos + 5);
    yPos += 15;
    
    // Verification checkboxes
    const checks = [
      { label: "Identity Verified", checked: app.identity_verified },
      { label: "Right to Work Confirmed", checked: app.right_to_work_verified },
      { label: "Authenticity Verified", checked: app.authenticity_verified },
    ];
    
    doc.setFontSize(10);
    checks.forEach((check, i) => {
      const xOffset = 25 + (i * 55);
      if (check.checked) {
        doc.setFillColor(34, 197, 94);
        doc.circle(xOffset, yPos + 2, 3, 'F');
        doc.setTextColor(34, 197, 94);
      } else {
        doc.setDrawColor(148, 163, 184);
        doc.circle(xOffset, yPos + 2, 3, 'S');
        doc.setTextColor(148, 163, 184);
      }
      doc.setFont("helvetica", "normal");
      doc.text(check.label, xOffset + 6, yPos + 4);
    });
    yPos += 30;
    
    // Pipeline Stage
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Pipeline Stage:", 20, yPos);
    
    const stageDisplayId = STAGE_DISPLAY_MAP[app.pipeline_stage || "initial_review"] ?? app.pipeline_stage ?? "initial_review";
    const stage = PIPELINE_STAGES.find(s => s.id === stageDisplayId);
    const stageLabel = stage?.label || "Initial Review";
    doc.setFont("helvetica", "normal");
    doc.setTextColor(59, 130, 246);
    doc.text(stageLabel, 70, yPos);
    yPos += 20;
    
    // Agent Summary Section
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Agent Summary", 20, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(51, 65, 85);
    const summary = app.agent_summary || "Awaiting agent review and summary.";
    const summaryLines = doc.splitTextToSize(summary, pageWidth - 40);
    doc.text(summaryLines, 20, yPos);
    yPos += (summaryLines.length * 5) + 15;
    
    // Agent Notes Section
    if (app.agent_notes) {
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Additional Notes", 20, yPos);
      yPos += 8;
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 65, 85);
      const notesLines = doc.splitTextToSize(app.agent_notes, pageWidth - 40);
      doc.text(notesLines, 20, yPos);
      yPos += (notesLines.length * 5) + 15;
    }
    
    // Footer
    const footerY = doc.internal.pageSize.getHeight() - 30;
    doc.setDrawColor(226, 232, 240);
    doc.line(20, footerY, pageWidth - 20, footerY);
    
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text("Contact details protected. Please contact your recruiting agent for candidate information.", 20, footerY + 10);
    doc.text("This document complies with NZ Privacy Act 2020 and Human Rights Act 1993.", 20, footerY + 18);
    
    // Save
    doc.save(`vexis-talent-packet-${app.id}.pdf`);
    toast.success("Talent packet exported as PDF");
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          job.location?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "bg-green-500/10 text-green-500 border-green-500/30";
      case "paused": return "bg-yellow-500/10 text-yellow-500 border-yellow-500/30";
      case "closed": return "bg-gray-500/10 text-gray-500 border-gray-500/30";
      case "draft": return "bg-blue-500/10 text-blue-500 border-blue-500/30";
      default: return "bg-gray-500/10 text-gray-500";
    }
  };

  const getApplicationStatusColor = (status: string) => {
    switch (status) {
      case "submitted": return "bg-blue-500/10 text-blue-500";
      case "shortlisted": return "bg-green-500/10 text-green-500";
      case "rejected": return "bg-red-500/10 text-red-500";
      case "hired": return "bg-purple-500/10 text-purple-500";
      case "interview_pending": return "bg-yellow-500/10 text-yellow-500";
      default: return "bg-gray-500/10 text-gray-500";
    }
  };

  const getAllApplications = () => {
    return filteredJobs.flatMap(job => 
      (job.applications || []).map(app => ({ ...app, jobTitle: job.title, jobId: job.id, source: job.source }))
    );
  };

  const getApplicationsByStage = (stage: string) => {
    return getAllApplications().filter(app => {
      const raw = app.pipeline_stage || "initial_review";
      const displayStage = STAGE_DISPLAY_MAP[raw] ?? raw;
      return displayStage === stage;
    });
  };

  const stats = {
    totalJobs: jobs.length,
    activeJobs: jobs.filter(j => j.status === "active").length,
    totalApplicants: jobs.reduce((sum, j) => sum + (j.applicants_total || j.applicant_count || 0), 0),
    pendingReviews: getAllApplications().filter(a => a.pipeline_stage === "initial_review" || !a.pipeline_stage).length,
    verified: getAllApplications().filter(a => a.human_verified).length,
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const interviewsAwaitingReview = candidates.filter(c => 
    c.interviewStatus === 'completed' && c.status !== 'approved' && c.status !== 'declined'
  ).length;

  const registrationsCount = candidates.filter(c => 
    c.interviewStatus === 'not_started' || c.interviewStatus === 'in_progress'
  ).length;

  const approvalCount = candidates.filter(c => 
    c.status === 'approved' || c.status === 'declined' || c.status === 'hold'
  ).length;

  return (
    <AgentDashboardLayout
      activeTab={activeTab}
      onTabChange={setActiveTab}
      agentName={user.fullName || user.email || "Agent"}
      agentRole={user.role === "ADMIN" ? "Administrator" : "Recruitment Agent"}
      onLogout={logout}
      badges={{
        registrations: registrationsCount,
        interviewReview: navBadgeCounts.pipelineReview > 0 ? navBadgeCounts.pipelineReview : interviewsAwaitingReview,
        approval: approvalCount,
        dossierReady: navBadgeCounts.dossierReady,
        rolesNeedingQuestions: navBadgeCounts.rolesNeedingQuestions,
      }}
    >
        {atsBannerError && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-2 text-sm">
            <span className="text-amber-700 dark:text-amber-300">ATS connection interrupted - candidate data may not be syncing.</span>
            <button
              onClick={() => { setAtsBannerError(false); setActiveTab("settings"); }}
              className="text-amber-700 dark:text-amber-300 underline hover:no-underline font-medium flex-shrink-0"
            >
              Reconnect
            </button>
          </div>
        )}

        {activeTab === "today" && (
          <>
            <PlanUsageCompact />
            <TodayTab setActiveTab={setActiveTab} priorityItems={navPriorityItems} onOpenJobQuestions={(jobId) => { const job = jobs.find(j => String(j.id) === String(jobId)); if (job) openEditJobModal(job); setActiveTab("roles"); }} onResendInvite={resendInvite} userName={user?.fullName || user?.email} />
          </>
        )}

        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Pipeline Workflow Banner */}
            <Card className="p-3 md:p-4 bg-blue-500/10 border-blue-500/20">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  <span className="text-xs md:text-sm font-medium text-blue-700 dark:text-blue-100">Your Pipeline Workflow</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                  <button onClick={() => setActiveTab("registrations")} className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-300 text-xs md:text-sm font-medium hover:bg-blue-500/30 transition-colors" data-testid="workflow-link-registrations">
                    <Users className="w-3 h-3" />
                    <span className="hidden sm:inline">Registrations</span>
                    <span className="sm:hidden">Reg</span>
                    <span className="font-bold">({registrationsCount})</span>
                  </button>
                  <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-blue-400/50 hidden sm:block" />
                  <button onClick={() => setActiveTab("pipeline")} className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-300 text-xs md:text-sm font-medium hover:bg-blue-500/30 transition-colors" data-testid="workflow-link-pipeline">
                    <Video className="w-3 h-3" />
                    <span className="hidden sm:inline">Pipeline</span>
                    <span className="sm:hidden">Review</span>
                    <span className="font-bold">({interviewsAwaitingReview})</span>
                  </button>
                  <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-blue-400/50 hidden sm:block" />
                  <button onClick={() => setActiveTab("approval")} className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-300 text-xs md:text-sm font-medium hover:bg-blue-500/30 transition-colors" data-testid="workflow-link-approval">
                    <UserCheck className="w-3 h-3" />
                    <span className="hidden sm:inline">Approval</span>
                    <span className="sm:hidden">Appr</span>
                    <span className="font-bold">({approvalCount})</span>
                  </button>
                  <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-muted-foreground hidden sm:block" />
                  <button onClick={() => setActiveTab("matching")} className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-300 text-xs md:text-sm font-medium hover:bg-blue-500/30 transition-colors" data-testid="workflow-link-matching">
                    <GitMerge className="w-3 h-3" />
                    <span className="hidden sm:inline">Matching</span>
                    <span className="sm:hidden">Match</span>
                  </button>
                  <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-blue-400/50 hidden sm:block" />
                  <button onClick={() => setActiveTab("shortlists")} className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-300 text-xs md:text-sm font-medium hover:bg-blue-500/30 transition-colors" data-testid="workflow-link-shortlists">
                    <Send className="w-3 h-3" />
                    <span className="hidden sm:inline">Shortlists</span>
                    <span className="sm:hidden">Short</span>
                  </button>
                  <ChevronRight className="w-3 h-3 md:w-4 md:h-4 text-green-400/50 hidden sm:block" />
                  <button onClick={() => setActiveTab("hired")} className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-green-500/20 text-green-600 dark:text-green-300 text-xs md:text-sm font-medium hover:bg-green-500/30 transition-colors" data-testid="workflow-link-hired">
                    <CheckCircle2 className="w-3 h-3" />
                    <span className="hidden sm:inline">Hired</span>
                    <span className="sm:hidden">Hire</span>
                  </button>
                </div>
              </div>
            </Card>

            {/* Review Queue Banner */}
            {interviewsAwaitingReview > 0 ? (
              <Card className="p-4 border-blue-500/50 bg-blue-500/10 cursor-pointer hover:bg-blue-500/15 transition-colors" onClick={() => setActiveTab("pipeline")} data-testid="alert-interviews-awaiting">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500 rounded-full">
                      <Video className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground" data-testid="text-interviews-count">{interviewsAwaitingReview} candidate{interviewsAwaitingReview === 1 ? '' : 's'} ready for review — estimated {interviewsAwaitingReview * 5} minutes total. Green band candidates are fastest — start there.</p>
                      <p className="text-sm text-muted-foreground">Click to open the review queue</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </Card>
            ) : (
              <Card className="p-4 border-green-500/30 bg-green-500/5" data-testid="alert-queue-clear">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500 rounded-full">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                  <p className="font-medium text-foreground">All clear — no candidates awaiting review right now.</p>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <Card className="p-4 md:p-6 cursor-pointer hover:border-primary/50 transition-colors active:scale-[0.98]" onClick={() => setActiveTab("jobs")} data-testid="card-kpi-active-roles">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Active Roles</p>
                    <p className="text-2xl md:text-3xl font-bold" data-testid="text-active-roles">{analytics?.activeRoles || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">roles currently accepting candidates</p>
                  </div>
                  <div className="p-2 md:p-3 bg-primary/10 rounded-lg">
                    <Briefcase className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                  </div>
                </div>
              </Card>
              
              <Card className="p-4 md:p-6 cursor-pointer hover:border-primary/50 transition-colors active:scale-[0.98]" onClick={() => setActiveTab("registrations")} data-testid="card-kpi-total-candidates">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Awaiting Link</p>
                    <p className="text-2xl md:text-3xl font-bold" data-testid="text-total-candidates">{registrationsCount}</p>
                    <p className="text-xs text-muted-foreground mt-1">candidates who haven't received their interview link yet</p>
                  </div>
                  <div className="p-2 md:p-3 bg-secondary/10 rounded-lg">
                    <Users className="w-5 h-5 md:w-6 md:h-6 text-secondary" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6 cursor-pointer hover:border-primary/50 transition-colors active:scale-[0.98]" onClick={() => setActiveTab("pipeline")} data-testid="card-kpi-pending-review">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">In Review Queue</p>
                    <p className="text-2xl md:text-3xl font-bold" data-testid="text-pending-review">{interviewsAwaitingReview}</p>
                    <p className="text-xs text-muted-foreground mt-1">candidates banded and awaiting agent review</p>
                  </div>
                  <div className="p-2 md:p-3 bg-blue-500/10 rounded-lg">
                    <Video className="w-5 h-5 md:w-6 md:h-6 text-blue-500" />
                  </div>
                </div>
              </Card>

              <Card className="p-4 md:p-6 cursor-pointer hover:border-primary/50 transition-colors active:scale-[0.98]" onClick={() => setActiveTab("dossiers")} data-testid="card-kpi-awaiting-approval">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Dossiers Sent</p>
                    <p className="text-2xl md:text-3xl font-bold" data-testid="text-awaiting-approval">{approvalCount}</p>
                    <p className="text-xs text-muted-foreground mt-1">dossiers currently with employers</p>
                  </div>
                  <div className="p-2 md:p-3 bg-yellow-500/10 rounded-lg">
                    <UserCheck className="w-5 h-5 md:w-6 md:h-6 text-yellow-500" />
                  </div>
                </div>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {/* Enquiries Summary */}
              <Card className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="text-base md:text-lg font-semibold">Enquiries</h3>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab("registrations")} className="h-8 px-2 md:px-3">
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-primary" />
                      <span className="text-sm">Employer Enquiries</span>
                    </div>
                    <Badge variant="secondary">{enquiries.filter(e => e.enquiryType === "employer").length}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-secondary/10 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-secondary" />
                      <span className="text-sm">Candidate Enquiries</span>
                    </div>
                    <Badge variant="secondary">{enquiries.filter(e => e.enquiryType === "candidate").length}</Badge>
                  </div>
                </div>
              </Card>

              {/* Pipeline Status */}
              <Card className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="text-base md:text-lg font-semibold">Pipeline Status</h3>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab("pipeline")} className="h-8 px-2 md:px-3">
                    Review <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer" onClick={() => setActiveTab("registrations")}>
                    <span className="text-sm">New Enquiries</span>
                    <Badge variant="secondary">{registrationsCount}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer" onClick={() => setActiveTab("pipeline")}>
                    <span className="text-sm">Awaiting Review</span>
                    <Badge variant="secondary">{interviewsAwaitingReview}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 cursor-pointer" onClick={() => setActiveTab("approval")}>
                    <span className="text-sm">Pending Approval</span>
                    <Badge variant="secondary">{approvalCount}</Badge>
                  </div>
                </div>
              </Card>

              {/* Quick Actions */}
              <Card className="p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Quick Actions</h3>
                <div className="grid gap-2 md:gap-3">
                  <Button 
                    id="tour-invite-candidate"
                    variant="outline" 
                    className="justify-start" 
                    onClick={() => setActiveTab("pipeline")}
                  >
                    <Video className="w-4 h-4 mr-2" />
                    Review Interviews
                  </Button>
                  <Button 
                    variant="outline" 
                    className="justify-start" 
                    onClick={() => setActiveTab("matching")}
                  >
                    <GitMerge className="w-4 h-4 mr-2" />
                    Match Candidates
                  </Button>
                  <Button 
                    variant="outline" 
                    className="justify-start" 
                    onClick={() => setActiveTab("roles")}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Manage Roles
                  </Button>
                </div>
              </Card>
            </div>

            <Card className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Recent Activity</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Video className="w-5 h-5 text-accent flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium">{analytics?.completedInterviews || 0}</p>
                    <p className="text-xs md:text-sm text-muted-foreground truncate">Interviews Done</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <UserCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium">{analytics?.shortlistedCandidates || 0}</p>
                    <p className="text-xs md:text-sm text-muted-foreground truncate">Shortlisted</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {activeTab === "pipeline" && (
        <>
        {/* Workflow Breadcrumb */}
        <div className="mb-3 md:mb-4 flex flex-wrap items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground">
          <button onClick={() => setActiveTab("overview")} className="hover:text-foreground transition-colors">Overview</button>
          <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
          <span className="text-primary font-medium">Pipeline</span>
          <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
          <button onClick={() => setActiveTab("invites")} className="hover:text-foreground transition-colors">Invites</button>
          <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
          <button onClick={() => setActiveTab("dossiers")} className="hover:text-foreground transition-colors">Dossiers</button>
        </div>

        <FreeRoleExpiryBanner />

        {/* Today's Intake Section */}
        {(() => {
          const todayStart = new Date();
          todayStart.setHours(0, 0, 0, 0);
          const employerEnquiries = enquiries.filter(e => e.enquiryType === "employer");
          const candidateEnquiries = enquiries.filter(e => e.enquiryType === "candidate");
          const employerNewToday = employerEnquiries.filter(e => new Date(e.createdAt) >= todayStart).length;
          const candidateNewToday = candidateEnquiries.filter(e => new Date(e.createdAt) >= todayStart).length;
          
          return (
          <div className="mb-4 md:mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-3 md:mb-4">
              <h2 className="text-xl md:text-2xl font-extrabold text-foreground" data-testid="section-todays-intake">Today's Intake</h2>
              <p className="text-xs md:text-sm text-muted-foreground">Start here: respond to new enquiries</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Employer Enquiries Box */}
              <Card className="p-4 md:p-5 border border-primary/30" data-testid="card-employer-enquiries">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 md:mb-4">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="p-1.5 md:p-2 bg-primary rounded-lg">
                      <Building2 className="w-4 h-4 md:w-5 md:h-5 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-base md:text-lg font-bold text-foreground">Employer Enquiries</h3>
                      <p className="text-xs md:text-sm text-muted-foreground">Companies looking to hire</p>
                    </div>
                  </div>
                  {employerNewToday > 0 && (
                    <Badge className="bg-primary/20 text-primary border border-primary/30 px-2 md:px-3 py-0.5 md:py-1 text-xs md:text-sm font-bold w-fit" data-testid="badge-employer-new-today">
                      {employerNewToday} NEW
                    </Badge>
                  )}
                </div>
                
                {loadingEnquiries ? (
                  <div className="text-center py-6">
                    <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : employerEnquiries.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No employer enquiries</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {employerEnquiries.map((enquiry) => {
                      const isNew = new Date(enquiry.createdAt) >= todayStart;
                      return (
                        <div key={enquiry.id} className={`p-3 bg-muted rounded-lg border ${isNew ? 'border-primary/50 shadow-sm' : 'border-border'}`} data-testid={`employer-enquiry-${enquiry.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-foreground truncate" data-testid={`employer-name-${enquiry.id}`}>{enquiry.name}</p>
                                {isNew && <Badge className="bg-destructive/20 text-destructive border border-destructive/30 text-xs px-2 py-0.5" data-testid={`employer-new-badge-${enquiry.id}`}>NEW</Badge>}
                              </div>
                              {enquiry.company && <p className="text-sm text-primary font-medium" data-testid={`employer-company-${enquiry.id}`}>{enquiry.company}</p>}
                              <p className="text-sm text-muted-foreground truncate" data-testid={`employer-email-${enquiry.id}`}>{enquiry.email}</p>
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2" data-testid={`employer-message-${enquiry.id}`}>{enquiry.message}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span data-testid={`employer-date-${enquiry.id}`}>{new Date(enquiry.createdAt).toLocaleDateString()}</span>
                                {enquiry.fileName && (
                                  <a href={`/api/agent/enquiries/${enquiry.id}/file`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`employer-file-${enquiry.id}`}>
                                    <FileText className="w-3 h-3" /> JD attached
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 ml-2">
                              <div className="flex items-center gap-1">
                                <a href={`mailto:${enquiry.email}`}>
                                  <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`employer-reply-${enquiry.id}`}>
                                    <Mail className="w-4 h-4" />
                                  </Button>
                                </a>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setConfirmDeleteEnquiry({id: enquiry.id, type: 'employer'})}
                                  data-testid={`employer-delete-${enquiry.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              <Select value={enquiry.status} onValueChange={(value) => updateEnquiryStatus(enquiry.id, value)}>
                                <SelectTrigger className="w-24 h-7 text-xs" data-testid={`employer-status-${enquiry.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="contacted">Contacted</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="converted">Converted</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-border text-center">
                  <p className="text-sm text-muted-foreground" data-testid="employer-total-count">
                    Total: <span className="font-semibold text-foreground">{employerEnquiries.length}</span> employer enquiries
                  </p>
                </div>
              </Card>

              {/* Candidate Enquiries Box */}
              <Card className="p-5 border border-secondary/30" data-testid="card-candidate-enquiries">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-secondary rounded-lg">
                      <Users className="w-5 h-5 text-secondary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground">Candidate Enquiries</h3>
                      <p className="text-sm text-muted-foreground">Candidates reaching out</p>
                    </div>
                  </div>
                  {candidateNewToday > 0 && (
                    <Badge className="bg-secondary/20 text-secondary border border-secondary/30 px-3 py-1 text-sm font-bold" data-testid="badge-candidate-new-today">
                      {candidateNewToday} NEW TODAY
                    </Badge>
                  )}
                </div>
                
                {loadingEnquiries ? (
                  <div className="text-center py-6">
                    <div className="animate-spin w-5 h-5 border-2 border-secondary border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : candidateEnquiries.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No candidate enquiries</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {candidateEnquiries.map((enquiry) => {
                      const isNew = new Date(enquiry.createdAt) >= todayStart;
                      return (
                        <div key={enquiry.id} className={`p-3 bg-muted rounded-lg border ${isNew ? 'border-secondary/50 shadow-sm' : 'border-border'}`} data-testid={`candidate-enquiry-${enquiry.id}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-foreground truncate" data-testid={`candidate-name-${enquiry.id}`}>{enquiry.name}</p>
                                {isNew && <Badge className="bg-destructive/20 text-destructive border border-destructive/30 text-xs px-2 py-0.5" data-testid={`candidate-new-badge-${enquiry.id}`}>NEW</Badge>}
                              </div>
                              <p className="text-sm text-muted-foreground truncate" data-testid={`candidate-email-${enquiry.id}`}>{enquiry.email}</p>
                              {enquiry.phone && <p className="text-sm text-muted-foreground" data-testid={`candidate-phone-${enquiry.id}`}>{enquiry.phone}</p>}
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2" data-testid={`candidate-message-${enquiry.id}`}>{enquiry.message}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span data-testid={`candidate-date-${enquiry.id}`}>{new Date(enquiry.createdAt).toLocaleDateString()}</span>
                                {enquiry.fileName && (
                                  <a href={`/api/agent/enquiries/${enquiry.id}/file`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline" data-testid={`candidate-file-${enquiry.id}`}>
                                    <FileText className="w-3 h-3" /> Resume attached
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 ml-2">
                              <div className="flex items-center gap-1">
                                <a href={`mailto:${enquiry.email}`}>
                                  <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`candidate-reply-${enquiry.id}`}>
                                    <Mail className="w-4 h-4" />
                                  </Button>
                                </a>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setConfirmDeleteEnquiry({id: enquiry.id, type: 'candidate'})}
                                  data-testid={`candidate-delete-${enquiry.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              <Select value={enquiry.status} onValueChange={(value) => updateEnquiryStatus(enquiry.id, value)}>
                                <SelectTrigger className="w-24 h-7 text-xs" data-testid={`candidate-status-${enquiry.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="contacted">Contacted</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="converted">Converted</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-border text-center">
                  <p className="text-sm text-muted-foreground" data-testid="candidate-total-count">
                    Total: <span className="font-semibold text-foreground">{candidateEnquiries.length}</span> candidate enquiries
                  </p>
                </div>
              </Card>
            </div>
          </div>
          );
        })()}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4 mb-4 md:mb-6">
          <Card className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-primary/10 rounded-lg">
                <Briefcase className="w-4 h-4 md:w-5 md:h-5 text-primary" />
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold">{stats.totalJobs}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground">Total Jobs</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-green-500/10 rounded-lg">
                <Play className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold">{stats.activeJobs}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </Card>

          <Card className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-blue-500/10 rounded-lg">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold">{stats.totalApplicants}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground">Applicants</p>
              </div>
            </div>
          </Card>

          <Card className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-yellow-500/10 rounded-lg">
                <Clock className="w-4 h-4 md:w-5 md:h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold">{stats.pendingReviews}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground">Pending</p>
              </div>
            </div>
          </Card>

          <Card className="p-3 md:p-4 col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-green-500/10 rounded-lg">
                <UserCheck className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
              </div>
              <div>
                <p className="text-xl md:text-2xl font-bold">{stats.verified}</p>
                <p className="text-[10px] md:text-xs text-muted-foreground">Verified</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Top Candidates Auto-Sift Section */}
        <Card className="p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Top Candidates (Auto-Sift)
                <Badge variant="outline" className="ml-2 text-xs">AI Ranked</Badge>
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Automatically ranked candidates by interview performance scores
              </p>
            </div>
          </div>
          <TopCandidatesView 
            jobs={jobs}
            onCandidateSelect={(candidateId) => {
              // Navigate to candidates tab filtered by this candidate
              setSearchQuery(candidateId.toString());
              setActiveTab("candidates");
            }}
          />
        </Card>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="relative flex-1 sm:max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search jobs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-36 h-10">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : viewMode === "kanban" ? (
          <div id="tour-pipeline" className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {PIPELINE_STAGES.map(stage => (
              <div key={stage.id} className="space-y-2 md:space-y-3">
                <div className="group relative flex items-center gap-2 p-2 md:p-3 bg-muted/50 rounded-lg cursor-default">
                  <div className={`w-2.5 h-2.5 md:w-3 md:h-3 rounded-full ${stage.color}`} />
                  <h3 className="font-semibold text-sm md:text-base">{stage.label}</h3>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {getApplicationsByStage(stage.id).length}
                  </Badge>
                  {stage.tooltip && (
                    <div className="absolute bottom-full left-0 mb-2 w-56 bg-popover border border-border rounded-lg p-2 text-xs text-foreground shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      {stage.tooltip}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2 min-h-[100px] md:min-h-[200px]">
                  {getApplicationsByStage(stage.id).map(app => {
                    const createdTs = app.created_at ? new Date(app.created_at).getTime() : Date.now();
                    const hoursInStage = isNaN(createdTs) ? 0 : Math.floor((Date.now() - createdTs) / 36e5);
                    const daysInStage = Math.floor(hoursInStage / 24);
                    const timeLabel = daysInStage >= 1 ? `${daysInStage}d in ${stage.label}` : `${hoursInStage}h in ${stage.label}`;
                    const timeColor = hoursInStage < 24 ? "text-green-600 dark:text-green-400" : hoursInStage < 48 ? "text-amber-500" : "text-red-500";
                    const score = (app as any).overall_score ?? (app as any).overallScore;
                    const band = score >= 70 ? "Green" : score >= 50 ? "Amber" : score != null ? "Red" : null;
                    return (
                    <Card 
                      key={app.id} 
                      className="p-3 md:p-4 cursor-pointer hover:border-primary/50 transition-colors active:scale-[0.98]"
                      onClick={() => openAgentSidebar(app)}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div className="min-w-0 flex-1 mr-2">
                          <p className="font-medium text-sm md:text-base truncate">{app.candidate_name || "Candidate"}</p>
                          <p className="text-xs text-muted-foreground truncate">{(app as any).jobTitle}</p>
                        </div>
                        {app.human_verified && (
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-[10px] md:text-xs flex-shrink-0">
                            <UserCheck className="w-3 h-3 mr-0.5 md:mr-1" />
                            <span className="hidden sm:inline">Verified</span>
                          </Badge>
                        )}
                      </div>

                      {/* Stage-specific status text */}
                      {stage.id === "initial_review" && (
                        <p className="text-xs text-muted-foreground mb-2">Awaiting interview link</p>
                      )}
                      {stage.id === "link_sent" && (
                        <p className="text-xs text-muted-foreground mb-2">Link sent — waiting for candidate to open it</p>
                      )}
                      {stage.id === "in_progress" && app.status !== "interview_completed" && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                          <p className="text-xs text-green-600 dark:text-green-400">Interview in progress...</p>
                        </div>
                      )}
                      {stage.id === "in_progress" && app.status === "interview_completed" && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">AI scoring is taking longer than expected. This sometimes happens with longer submissions. It will complete automatically - you can move on and come back.</p>
                      )}
                      {stage.id === "dossier_ready" && (
                        <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-2">Dossier ready</p>
                      )}
                      {stage.id === "under_review" && (
                        (app.pipeline_stage === "submitted" || app.pipeline_stage === "ai_triage") ? (
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse flex-shrink-0" />
                            <p className="text-xs text-cyan-600 dark:text-cyan-400">AI scoring in progress...</p>
                          </div>
                        ) : (
                          <p className="text-xs text-orange-600 dark:text-orange-400 mb-2">Under review</p>
                        )
                      )}
                      {stage.id === "outcome" && (
                        <p className="text-xs font-medium mb-2">
                          {app.outcome === "in_person" ? "Proceeding to in-person" : app.outcome === "not_proceeding" ? "Not proceeding" : "Outcome recorded"}
                        </p>
                      )}

                      {/* Band badge if available */}
                      {band && (
                        <div className="mb-2">
                          <Badge className={`text-[10px] ${band === "Green" ? "bg-green-500/15 text-green-700 border-green-400" : band === "Amber" ? "bg-amber-500/15 text-amber-700 border-amber-400" : "bg-red-500/15 text-red-700 border-red-400"}`}>
                            {band === "Green" ? "Green — high confidence" : band === "Amber" ? "Amber — review flagged question" : "Red — low score"} · {score}/100
                          </Badge>
                        </div>
                      )}

                      {/* Time in stage */}
                      <div className={`flex items-center gap-1 text-[10px] font-medium mb-2 ${timeColor}`}>
                        <Clock className="w-2.5 h-2.5" />
                        {timeLabel}
                      </div>

                      <div className="flex flex-col gap-1">
                        {/* Primary action — one per stage */}
                        {stage.id === "initial_review" && (
                          <div className="w-full space-y-1">
                            <Button
                              size="sm"
                              className="text-xs h-7 w-full bg-blue-600 hover:bg-blue-700 text-white pulse-blue disabled:opacity-60 disabled:cursor-not-allowed"
                              disabled={sendingInviteIds.has(app.id)}
                              onClick={(e) => { e.stopPropagation(); generateInviteLink(app.id); }}
                              data-testid={`button-invite-kanban-${app.id}`}
                            >
                              {sendingInviteIds.has(app.id) ? (
                                <><span className="w-3 h-3 mr-1 animate-spin rounded-full border border-white border-t-transparent inline-block" />Sending…</>
                              ) : (
                                <><Link2 className="w-3 h-3 mr-1" />Send Interview Link</>
                              )}
                            </Button>
                          </div>
                        )}

                        {stage.id === "link_sent" && (
                          <div className="w-full space-y-1">
                            <Button size="sm" disabled className="text-xs h-7 w-full bg-muted text-muted-foreground cursor-not-allowed" data-testid={`button-awaiting-${app.id}`}>
                              Awaiting Response
                            </Button>
                            <Button size="sm" variant="ghost" className="text-[10px] h-6 w-full text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); resendInvite(app.id); }} data-testid={`button-resend-link-${app.id}`}>
                              <RefreshCw className="w-2.5 h-2.5 mr-1" />Resend Link
                            </Button>
                          </div>
                        )}

                        {stage.id === "in_progress" && (
                          <Button size="sm" disabled className="text-xs h-7 w-full bg-muted text-muted-foreground cursor-not-allowed" data-testid={`button-in-progress-${app.id}`}>
                            Interview In Progress
                          </Button>
                        )}

                        {stage.id === "dossier_ready" && (
                          <div className="w-full space-y-1">
                            <Button
                              size="sm"
                              className="text-xs h-7 bg-green-600 hover:bg-green-700 text-white w-full pulse-green"
                              onClick={(e) => { e.stopPropagation(); openSendDossierModal(app); }}
                              data-testid={`button-send-dossier-${app.id}`}
                            >
                              Send Dossier →
                            </Button>
                            <Button
                              size="sm"
                              className="text-xs h-7 w-full bg-teal-600 hover:bg-teal-700 text-white"
                              onClick={(e) => { e.stopPropagation(); openOutcomeModal(app); }}
                              data-testid={`button-set-outcome-${app.id}`}
                            >
                              Set Outcome →
                            </Button>
                            {/* Recruiter feedback prompt */}
                            {feedbackSubmitted.has(typeof app.id === "string" ? parseInt(app.id) : app.id) ? (
                              <p className="text-[10px] text-green-600 dark:text-green-400 text-center pt-1" data-testid={`feedback-thanks-${app.id}`}>Thanks for your feedback!</p>
                            ) : (
                              <div className="pt-1 space-y-1" onClick={(e) => e.stopPropagation()} data-testid={`feedback-prompt-${app.id}`}>
                                <p className="text-[10px] text-muted-foreground text-center">Rate this dossier</p>
                                <div className="flex justify-center gap-0.5">
                                  {[1,2,3,4,5].map(n => {
                                    const appId = typeof app.id === "string" ? parseInt(app.id) : app.id;
                                    return (
                                      <button key={n} type="button"
                                        onClick={() => setFeedbackRating(prev => ({ ...prev, [appId]: n }))}
                                        onMouseEnter={() => setFeedbackHover(prev => ({ ...prev, [appId]: n }))}
                                        onMouseLeave={() => setFeedbackHover(prev => ({ ...prev, [appId]: 0 }))}
                                        data-testid={`recruiter-star-${n}-${app.id}`}
                                        className="p-0.5"
                                      >
                                        <Star className={`w-4 h-4 ${((feedbackHover[appId] || feedbackRating[appId]) >= n) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`} />
                                      </button>
                                    );
                                  })}
                                </div>
                                {feedbackRating[typeof app.id === "string" ? parseInt(app.id) : app.id] > 0 && (
                                  <Button size="sm" variant="outline" className="text-[10px] h-6 w-full"
                                    disabled={feedbackSaving.has(typeof app.id === "string" ? parseInt(app.id) : app.id)}
                                    onClick={() => submitRecruiterFeedback(typeof app.id === "string" ? parseInt(app.id) : app.id)}
                                    data-testid={`button-submit-recruiter-feedback-${app.id}`}
                                  >
                                    {feedbackSaving.has(typeof app.id === "string" ? parseInt(app.id) : app.id) ? "Saving..." : "Submit"}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {stage.id === "under_review" && (() => {
                          if (app.pipeline_stage === "submitted" || app.pipeline_stage === "ai_triage") {
                            return (
                              <Button size="sm" disabled className="text-xs h-7 w-full bg-muted text-muted-foreground cursor-not-allowed" data-testid={`button-ai-processing-${app.id}`}>
                                <span className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse inline-block mr-1.5 flex-shrink-0" />
                                AI scoring in progress
                              </Button>
                            );
                          }
                          const appInterview = interviews.find(i => i.applicationId === parseInt(app.id as string));
                          if (!appInterview) {
                            return (
                              <Button size="sm" disabled className="text-xs h-7 w-full bg-muted text-muted-foreground cursor-not-allowed" data-testid={`button-no-interview-${app.id}`}>
                                No interview found
                              </Button>
                            );
                          }
                          return (
                            <div className="w-full space-y-1">
                              <Button size="sm" className="text-xs h-7 w-full bg-blue-600 hover:bg-blue-700 text-white pulse-blue" onClick={(e) => { e.stopPropagation(); openReview(appInterview); }} data-testid={`button-review-kanban-${app.id}`}>
                                Review & Create Dossier →
                              </Button>
                            </div>
                          );
                        })()}

                        {stage.id === "not_proceeding" && (
                          <Button size="sm" disabled className="text-xs h-7 w-full bg-muted text-muted-foreground cursor-not-allowed" data-testid={`button-not-proceeding-${app.id}`}>
                            Not Proceeding
                          </Button>
                        )}

                        {stage.id === "outcome" && (
                          <div className="w-full space-y-1">
                            <Button size="sm" disabled className="text-xs h-7 w-full bg-muted text-muted-foreground cursor-not-allowed" data-testid={`button-completed-${app.id}`}>
                              Completed
                            </Button>
                            {/* Recruiter feedback prompt */}
                            {feedbackSubmitted.has(typeof app.id === "string" ? parseInt(app.id) : app.id) ? (
                              <p className="text-[10px] text-green-600 dark:text-green-400 text-center pt-1" data-testid={`feedback-thanks-outcome-${app.id}`}>Thanks for your feedback!</p>
                            ) : (
                              <div className="pt-1 space-y-1" onClick={(e) => e.stopPropagation()} data-testid={`feedback-prompt-outcome-${app.id}`}>
                                <p className="text-[10px] text-muted-foreground text-center">Rate this assessment</p>
                                <div className="flex justify-center gap-0.5">
                                  {[1,2,3,4,5].map(n => {
                                    const appId = typeof app.id === "string" ? parseInt(app.id) : app.id;
                                    return (
                                      <button key={n} type="button"
                                        onClick={() => setFeedbackRating(prev => ({ ...prev, [appId]: n }))}
                                        onMouseEnter={() => setFeedbackHover(prev => ({ ...prev, [appId]: n }))}
                                        onMouseLeave={() => setFeedbackHover(prev => ({ ...prev, [appId]: 0 }))}
                                        data-testid={`recruiter-star-outcome-${n}-${app.id}`}
                                        className="p-0.5"
                                      >
                                        <Star className={`w-4 h-4 ${((feedbackHover[appId] || feedbackRating[appId]) >= n) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`} />
                                      </button>
                                    );
                                  })}
                                </div>
                                {feedbackRating[typeof app.id === "string" ? parseInt(app.id) : app.id] > 0 && (
                                  <Button size="sm" variant="outline" className="text-[10px] h-6 w-full"
                                    disabled={feedbackSaving.has(typeof app.id === "string" ? parseInt(app.id) : app.id)}
                                    onClick={() => submitRecruiterFeedback(typeof app.id === "string" ? parseInt(app.id) : app.id)}
                                    data-testid={`button-submit-recruiter-feedback-outcome-${app.id}`}
                                  >
                                    {feedbackSaving.has(typeof app.id === "string" ? parseInt(app.id) : app.id) ? "Saving..." : "Submit"}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    </Card>
                    );
                  })}
                  
                  {getApplicationsByStage(stage.id).length === 0 && (
                    <div className="p-8 text-center text-muted-foreground border-2 border-dashed rounded-lg">
                      <p className="text-sm">No candidates in this stage right now.</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="p-6">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Verified</TableHead>
                  <TableHead>Applied</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getAllApplications().map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{app.candidate_name || "Candidate"}</p>
                          <p className="text-xs text-muted-foreground">[Contact Protected]</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{(app as any).jobTitle}</TableCell>
                    <TableCell>
                      <Badge className={getApplicationStatusColor(app.status)}>
                        {PIPELINE_STAGES.find(s => s.id === (STAGE_DISPLAY_MAP[app.pipeline_stage || "initial_review"] ?? app.pipeline_stage ?? "initial_review"))?.label || "Initial Review"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {app.human_verified ? (
                        <Badge className="bg-green-500/10 text-green-600">
                          <UserCheck className="w-3 h-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge variant="outline">Pending</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(app.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={sendingInviteIds.has(app.id)}
                          onClick={(e) => { e.stopPropagation(); generateInviteLink(app.id); }}
                          data-testid={`button-invite-${app.id}`}
                        >
                          {sendingInviteIds.has(app.id) ? (
                            <><span className="w-4 h-4 mr-1 animate-spin rounded-full border border-current border-t-transparent inline-block" />Sending…</>
                          ) : (
                            <><Link2 className="w-4 h-4 mr-1" />Invite</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAgentSidebar(app)}
                        >
                          <Shield className="w-4 h-4 mr-1" />
                          Verify
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => exportTalentPacket(app)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        )}
        </>
        )}

        {activeTab === "jobs" && (
          <div className="space-y-6">
            {/* Context Banner */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border text-sm">
              <Briefcase className="w-5 h-5 text-primary" />
              <span className="text-muted-foreground">
                <strong className="text-foreground">Jobs</strong> define open roles. Candidates apply to jobs, then move through the 
                <button onClick={() => setActiveTab("pipeline")} className="text-primary hover:underline mx-1">Pipeline</button>
                to receive interview
                <button onClick={() => setActiveTab("invites")} className="text-accent hover:underline mx-1">Invites</button>.
              </span>
            </div>
            
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-2">Job Postings</h2>
              <p className="text-muted-foreground mb-6">Manage job postings for all employers. Click on a job to view details or edit.</p>
              
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading jobs...</p>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">No Jobs Yet</h3>
                  <p className="text-muted-foreground mb-4">Create your first job posting to start receiving applications</p>
                  <Link href="/agent/create-job">
                    <Button className="gap-2">
                      <Plus className="w-4 h-4" />
                      Create Job
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job Title</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Interview Link</TableHead>
                      <TableHead>JD Document</TableHead>
                      <TableHead>Applicants</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredJobs.map((job) => {
                      const hasRequirements = job.employer_question_requirements && (
                        (job.employer_question_requirements.customQuestions?.length || 0) > 0 ||
                        (job.employer_question_requirements.topicsToAssess?.length || 0) > 0 ||
                        (job.employer_question_requirements.scenariosToInclude?.length || 0) > 0 ||
                        (job.employer_question_requirements.specificSkillsToTest?.length || 0) > 0 ||
                        (job.employer_question_requirements.notes?.trim().length || 0) > 0
                      );
                      const reqCount = hasRequirements ? (
                        (job.employer_question_requirements?.customQuestions?.length || 0) +
                        (job.employer_question_requirements?.topicsToAssess?.length || 0) +
                        (job.employer_question_requirements?.scenariosToInclude?.length || 0) +
                        (job.employer_question_requirements?.specificSkillsToTest?.length || 0)
                      ) : 0;
                      
                      const customConfigured = jobQuestionCounts[String(job.id)]?.hasCustom ?? false;

                      return (
                      <TableRow key={job.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Briefcase className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <span className="font-medium">{job.title}</span>
                              {customConfigured ? (
                                <span className="text-xs text-green-600 dark:text-green-400 font-medium">✓ Ready</span>
                              ) : (
                                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium pulse-amber">⚠ Set Questions</span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-muted-foreground" />
                            {job.location || "Remote"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(job.status)}>
                            {job.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-xs"
                            onClick={async () => {
                              try {
                                // FIXED: POST /api/agent/role-invites now accessible to RECRUITMENT_AGENT
                                const res = await apiRequest("POST", "/api/agent/role-invites", {
                                  roleId: job.id.toString(),
                                  expiresInHours: 72,
                                  questionCount: 5,
                                });
                                const data = await res.json();
                                if (data.inviteUrl) {
                                  await navigator.clipboard.writeText(data.inviteUrl);
                                  setLinkCopied(true);
                                  toast.success("Interview link copied to clipboard!");
                                  setTimeout(() => setLinkCopied(false), 2500);
                                  fetchInvites();
                                  fetchJobs();
                                } else {
                                  toast.error("Failed to generate link");
                                }
                              } catch (err) {
                                toast.error("Failed to generate interview link");
                              }
                            }}
                            data-testid={`button-quick-invite-${job.id}`}
                          >
                            <Link2 className="w-3 h-3" />
                            Generate & Copy
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {job.jd_pdf_storage_path ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1"
                                onClick={async () => {
                                  try {
                                    const res = await apiRequest("GET", `/api/agent/jobs/${job.id}/pdf-url`);
                                    if (res.ok) {
                                      const { url } = await res.json();
                                      window.open(url, '_blank');
                                    } else {
                                      toast.error("Failed to get document URL");
                                    }
                                  } catch (err) {
                                    toast.error("Failed to open document");
                                  }
                                }}
                                data-testid={`button-view-jd-${job.id}`}
                              >
                                <FileText className="w-4 h-4" />
                                View
                              </Button>
                            ) : !isNaN(parseInt(job.id)) ? (
                              <DocumentUploader
                                documentType="job_description"
                                roleId={parseInt(job.id)}
                                buttonText="Upload JD"
                                onUploadComplete={() => {
                                  toast.success("Job description uploaded");
                                  fetchJobs();
                                }}
                              />
                            ) : (
                              <span className="text-muted-foreground text-sm">No file</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            {job.applicants_total || job.applicant_count || 0}
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(job.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Link href={`/agent/jobs/${job.id}?tab=candidates`}>
                              <Button
                                size="sm"
                                variant="default"
                                className="gap-1 bg-blue-600 hover:bg-blue-700"
                                data-testid={`button-saved-job-${job.id}`}
                              >
                                <Users className="w-4 h-4" />
                                Saved
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditJobModal(job)}
                              className={!customConfigured ? "pulse-amber border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-500 dark:hover:bg-amber-900/20" : ""}
                              data-testid={`button-edit-job-${job.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              {customConfigured ? "Edit" : "Set Up Questions"}
                            </Button>
                            <Link href={`/agent/jobs/${job.id}`}>
                              <Button size="sm" variant="outline" data-testid={`button-view-job-${job.id}`}>
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              onClick={() => openQuestionsModal(job)}
                              data-testid={`button-questions-job-${job.id}`}
                              title="Manage Interview Questions"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setConfirmDeleteJob({id: job.id, title: job.title})}
                              disabled={deletingJobId === job.id}
                              data-testid={`button-delete-job-${job.id}`}
                            >
                              {deletingJobId === job.id ? (
                                <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
                </div>
              )}
            </Card>

            {editingJob && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) { setEditingJob(null); setSelectedJobForQuestions(null); setEditingQuestion(null); } }}>
                <Card className={`w-full bg-background my-8 ${editJobTab === "questions" ? "max-w-3xl" : "max-w-2xl"}`} onClick={(e) => e.stopPropagation()}>
                  {/* Tab header */}
                  <div className="flex items-center justify-between border-b px-6 pt-5 pb-0">
                    <div className="flex gap-0">
                      <button
                        className={`px-4 pb-3 text-sm font-medium border-b-2 transition-colors ${editJobTab === "details" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                        onClick={() => setEditJobTab("details")}
                        data-testid="tab-job-details"
                      >
                        Job Details
                      </button>
                      <button
                        className={`px-4 pb-3 text-sm font-medium border-b-2 transition-colors ${editJobTab === "questions" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                        onClick={() => setEditJobTab("questions")}
                        data-testid="tab-interview-questions"
                      >
                        Interview Questions {jobQuestions.length > 0 ? `(${jobQuestions.length})` : "⚠"}
                      </button>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingJob(null); setSelectedJobForQuestions(null); setEditingQuestion(null); }} className="mb-2">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Tab 1 — Job Details */}
                  {editJobTab === "details" && (
                  <div className="p-6">
                  <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Job Title</label>
                        <Input
                          value={editJobForm.title}
                          onChange={(e) => setEditJobForm({...editJobForm, title: e.target.value})}
                          placeholder="Enter job title"
                          data-testid="input-edit-job-title"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium mb-1 block">Location</label>
                        <Input
                          value={editJobForm.location}
                          onChange={(e) => setEditJobForm({...editJobForm, location: e.target.value})}
                          placeholder="e.g., Remote, New York, London"
                          data-testid="input-edit-job-location"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Status</label>
                        <Select
                          value={editJobForm.status}
                          onValueChange={(value) => setEditJobForm({...editJobForm, status: value})}
                        >
                          <SelectTrigger data-testid="select-edit-job-status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="published">Published</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium mb-1 block">Salary Range</label>
                        <Input
                          value={editJobForm.salary_range}
                          onChange={(e) => setEditJobForm({...editJobForm, salary_range: e.target.value})}
                          placeholder="e.g., $80,000 - $120,000"
                          data-testid="input-edit-job-salary"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Date Required</label>
                        <Input
                          type="date"
                          value={editJobForm.date_required}
                          onChange={(e) => setEditJobForm({...editJobForm, date_required: e.target.value})}
                          data-testid="input-edit-job-date"
                        />
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium mb-1 block">Employment Type</label>
                        <Select
                          value={editJobForm.employment_type}
                          onValueChange={(value) => setEditJobForm({...editJobForm, employment_type: value})}
                        >
                          <SelectTrigger data-testid="select-edit-job-employment">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="full-time">Full-time</SelectItem>
                            <SelectItem value="part-time">Part-time</SelectItem>
                            <SelectItem value="contract">Contract</SelectItem>
                            <SelectItem value="temporary">Temporary</SelectItem>
                            <SelectItem value="internship">Internship</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium mb-1 block">Work Mode</label>
                        <Select
                          value={editJobForm.work_mode}
                          onValueChange={(value) => setEditJobForm({...editJobForm, work_mode: value})}
                        >
                          <SelectTrigger data-testid="select-edit-job-workmode">
                            <SelectValue placeholder="Select mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="on-site">On-site</SelectItem>
                            <SelectItem value="remote">Remote</SelectItem>
                            <SelectItem value="hybrid">Hybrid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-1 block">Job Description</label>
                      <Textarea
                        value={editJobForm.description}
                        onChange={(e) => setEditJobForm({...editJobForm, description: e.target.value})}
                        placeholder="Enter the full job description..."
                        className="min-h-[150px]"
                        data-testid="input-edit-job-description"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" onClick={() => { setEditingJob(null); setSelectedJobForQuestions(null); setEditingQuestion(null); }} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={saveJob} disabled={savingJob} className="flex-1 gap-2" data-testid="button-save-job">
                      {savingJob ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                  </div>
                  )}

                  {/* Tab 2 — Interview Questions */}
                  {editJobTab === "questions" && (
                  <div className="p-6 max-h-[80vh] overflow-y-auto">
                    {/* Success banner */}
                    {questionSaveBanner && (
                      <div className="flex items-center justify-between gap-3 mb-4 px-4 py-3 rounded-lg border border-blue-500 pulse-blue text-white text-sm" style={{ background: "#1D4ED8" }} data-testid="question-save-banner">
                        <span>✓ Questions saved — return to JobAdder and click Vexis Interview to send the assessment to your candidate.</span>
                        <button onClick={() => setQuestionSaveBanner(false)} className="shrink-0 opacity-80 hover:opacity-100"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                    {/* Zero-questions prompt */}
                    {jobQuestions.length === 0 && !loadingQuestions && (
                      <div className="mb-4 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-700 dark:text-amber-400">
                        Add at least one custom question before sending assessments for this role.
                      </div>
                    )}
                    <div className="flex gap-2 mb-4">
                      <Button variant="outline" onClick={regenerateQuestions} disabled={regeneratingQuestions} className="gap-2" data-testid="button-regenerate-questions-tab">
                        {regeneratingQuestions ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Regenerate All
                      </Button>
                      <Button onClick={() => setShowAddQuestion(true)} className="gap-2" data-testid="button-add-question-tab">
                        <Plus className="w-4 h-4" />Add Question
                      </Button>
                    </div>
                    {loadingQuestions ? (
                      <div className="text-center py-8">
                        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading questions...</p>
                      </div>
                    ) : jobQuestions.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed rounded-lg">
                        <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No interview questions yet.</p>
                        <p className="text-sm text-muted-foreground mt-2">Click "Regenerate All" to generate questions from the job description.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {jobQuestions.map((q, idx) => (
                          <div key={q.id} className="border rounded-lg p-4 bg-muted/30">
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
                                  <Badge variant="secondary" className="text-xs">{q.questionType}</Badge>
                                  <Badge variant="outline" className="text-xs">{q.category}</Badge>
                                  <span className="text-xs text-muted-foreground ml-auto">{q.timeLimit || 30}s</span>
                                </div>
                                {editingQuestion?.id === q.id ? (
                                  <div className="space-y-3">
                                    <Textarea value={editQuestionForm.questionText} onChange={(e) => setEditQuestionForm({...editQuestionForm, questionText: e.target.value})} className="min-h-[80px]" data-testid={`textarea-edit-q-tab-${q.id}`} />
                                    <div className="grid grid-cols-3 gap-2">
                                      <Select value={editQuestionForm.questionType} onValueChange={(v) => setEditQuestionForm({...editQuestionForm, questionType: v})}>
                                        <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="behavioral">Behavioral</SelectItem>
                                          <SelectItem value="technical">Technical</SelectItem>
                                          <SelectItem value="scenario">Scenario</SelectItem>
                                          <SelectItem value="verification">Verification</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <Input placeholder="Category" value={editQuestionForm.category} onChange={(e) => setEditQuestionForm({...editQuestionForm, category: e.target.value})} />
                                      <Input type="number" placeholder="Time (s)" value={editQuestionForm.timeLimit} onChange={(e) => setEditQuestionForm({...editQuestionForm, timeLimit: parseInt(e.target.value) || 30})} />
                                    </div>
                                    <div className="flex gap-2">
                                      <Button size="sm" onClick={updateQuestion} disabled={savingQuestion}>{savingQuestion ? "Saving..." : "Save"}</Button>
                                      <Button size="sm" variant="outline" onClick={() => setEditingQuestion(null)}>Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm">{q.questionText}</p>
                                )}
                              </div>
                              {editingQuestion?.id !== q.id && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingQuestion(q); setEditQuestionForm({ questionText: q.questionText, questionType: q.questionType, category: q.category, timeLimit: q.timeLimit || 30 }); }} data-testid={`button-edit-q-tab-${q.id}`}><Pencil className="w-4 h-4" /></Button>
                                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => deleteQuestion(q.id)} data-testid={`button-delete-q-tab-${q.id}`}><Trash2 className="w-4 h-4" /></Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {showAddQuestion && (
                      <div className="mt-4 border rounded-lg p-4 bg-muted/50">
                        <h4 className="font-medium mb-3">Add New Question</h4>
                        <div className="space-y-3">
                          <Textarea placeholder="Enter your question..." value={newQuestionForm.questionText} onChange={(e) => setNewQuestionForm({...newQuestionForm, questionText: e.target.value})} className="min-h-[80px]" data-testid="textarea-new-question-tab" />
                          <div className="grid grid-cols-3 gap-2">
                            <Select value={newQuestionForm.questionType} onValueChange={(v) => setNewQuestionForm({...newQuestionForm, questionType: v})}>
                              <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="behavioral">Behavioral</SelectItem>
                                <SelectItem value="technical">Technical</SelectItem>
                                <SelectItem value="scenario">Scenario</SelectItem>
                                <SelectItem value="verification">Verification</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input placeholder="Category" value={newQuestionForm.category} onChange={(e) => setNewQuestionForm({...newQuestionForm, category: e.target.value})} data-testid="input-new-question-category-tab" />
                            <Input type="number" placeholder="Time (s)" value={newQuestionForm.timeLimit} onChange={(e) => setNewQuestionForm({...newQuestionForm, timeLimit: parseInt(e.target.value) || 30})} data-testid="input-new-question-time-tab" />
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={addQuestion} disabled={addingQuestion || !newQuestionForm.questionText.trim()}>{addingQuestion ? "Adding..." : "Add Question"}</Button>
                            <Button variant="outline" onClick={() => setShowAddQuestion(false)}>Cancel</Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </Card>
              </div>
            )}

            {selectedJobForQuestions && !editingJob && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) { setSelectedJobForQuestions(null); setEditingQuestion(null); setShowAddQuestion(false); } }}>
                <Card className="w-full max-w-3xl p-6 bg-background my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-xl font-bold flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        Interview Questions
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">{selectedJobForQuestions.title}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedJobForQuestions(null); setEditingQuestion(null); setShowAddQuestion(false); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="flex gap-2 mb-6">
                    <Button
                      variant="outline"
                      onClick={regenerateQuestions}
                      disabled={regeneratingQuestions}
                      className="gap-2"
                      data-testid="button-regenerate-questions"
                    >
                      {regeneratingQuestions ? (
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Regenerate All
                    </Button>
                    <Button
                      onClick={() => setShowAddQuestion(true)}
                      className="gap-2"
                      data-testid="button-add-question"
                    >
                      <Plus className="w-4 h-4" />
                      Add Question
                    </Button>
                  </div>

                  {loadingQuestions ? (
                    <div className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                      <p className="text-muted-foreground">Loading questions...</p>
                    </div>
                  ) : jobQuestions.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg">
                      <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No interview questions yet.</p>
                      <p className="text-sm text-muted-foreground mt-2">Click "Regenerate All" to generate questions based on the job description.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {jobQuestions.map((q, idx) => (
                        <div key={q.id} className="border rounded-lg p-4 bg-muted/30">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
                                <Badge variant="secondary" className="text-xs">{q.questionType}</Badge>
                                <Badge variant="outline" className="text-xs">{q.category}</Badge>
                                <span className="text-xs text-muted-foreground ml-auto">{q.timeLimit || 30}s</span>
                              </div>
                              {editingQuestion?.id === q.id ? (
                                <div className="space-y-3">
                                  <Textarea
                                    value={editQuestionForm.questionText}
                                    onChange={(e) => setEditQuestionForm({...editQuestionForm, questionText: e.target.value})}
                                    className="min-h-[80px]"
                                    data-testid={`textarea-edit-question-${q.id}`}
                                  />
                                  <div className="grid grid-cols-3 gap-2">
                                    <Select value={editQuestionForm.questionType} onValueChange={(v) => setEditQuestionForm({...editQuestionForm, questionType: v})}>
                                      <SelectTrigger data-testid={`select-edit-question-type-${q.id}`}>
                                        <SelectValue placeholder="Type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="behavioral">Behavioral</SelectItem>
                                        <SelectItem value="technical">Technical</SelectItem>
                                        <SelectItem value="scenario">Scenario</SelectItem>
                                        <SelectItem value="verification">Verification</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <Input
                                      placeholder="Category"
                                      value={editQuestionForm.category}
                                      onChange={(e) => setEditQuestionForm({...editQuestionForm, category: e.target.value})}
                                      data-testid={`input-edit-question-category-${q.id}`}
                                    />
                                    <Input
                                      type="number"
                                      placeholder="Time (s)"
                                      value={editQuestionForm.timeLimit}
                                      onChange={(e) => setEditQuestionForm({...editQuestionForm, timeLimit: parseInt(e.target.value) || 30})}
                                      data-testid={`input-edit-question-time-${q.id}`}
                                    />
                                  </div>
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={updateQuestion} disabled={savingQuestion}>
                                      {savingQuestion ? "Saving..." : "Save"}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setEditingQuestion(null)}>Cancel</Button>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm">{q.questionText}</p>
                              )}
                            </div>
                            {editingQuestion?.id !== q.id && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingQuestion(q);
                                    setEditQuestionForm({
                                      questionText: q.questionText,
                                      questionType: q.questionType,
                                      category: q.category,
                                      timeLimit: q.timeLimit || 30,
                                    });
                                  }}
                                  data-testid={`button-edit-question-${q.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => deleteQuestion(q.id)}
                                  data-testid={`button-delete-question-${q.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showAddQuestion && (
                    <div className="mt-6 border rounded-lg p-4 bg-muted/50">
                      <h4 className="font-medium mb-3">Add New Question</h4>
                      <div className="space-y-3">
                        <Textarea
                          placeholder="Enter your question..."
                          value={newQuestionForm.questionText}
                          onChange={(e) => setNewQuestionForm({...newQuestionForm, questionText: e.target.value})}
                          className="min-h-[80px]"
                          data-testid="textarea-new-question"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <Select value={newQuestionForm.questionType} onValueChange={(v) => setNewQuestionForm({...newQuestionForm, questionType: v})}>
                            <SelectTrigger data-testid="select-new-question-type">
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="behavioral">Behavioral</SelectItem>
                              <SelectItem value="technical">Technical</SelectItem>
                              <SelectItem value="scenario">Scenario</SelectItem>
                              <SelectItem value="verification">Verification</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            placeholder="Category"
                            value={newQuestionForm.category}
                            onChange={(e) => setNewQuestionForm({...newQuestionForm, category: e.target.value})}
                            data-testid="input-new-question-category"
                          />
                          <Input
                            type="number"
                            placeholder="Time (s)"
                            value={newQuestionForm.timeLimit}
                            onChange={(e) => setNewQuestionForm({...newQuestionForm, timeLimit: parseInt(e.target.value) || 30})}
                            data-testid="input-new-question-time"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={addQuestion} disabled={addingQuestion || !newQuestionForm.questionText.trim()}>
                            {addingQuestion ? "Adding..." : "Add Question"}
                          </Button>
                          <Button variant="outline" onClick={() => setShowAddQuestion(false)}>Cancel</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {activeTab === "candidates" && (
          <div className="space-y-6">
            {/* Context Banner */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border text-sm">
              <Users className="w-5 h-5 text-secondary" />
              <span className="text-muted-foreground">
                <strong className="text-foreground">Candidates</strong> in this database can be matched to jobs. Track their progress in the 
                <button onClick={() => setActiveTab("pipeline")} className="text-primary hover:underline mx-1">Pipeline</button>
                tab after they apply.
              </span>
            </div>
            
            <Card className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2">Candidate Database</h2>
                  <p className="text-sm text-muted-foreground">Direct and non-JobAdder candidates. JobAdder candidates are managed via the Assessment Pipeline.</p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search candidates..."
                      value={candidateSearch}
                      onChange={(e) => setCandidateSearch(e.target.value)}
                      className="pl-9 w-full sm:w-64"
                      data-testid="input-candidate-search"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={fetchCandidates} size="sm" className="flex-1 sm:flex-none">
                      Refresh
                    </Button>
                    <Button onClick={() => setShowNewCandidateDialog(true)} className="gap-2 flex-1 sm:flex-none" data-testid="button-new-candidate">
                      <Plus className="w-4 h-4" />
                      <span className="sm:inline">New</span>
                    </Button>
                    {candidates.some(c => c.isPlaceholder) && user?.role === "ADMIN" && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                        onClick={async () => {
                          const count = candidates.filter(c => c.isPlaceholder).length;
                          if (!confirm(`Delete ${count} placeholder/demo candidate entries?`)) return;
                          try {
                            const res = await apiRequest("DELETE", "/api/agent/candidates/placeholders");
                            const data = await res.json();
                            if (res.ok) {
                              toast.success(`Cleaned up ${data.deleted} placeholder entries`);
                              fetchCandidates();
                            } else {
                              toast.error(data.error || "Failed to clean up");
                            }
                          } catch {
                            toast.error("Failed to clean up placeholder entries");
                          }
                        }}
                        data-testid="button-cleanup-placeholders"
                      >
                        Clean Up Demo Data ({candidates.filter(c => c.isPlaceholder).length})
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {loadingCandidates ? (
                <div className="text-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading candidates...</p>
                </div>
              ) : candidates.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Direct Candidates</h3>
                  <p className="text-muted-foreground mb-4">This database is for candidates assessed outside of JobAdder — referrals, direct applicants, or walk-ins. Candidates sourced through JobAdder are assessed via the SideDrawer and tracked in your Assessment Pipeline.</p>
                  <Button onClick={() => setShowNewCandidateDialog(true)} className="gap-2" data-testid="button-add-candidate-empty">
                    <Plus className="w-4 h-4" />
                    Add Candidate
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-6 px-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead className="hidden sm:table-cell">Email</TableHead>
                      <TableHead className="hidden md:table-cell">Applied For</TableHead>
                      <TableHead className="hidden lg:table-cell">Resume</TableHead>
                      <TableHead className="hidden md:table-cell">Registered</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {candidates
                      .filter(c => !c.isPlaceholder)
                      .filter(c => 
                        !candidateSearch || 
                        c.fullName?.toLowerCase().includes(candidateSearch.toLowerCase()) ||
                        c.email?.toLowerCase().includes(candidateSearch.toLowerCase()) ||
                        c.rolesDisplay?.toLowerCase().includes(candidateSearch.toLowerCase())
                      )
                      .map((candidate) => (
                        <TableRow key={candidate.id} data-testid={`candidate-row-${candidate.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-2 md:gap-3">
                              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                <Users className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                              </div>
                              <div>
                                <span className="font-medium text-sm md:text-base">{candidate.fullName}</span>
                                <p className="text-xs text-muted-foreground sm:hidden">{candidate.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">{candidate.email}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            {candidate.rolesDisplay ? (
                              <span className="text-sm text-primary">{candidate.rolesDisplay}</span>
                            ) : (
                              <span className="text-muted-foreground text-sm">No application</span>
                            )}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <DocumentUploader
                              documentType="resume"
                              candidateId={candidate.id}
                              buttonText="Upload"
                              existingUrl={candidate.resumeUrl}
                              onUploadComplete={() => {
                                toast.success("Resume uploaded for " + candidate.fullName);
                                fetchCandidates();
                              }}
                            />
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            {candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : "-"}
                          </TableCell>
                          <TableCell>
                            {candidate.outcomeNote ? (
                              candidate.outcomeNote.startsWith("Proceeding") ? (
                                <Badge className="text-xs bg-green-500/15 text-green-700 dark:text-green-400 border border-green-400/50 whitespace-nowrap">
                                  Proceeding to in-person
                                </Badge>
                              ) : (
                                <Badge className="text-xs bg-red-500/15 text-red-700 dark:text-red-400 border border-red-400/50 max-w-[180px] truncate block" title={candidate.outcomeNote}>
                                  {candidate.outcomeNote}
                                </Badge>
                              )
                            ) : (
                              <Badge variant={candidate.isVerified ? "default" : "secondary"} className="text-xs">
                                {candidate.isVerified ? "Verified" : "Pending"}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1 md:gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewCandidateVideos(candidate.id, candidate.fullName)}
                                disabled={loadingCandidateVideos}
                                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                data-testid={`button-view-videos-${candidate.id}`}
                                title="View Interview Videos"
                              >
                                <Video className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditCandidateModal(candidate)}
                                data-testid={`button-edit-candidate-${candidate.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmDeleteCandidate({id: candidate.id, name: candidate.fullName || 'this candidate'})}
                                disabled={deletingCandidateId === candidate.id}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                data-testid={`button-delete-candidate-${candidate.id}`}
                              >
                                {deletingCandidateId === candidate.id ? (
                                  <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </Card>

            {showNewCandidateDialog && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                  <h3 className="text-lg md:text-xl font-bold mb-4">Add New Candidate</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Full Name *</label>
                      <Input
                        value={newCandidate.fullName}
                        onChange={(e) => setNewCandidate({...newCandidate, fullName: e.target.value})}
                        placeholder="Enter candidate's full name"
                        data-testid="input-new-candidate-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Email *</label>
                      <Input
                        type="email"
                        value={newCandidate.email}
                        onChange={(e) => setNewCandidate({...newCandidate, email: e.target.value})}
                        placeholder="Enter candidate's email"
                        data-testid="input-new-candidate-email"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Phone</label>
                      <Input
                        value={newCandidate.phone}
                        onChange={(e) => setNewCandidate({...newCandidate, phone: e.target.value})}
                        placeholder="Enter phone number"
                        data-testid="input-new-candidate-phone"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Resume/Notes</label>
                      <Textarea
                        value={newCandidate.resumeText}
                        onChange={(e) => setNewCandidate({...newCandidate, resumeText: e.target.value})}
                        placeholder="Paste resume text or add notes about this candidate..."
                        className="min-h-[100px]"
                        data-testid="input-new-candidate-resume"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button variant="outline" onClick={() => setShowNewCandidateDialog(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={createCandidate} disabled={creatingCandidate} className="flex-1 gap-2" data-testid="button-create-candidate">
                      {creatingCandidate ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                      Add Candidate
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {editingCandidate && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                  <h3 className="text-lg md:text-xl font-bold mb-4">Edit Candidate</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Full Name *</label>
                      <Input
                        value={editCandidateForm.fullName}
                        onChange={(e) => setEditCandidateForm({...editCandidateForm, fullName: e.target.value})}
                        placeholder="Enter candidate's full name"
                        data-testid="input-edit-candidate-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Email *</label>
                      <Input
                        type="email"
                        value={editCandidateForm.email}
                        onChange={(e) => setEditCandidateForm({...editCandidateForm, email: e.target.value})}
                        placeholder="Enter candidate's email"
                        data-testid="input-edit-candidate-email"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Phone</label>
                      <Input
                        value={editCandidateForm.phone}
                        onChange={(e) => setEditCandidateForm({...editCandidateForm, phone: e.target.value})}
                        placeholder="Enter phone number"
                        data-testid="input-edit-candidate-phone"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Resume/Notes</label>
                      <Textarea
                        value={editCandidateForm.resumeText}
                        onChange={(e) => setEditCandidateForm({...editCandidateForm, resumeText: e.target.value})}
                        placeholder="Paste resume text or add notes about this candidate..."
                        className="min-h-[100px]"
                        data-testid="input-edit-candidate-resume"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button variant="outline" onClick={() => setEditingCandidate(null)} className="flex-1">
                      Cancel
                    </Button>
                    <Button onClick={saveCandidate} disabled={savingCandidate} className="flex-1 gap-2" data-testid="button-save-candidate">
                      {savingCandidate ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {candidateVideoModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-4xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg md:text-xl font-bold flex items-center gap-2">
                      <Video className="w-5 h-5 text-indigo-600" />
                      Interview Videos - {candidateVideoModal.candidateName}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => setCandidateVideoModal(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {candidateVideoModal.interviews.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg">
                      <Video className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h4 className="text-lg font-medium mb-2">No Videos Available</h4>
                      <p className="text-muted-foreground">This candidate hasn't completed any video interviews yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {candidateVideoModal.interviews.map((interview) => (
                        <div key={interview.interviewId} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h4 className="font-medium text-lg">{interview.roleTitle}</h4>
                              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                <Badge variant={interview.status === "completed" ? "default" : "secondary"}>
                                  {interview.status}
                                </Badge>
                                {interview.completedAt && (
                                  <span>Completed: {new Date(interview.completedAt).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            <span className="text-sm text-muted-foreground">{interview.videos.length} response(s)</span>
                          </div>
                          
                          <div className="grid gap-4">
                            {interview.videos.map((video, idx) => (
                              <div key={video.questionId} className="bg-slate-50 rounded-lg p-4">
                                <p className="text-sm font-medium mb-3">
                                  <span className="text-indigo-600">Q{idx + 1}:</span> {video.questionText}
                                </p>
                                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                                  <video
                                    controls
                                    className="w-full h-full"
                                    src={video.downloadUrl}
                                    preload="metadata"
                                  >
                                    Your browser does not support video playback.
                                  </video>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">
                                  Recorded: {new Date(video.timestamp).toLocaleString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex justify-end mt-6">
                    <Button variant="outline" onClick={() => setCandidateVideoModal(null)}>
                      Close
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {activeTab === "dossiers" && (
          <div className="space-y-4">
            <DossiersTab
              candidates={candidates}
              onSendDossier={openSendDossierModal}
              reviewingInterview={reviewingInterview}
              setReviewingInterview={setReviewingInterview}
              reviewForm={reviewForm}
              setReviewForm={setReviewForm}
              reviewErrors={reviewErrors}
              setReviewErrors={setReviewErrors}
              dossierResult={dossierResult}
              setDossierResult={setDossierResult}
              dossierError={dossierError}
              setDossierError={setDossierError}
              openReview={openReview}
            />
          </div>
        )}


        {confirmDeleteCandidate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDeleteCandidate(null)}>
            <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-2 text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete Candidate
              </h2>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete <strong>{confirmDeleteCandidate.name}</strong>? This action cannot be undone and will remove all their applications and interview data.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setConfirmDeleteCandidate(null)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => deleteCandidate(confirmDeleteCandidate.id)} 
                  className="flex-1 gap-2"
                  data-testid="button-confirm-delete-candidate"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Candidate
                </Button>
              </div>
            </Card>
          </div>
        )}

        {confirmDeleteJob && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDeleteJob(null)}>
            <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-2 text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete Job Posting
              </h2>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete <strong>"{confirmDeleteJob.title}"</strong>? This action cannot be undone and will remove all associated applications.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setConfirmDeleteJob(null)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => deleteJob(confirmDeleteJob.id)} 
                  className="flex-1 gap-2"
                  data-testid="button-confirm-delete-job"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Job
                </Button>
              </div>
            </Card>
          </div>
        )}

        {confirmDeleteEnquiry && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setConfirmDeleteEnquiry(null)}>
            <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-bold mb-2 text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete Enquiry
              </h2>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this {confirmDeleteEnquiry.type} enquiry? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setConfirmDeleteEnquiry(null)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => deleteEnquiry(confirmDeleteEnquiry.id)} 
                  className="flex-1 gap-2"
                  data-testid="button-confirm-delete-enquiry"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Enquiry
                </Button>
              </div>
            </Card>
          </div>
        )}


        {/* === Send Dossier to Employer Modal === */}
        {sendDossierModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => sendDossierModal.step !== "sending" && setSendDossierModal(null)}>
            <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              {sendDossierModal.step === "done" ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <h2 className="text-lg font-bold">Dossier Sent</h2>
                  </div>
                  <p className="text-muted-foreground">
                    Dossier sent to <strong>{sendDossierModal.email}</strong> at{" "}
                    {sendDossierModal.sentAt ? new Date(sendDossierModal.sentAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "now"}.
                  </p>
                  <p className="text-sm text-muted-foreground">The employer has 14 days to review. The candidate card has been moved to <strong>Employer Reviewing</strong>.</p>
                  <Button className="w-full" onClick={() => setSendDossierModal(null)} data-testid="button-done-send-dossier">Done</Button>
                </div>
              ) : sendDossierModal.step === "email_prompt" ? (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold">Enter Employer Email</h2>
                  <p className="text-muted-foreground text-sm">No employer email is set for this role. Enter the address to send the dossier to:</p>
                  <input
                    type="email"
                    className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="employer@company.com"
                    value={emailOverride}
                    onChange={e => setEmailOverride(e.target.value)}
                    data-testid="input-employer-email-override"
                    autoFocus
                  />
                  <div className="flex gap-3">
                    <Button variant="outline" className="flex-1" onClick={() => setSendDossierModal(null)}>Cancel</Button>
                    <Button className="flex-1" disabled={!emailOverride.includes("@")} onClick={() => executeSendDossier(sendDossierModal.app, emailOverride)} data-testid="button-confirm-email-send">
                      Confirm & Send
                    </Button>
                  </div>
                </div>
              ) : sendDossierModal.step === "error" ? (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-red-600">Send Failed</h2>
                  <p className="text-muted-foreground text-sm">{sendDossierModal.errorMsg}</p>
                  <div className="flex gap-3">
                    <Button variant="outline" className="flex-1" onClick={() => setSendDossierModal(null)}>Cancel</Button>
                    <Button className="flex-1" onClick={() => executeSendDossier(sendDossierModal.app)}>Retry</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold">Send Dossier to Employer</h2>
                  <p className="text-muted-foreground text-sm">
                    This will email the dossier for <strong>{sendDossierModal.app.candidate_name}</strong> to the employer contact on file and move the candidate to <strong>Employer Reviewing</strong>.
                  </p>
                  <div className="flex gap-3">
                    <Button variant="outline" className="flex-1" onClick={() => setSendDossierModal(null)} disabled={sendDossierModal.step === "sending"}>Cancel</Button>
                    <Button className="flex-1 gap-2" onClick={() => executeSendDossier(sendDossierModal.app)} disabled={sendDossierModal.step === "sending"} data-testid="button-confirm-send-dossier">
                      {sendDossierModal.step === "sending" ? (
                        <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Sending…</>
                      ) : "Send →"}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* === Set Outcome Modal === */}
        {outcomeModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => outcomeModal.step !== "submitting" && setOutcomeModal(null)}>
            <Card className="w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              {outcomeModal.step === "done" ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${outcomeModal.outcome === "in_person" ? "bg-green-100 dark:bg-green-900/30" : "bg-slate-100 dark:bg-slate-800"}`}>
                      <CheckCircle className={`w-5 h-5 ${outcomeModal.outcome === "in_person" ? "text-green-600" : "text-slate-500"}`} />
                    </div>
                    <h2 className="text-lg font-bold">Outcome Set</h2>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {outcomeModal.outcome === "in_person"
                      ? "The candidate has been notified of the in-person interview invitation."
                      : "The candidate has been notified that we won't be moving forward at this stage."}
                  </p>
                  <p className="text-sm text-muted-foreground">Interview video is scheduled for deletion in 24 hours.</p>
                  <Button className="w-full" onClick={() => { setOutcomeModal(null); setActiveTab("candidates"); fetchCandidates(); }}>
                    Back to Candidates
                  </Button>
                </div>
              ) : (
                <div className="space-y-5">
                  <h2 className="text-lg font-bold">Set Outcome — {outcomeModal.app.candidate_name}</h2>
                  <p className="text-sm text-muted-foreground">Select an outcome. A notification will be sent to the candidate automatically.</p>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="outcome-topic-note">
                      Personalised observation for the candidate <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="outcome-topic-note"
                      className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px] resize-none"
                      placeholder="Add one specific observation from this candidate's interview, e.g. We were particularly impressed by your answer on handling competing priorities."
                      value={outcomeTopicNote}
                      onChange={e => setOutcomeTopicNote(e.target.value)}
                      disabled={outcomeModal.step === "submitting"}
                      data-testid="input-outcome-topic-note"
                    />
                    <p className="text-xs text-muted-foreground">This appears in the candidate's email for the "Not proceeding" outcome. Required before sending.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      className="p-4 rounded-lg border-2 border-green-400 bg-green-50 dark:bg-green-900/20 text-left hover:border-green-500 transition-colors disabled:opacity-50"
                      onClick={() => executeSetOutcome(outcomeModal.app, "in_person")}
                      disabled={outcomeModal.step === "submitting"}
                      data-testid="button-outcome-in-person"
                    >
                      <p className="font-semibold text-green-700 dark:text-green-400 text-sm">✓ Proceeding to in-person</p>
                      <p className="text-xs text-muted-foreground mt-1">Candidate receives an invitation email</p>
                    </button>
                    <button
                      className={`p-4 rounded-lg border-2 text-left transition-colors ${!outcomeTopicNote.trim() ? "border-red-800/40 bg-red-950/20 dark:bg-red-950/20 opacity-50 cursor-not-allowed" : "border-red-500 bg-red-950/30 dark:bg-red-950/30 hover:border-red-400 hover:bg-red-950/40 cursor-pointer"}`}
                      onClick={() => {
                        if (!outcomeTopicNote.trim()) return;
                        executeSetOutcome(outcomeModal.app, "not_proceeding");
                      }}
                      disabled={outcomeModal.step === "submitting" || !outcomeTopicNote.trim()}
                      data-testid="button-outcome-not-proceeding"
                      title={!outcomeTopicNote.trim() ? "Add a personalised observation above before selecting this outcome" : undefined}
                    >
                      <p className="font-semibold text-red-400 dark:text-red-400 text-sm">✗ Not proceeding</p>
                      <p className="text-xs text-red-300/70 dark:text-red-300/70 mt-1">{!outcomeTopicNote.trim() ? "Add observation above first" : "Candidate receives a personalised message"}</p>
                    </button>
                  </div>
                  {outcomeModal.step === "submitting" && (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      Sending notification…
                    </div>
                  )}
                  <Button variant="outline" className="w-full" onClick={() => setOutcomeModal(null)} disabled={outcomeModal.step === "submitting"}>Cancel</Button>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === "scoring" && (
          <div className="space-y-6">
            {/* Context Banner */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border text-sm">
              <Award className="w-5 h-5 text-primary" />
              <span className="text-muted-foreground">
                <strong className="text-foreground">AI Scoring</strong> evaluates candidates during interviews. Scores are shown in 
                <button onClick={() => setActiveTab("dossiers")} className="text-blue-400 hover:underline mx-1">Dossiers</button>
                after interviews complete.
              </span>
            </div>
            
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <Award className="w-6 h-6 text-primary" />
                Candidate Scoring
              </h2>
              <p className="text-muted-foreground mb-6">
                Understanding how VEXIS evaluates and scores candidates using advanced technology.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      Bias-Free Evaluation
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      The system removes identifying information during scoring to ensure fair, merit-based evaluation.
                    </p>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Names, ages, and photos are hidden during scoring
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Focus on skills, experience, and competencies
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Consistent rubric applied to all candidates
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      AI Resume Detection
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      We detect AI-generated or heavily embellished resumes to ensure authenticity.
                    </p>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Pattern analysis for AI writing signatures
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Cross-reference verbal vs. written claims
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Timeline consistency verification
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="p-4 border rounded-lg">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                      <Video className="w-5 h-5 text-purple-500" />
                      Video Interview Analysis
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      AI analyzes interview responses for depth, clarity, and technical accuracy.
                    </p>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Response quality and depth scoring
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Technical accuracy assessment
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Communication style evaluation
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                      <Award className="w-5 h-5 text-green-500" />
                      Credential Verification
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Verified credentials from trusted sources boost candidate scores.
                    </p>
                    <ul className="text-sm space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Credly badges integration
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        LinkedIn Learning certificates
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Blockchain credential verification
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-extrabold text-2xl mb-6 text-gray-900 tracking-tight">Scoring Breakdown</h3>
              <div className="grid md:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-4xl font-black text-blue-600">30%</p>
                  <p className="text-base font-semibold text-gray-800 mt-2">Technical Skills</p>
                  <p className="text-sm text-gray-600 mt-1">Role-specific competencies</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-4xl font-black text-purple-600">25%</p>
                  <p className="text-base font-semibold text-gray-800 mt-2">Interview Quality</p>
                  <p className="text-sm text-gray-600 mt-1">Response depth and clarity</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-4xl font-black text-green-600">20%</p>
                  <p className="text-base font-semibold text-gray-800 mt-2">Experience Match</p>
                  <p className="text-sm text-gray-600 mt-1">Relevance to role</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-4xl font-black text-amber-600">15%</p>
                  <p className="text-base font-semibold text-gray-800 mt-2">Verification Score</p>
                  <p className="text-sm text-gray-600 mt-1">Authenticity and consistency</p>
                </div>
                <div className="text-center p-4 bg-rose-50 rounded-lg border border-rose-100">
                  <p className="text-4xl font-black text-rose-600">10%</p>
                  <p className="text-base font-semibold text-gray-800 mt-2">Credentials Boost</p>
                  <p className="text-sm text-gray-600 mt-1">Verified certifications</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-extrabold text-2xl mb-6 text-gray-900 tracking-tight">Score Interpretation</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg border border-green-100">
                  <Badge className="bg-green-500 text-white px-4 py-1.5 text-base font-bold">85-100</Badge>
                  <div>
                    <p className="font-bold text-lg text-gray-900">Excellent Match</p>
                    <p className="text-sm text-gray-600">Strong recommendation for immediate consideration</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <Badge className="bg-blue-500 text-white px-4 py-1.5 text-base font-bold">70-84</Badge>
                  <div>
                    <p className="font-bold text-lg text-gray-900">Good Match</p>
                    <p className="text-sm text-gray-600">Recommended for shortlist with minor considerations</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg border border-amber-100">
                  <Badge className="bg-amber-500 text-white px-4 py-1.5 text-base font-bold">50-69</Badge>
                  <div>
                    <p className="font-bold text-lg text-gray-900">Potential Match</p>
                    <p className="text-sm text-gray-600">May require additional screening or skills assessment</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg border border-red-100">
                  <Badge className="bg-red-500 text-white px-4 py-1.5 text-base font-bold">0-49</Badge>
                  <div>
                    <p className="font-bold text-lg text-gray-900">Low Match</p>
                    <p className="text-sm text-gray-600">Not recommended for this particular role</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {activeTab === "matches" && (
          <div className="space-y-4">
            {/* Context Banner */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border text-sm">
              <Shield className="w-5 h-5 text-secondary" />
              <span className="text-muted-foreground">
                <strong className="text-foreground">AI Matches</strong> suggest candidates for jobs based on skills and experience. Add promising matches to the 
                <button onClick={() => setActiveTab("pipeline")} className="text-primary hover:underline mx-1">Pipeline</button>
                for interviews.
              </span>
            </div>
            <MatchesTab jobs={jobs} />
          </div>
        )}

        {activeTab === "invites" && (
          <div className="space-y-4 md:space-y-6">
            {/* Workflow Breadcrumb */}
            <div className="flex flex-wrap items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground">
              <button onClick={() => setActiveTab("overview")} className="hover:text-foreground transition-colors">Overview</button>
              <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
              <button onClick={() => setActiveTab("pipeline")} className="hover:text-foreground transition-colors">Pipeline</button>
              <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
              <span className="text-accent font-medium">Invites</span>
              <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
              <button onClick={() => setActiveTab("dossiers")} className="hover:text-foreground transition-colors">Dossiers</button>
            </div>
            
            <Card className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4 flex items-center gap-2">
                <Link2 className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                Generate Interview Link
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground mb-4 md:mb-6">
                Create a unique, single-use interview link for a specific role.
                {jobs.filter(j => j.source === "postgresql").length < 10 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="ml-2 text-xs p-0 h-auto"
                    onClick={async () => {
                      try {
                        const res = await apiRequest("POST", "/api/agent/seed-health-roles");
                        const data = await res.json();
                        if (res.ok) {
                          toast.success(`Added ${data.added?.length || 0} health roles`);
                          fetchJobs();
                        } else {
                          toast.error(data.error || "Failed to add health roles");
                        }
                      } catch (error) {
                        toast.error("Failed to add health roles");
                      }
                    }}
                    data-testid="button-seed-health-roles"
                  >
                    + Add Health Role Templates
                  </Button>
                )}
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">Select Role</label>
                  <Select value={selectedRoleForInvite} onValueChange={setSelectedRoleForInvite}>
                    <SelectTrigger data-testid="select-role-for-invite">
                      <SelectValue placeholder="Choose a role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {jobs.filter(j => {
                        const status = (j.status || "").toLowerCase();
                        // Include any job that's not closed, expired, or filled
                        const excludedStatuses = ["closed", "expired", "filled", "cancelled"];
                        return !excludedStatuses.includes(status);
                      }).map((job) => (
                        <SelectItem key={job.id} value={job.id.toString()} data-testid={`option-role-${job.id}`}>
                          {job.title} {job.status === "draft" && "(Draft)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Number of Questions</label>
                  <Select value={inviteQuestionCount.toString()} onValueChange={(v) => setInviteQuestionCount(parseInt(v))}>
                    <SelectTrigger data-testid="select-question-count">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 questions</SelectItem>
                      <SelectItem value="4">4 questions</SelectItem>
                      <SelectItem value="5">5 questions</SelectItem>
                      <SelectItem value="6">6 questions</SelectItem>
                      <SelectItem value="7">7 questions</SelectItem>
                      <SelectItem value="8">8 questions</SelectItem>
                      <SelectItem value="10">10 questions</SelectItem>
                      <SelectItem value="12">12 questions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Link Expiry</label>
                  <Select value={inviteExpiryHours.toString()} onValueChange={(v) => setInviteExpiryHours(parseInt(v))}>
                    <SelectTrigger data-testid="select-invite-expiry">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">24 hours</SelectItem>
                      <SelectItem value="48">48 hours</SelectItem>
                      <SelectItem value="72">72 hours (3 days)</SelectItem>
                      <SelectItem value="168">1 week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-end">
                  <Button 
                    onClick={async () => {
                      if (!selectedRoleForInvite) {
                        toast.error("Please select a role first");
                        return;
                      }
                      setGeneratingInvite(true);
                      try {
                        // FIXED: POST /api/agent/role-invites now accessible to RECRUITMENT_AGENT
                        const res = await apiRequest("POST", "/api/agent/role-invites", {
                          roleId: selectedRoleForInvite,
                          expiresInHours: inviteExpiryHours,
                          questionCount: inviteQuestionCount,
                        });
                        const data = await res.json();
                        setGeneratedInviteLink(data.inviteUrl);
                        toast.success("Interview link generated!");
                        fetchInvites();
                      } catch (err) {
                        toast.error("Failed to generate interview link");
                      } finally {
                        setGeneratingInvite(false);
                      }
                    }}
                    disabled={generatingInvite || !selectedRoleForInvite}
                    className="gap-2 w-full"
                    data-testid="button-generate-invite"
                  >
                    {generatingInvite ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4" />
                    )}
                    Generate Link
                  </Button>
                </div>
              </div>

              {generatedInviteLink && (
                <div className="p-4 md:p-6 bg-green-900/20 border-2 border-green-500 rounded-lg">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                      <p className="text-lg font-bold text-green-400">Interview Link Ready!</p>
                    </div>
                    <div className="bg-background/80 p-3 rounded-lg border border-border">
                      <p className="text-xs text-muted-foreground mb-2">Share this link with the candidate:</p>
                      <code className="text-sm text-foreground font-mono break-all block p-2 bg-muted rounded">
                        {generatedInviteLink}
                      </code>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button 
                        onClick={() => {
                          navigator.clipboard.writeText(generatedInviteLink);
                          setLinkCopied(true);
                          toast.success("Link copied to clipboard!");
                          setTimeout(() => setLinkCopied(false), 2500);
                        }}
                        className={`gap-2 flex-1 transition-all duration-300 ${linkCopied ? "bg-green-600 hover:bg-green-700" : ""}`}
                        data-testid="button-copy-invite-link"
                      >
                        {linkCopied ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy Link
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setGeneratedInviteLink("")}
                        className="gap-2"
                        data-testid="button-dismiss-invite-link"
                      >
                        <X className="w-4 h-4" />
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base md:text-lg font-bold flex items-center gap-2">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                  <span className="hidden sm:inline">Interview</span> Invites
                </h3>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchInvites}
                  disabled={loadingInvites}
                  data-testid="button-refresh-invites"
                  className="h-8 text-xs md:text-sm"
                >
                  {loadingInvites ? "..." : "Refresh"}
                </Button>
              </div>

              {loadingInvites ? (
                <div className="flex items-center justify-center py-6 md:py-8">
                  <div className="w-6 h-6 md:w-8 md:h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : invites.length === 0 ? (
                <div className="text-center py-6 md:py-8 text-muted-foreground">
                  <Link2 className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 opacity-30" />
                  <p className="text-sm">No interview invites yet</p>
                  <p className="text-xs">Generate your first invite link above</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Role</TableHead>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invites.map((invite: any) => (
                      <TableRow key={invite.id} data-testid={`row-invite-${invite.id}`}>
                        <TableCell className="font-medium">{invite.roleTitle}</TableCell>
                        <TableCell>
                          {invite.isOpenInvite && invite.status === "issued" ? (
                            <Badge variant="outline" className="text-blue-600 border-blue-200">Open Invite</Badge>
                          ) : (
                            <div>
                              <p className="font-medium">{invite.candidateName || "Unknown"}</p>
                              {invite.candidateEmail && (
                                <p className="text-xs text-muted-foreground">{invite.candidateEmail}</p>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={
                              invite.status === "issued" ? "bg-blue-500" :
                              invite.status === "claimed" ? "bg-yellow-500" :
                              invite.status === "used" ? "bg-green-500" :
                              invite.status === "expired" ? "bg-gray-500" :
                              invite.status === "revoked" ? "bg-red-500" : "bg-gray-400"
                            }
                          >
                            {invite.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(invite.expiresAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(invite.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {invite.status === "issued" && (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={async () => {
                                try {
                                  const res = await apiRequest("POST", `/api/agent/invites/${invite.id}/revoke`);
                                  if (res.ok) {
                                    toast.success("Invite revoked");
                                    fetchInvites();
                                  } else {
                                    const errorData = await res.json().catch(() => ({}));
                                    toast.error(errorData.error || "Failed to revoke invite");
                                  }
                                } catch (err) {
                                  toast.error("Failed to revoke invite");
                                }
                              }}
                              data-testid={`button-revoke-invite-${invite.id}`}
                            >
                              <X className="w-3 h-3 mr-1" />
                              Revoke
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              )}
            </Card>

            {/* Bulk Invites Section */}
            <Card className="p-4 md:p-6">
              <h3 className="text-base md:text-lg font-bold mb-3 md:mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-primary" />
                Bulk Interview Invites
                <Badge variant="outline" className="ml-2 text-xs">Beta</Badge>
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground mb-4">
                Send interview invites to multiple candidates at once. Upload a CSV file or enter candidates manually.
              </p>

              <BulkInvitesSection 
                jobs={jobs}
                onBatchCreated={fetchInvites}
              />
            </Card>
          </div>
        )}

        {activeTab === "registrations" && (
          <div className="space-y-6">
            {/* Guidance Banner */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-700 dark:text-blue-100 font-medium">These candidates and employers have not yet completed an interview.</p>
                  <p className="text-blue-600/70 dark:text-blue-200/70 text-sm mt-1">Review enquiries and send interview invites to qualified candidates.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">New Enquiries</h1>
                <p className="text-muted-foreground">Review new candidate and employer enquiries</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { fetchEnquiries(); fetchCandidates(); }}
                disabled={loadingEnquiries}
                data-testid="btn-refresh-registrations"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingEnquiries ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Candidate Enquiries</h3>
                  <Badge variant="secondary">{enquiries.filter(e => e.enquiryType === "candidate" && e.status === "new").length}</Badge>
                </div>
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Resume</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enquiries.filter(e => e.enquiryType === "candidate").slice(0, 10).map((enquiry) => (
                      <TableRow key={enquiry.id}>
                        <TableCell className="font-medium">{enquiry.name}</TableCell>
                        <TableCell className="text-muted-foreground">{enquiry.email}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-muted-foreground">{enquiry.message}</TableCell>
                        <TableCell>
                          {enquiry.fileName ? (
                            <Badge variant="outline" className="text-xs">
                              <FileText className="w-3 h-3 mr-1" />
                              {enquiry.fileName}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">No file</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={enquiry.status === "new" ? "default" : "secondary"}>
                            {enquiry.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {enquiries.filter(e => e.enquiryType === "candidate").length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No candidate enquiries yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Employer Enquiries</h3>
                  <Badge variant="secondary">{enquiries.filter(e => e.enquiryType === "employer" && e.status === "new").length}</Badge>
                </div>
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enquiries.filter(e => e.enquiryType === "employer").slice(0, 10).map((enquiry) => (
                      <TableRow key={enquiry.id}>
                        <TableCell className="font-medium">{enquiry.company || enquiry.name}</TableCell>
                        <TableCell className="text-muted-foreground">{enquiry.email}</TableCell>
                        <TableCell>
                          <Badge variant={enquiry.status === "new" ? "default" : "secondary"}>
                            {enquiry.status}
                          </Badge>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    ))}
                    {enquiries.filter(e => e.enquiryType === "employer").length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No employer enquiries yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                </div>
              </Card>
            </div>
          </div>
        )}


        {activeTab === "approval" && (
          <div className="space-y-6">
            {/* Guidance Banner */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-700 dark:text-blue-100 font-medium">Only approved candidates can appear in Matching.</p>
                  <p className="text-blue-600/70 dark:text-blue-200/70 text-sm mt-1">Review verification status and make the final decision on each candidate.</p>
                </div>
              </div>
            </div>

            <div>
              <h1 className="text-2xl font-bold">Dossier Approval</h1>
              <p className="text-muted-foreground">Final review and approval of candidates before presenting to employers</p>
            </div>
            
            <Card className="p-4">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Identity</TableHead>
                    <TableHead>Right to Work</TableHead>
                    <TableHead>Authenticity</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.filter(c => c.applications?.some((a: any) => a.pipeline_stage === "link_sent")).map((candidate) => {
                    const app = candidate.applications?.find((a: any) => a.pipeline_stage === "link_sent");
                    return (
                      <TableRow key={candidate.id}>
                        <TableCell className="font-medium">{candidate.fullName}</TableCell>
                        <TableCell>{app?.role_title || "N/A"}</TableCell>
                        <TableCell>
                          <Badge variant={app?.overall_score >= 70 ? "default" : "secondary"}>
                            {app?.overall_score || 0}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {app?.identity_verified ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          {app?.right_to_work_verified ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          {app?.authenticity_verified ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => setActiveTab("pipeline")}>
                              <Eye className="w-3 h-3 mr-1" />
                              Review
                            </Button>
                            <Button size="sm" variant="default">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Approve
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {candidates.filter(c => c.applications?.some((a: any) => a.pipeline_stage === "link_sent")).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                        <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">No candidates awaiting approval</p>
                        <p className="text-sm">Candidates in "Skill Verified" stage will appear here</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </Card>
          </div>
        )}

        {activeTab === "roles" && (
          <div className="space-y-6">
            {/* Guidance Banner */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-700 dark:text-blue-100 font-medium">Only active, employer-approved roles appear in Matching.</p>
                  <p className="text-blue-600/70 dark:text-blue-200/70 text-sm mt-1">Manage job postings, generate interview invites, and track applicants.</p>
                </div>
              </div>
            </div>

            {planEnforcement.isNearRoleLimit && !planEnforcement.isAtRoleLimit && (
              <div
                data-testid="role-limit-warning-banner"
                style={{
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: "1px solid rgba(245,158,11,0.35)",
                  background: "rgba(245,158,11,0.07)",
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <span>
                  You're using <strong>{planEnforcement.usage.activeRoles}</strong> of{" "}
                  <strong>{planEnforcement.plan.maxActiveRoles}</strong> role slots.
                </span>
                <button
                  onClick={() => navigateToUpgrade(setLocation, "approaching_role_limit", planEnforcement.plan.tierName)}
                  style={{ color: "#3b82f6", background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13 }}
                >
                  Upgrade →
                </button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">Active Roles</h1>
                <p className="text-muted-foreground">Manage job postings and role requirements</p>
              </div>
              <Button
                data-testid="button-new-role"
                onClick={() => {
                  if (planEnforcement.isAtRoleLimit) {
                    setShowRoleLimitModal(true);
                  } else {
                    setLocation("/agent/create-job");
                  }
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Role
              </Button>
            </div>

            {/* Role limit modal */}
            {showRoleLimitModal && (
              <div
                style={{
                  position: "fixed", inset: 0, zIndex: 9999,
                  background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center",
                }}
                onClick={() => setShowRoleLimitModal(false)}
              >
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    background: "#0f1629", borderRadius: 12, padding: 28, maxWidth: 420, width: "90%",
                    border: "1px solid rgba(239,68,68,0.3)",
                  }}
                >
                  <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 12, color: "#f1f5f9" }}>
                    Role limit reached
                  </h3>
                  <p style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.6, marginBottom: 20 }}>
                    You're using all{" "}
                    <strong style={{ color: "#f1f5f9" }}>{planEnforcement.plan.maxActiveRoles}</strong> active role
                    slots on your <strong style={{ color: "#f1f5f9" }}>{planEnforcement.plan.displayName}</strong> plan.
                    <br /><br />
                    Close an existing role to create a new one, or upgrade your plan to manage more roles simultaneously.
                  </p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button
                      variant="outline"
                      data-testid="role-limit-view-roles-btn"
                      onClick={() => setShowRoleLimitModal(false)}
                      style={{ flex: 1, borderColor: "rgba(99,102,241,0.25)", color: "#94a3b8", fontSize: 13 }}
                    >
                      View My Roles
                    </Button>
                    <Button
                      data-testid="role-limit-upgrade-btn"
                      onClick={() => {
                        setShowRoleLimitModal(false);
                        navigateToUpgrade(setLocation, "role_limit", planEnforcement.plan.tierName);
                      }}
                      style={{ flex: 1, background: "#3b82f6", color: "#fff", border: "none", fontSize: 13 }}
                    >
                      Upgrade Plan →
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Candidate limit modal */}
            {showCandidateLimitModal && (
              <div
                style={{
                  position: "fixed", inset: 0, zIndex: 9999,
                  background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center",
                }}
                onClick={() => setShowCandidateLimitModal(false)}
              >
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    background: "#0f1629", borderRadius: 12, padding: 28, maxWidth: 420, width: "90%",
                    border: "1px solid rgba(239,68,68,0.3)",
                  }}
                >
                  <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 12, color: "#f1f5f9" }}>
                    Monthly assessment limit reached
                  </h3>
                  <p style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.6, marginBottom: 20 }}>
                    You've sent <strong style={{ color: "#f1f5f9" }}>{planEnforcement.usage.candidatesThisMonth}</strong> candidate assessment
                    {planEnforcement.usage.candidatesThisMonth !== 1 ? "s" : ""} this month — the limit on your{" "}
                    <strong style={{ color: "#f1f5f9" }}>{planEnforcement.plan.displayName}</strong> plan.
                    <br /><br />
                    Upgrade your plan to send more invites, or wait until the start of next month.
                  </p>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button
                      variant="outline"
                      data-testid="candidate-limit-close-btn"
                      onClick={() => setShowCandidateLimitModal(false)}
                      style={{ flex: 1, borderColor: "rgba(99,102,241,0.25)", color: "#94a3b8", fontSize: 13 }}
                    >
                      Close
                    </Button>
                    <Button
                      data-testid="candidate-limit-upgrade-btn"
                      onClick={() => {
                        setShowCandidateLimitModal(false);
                        navigateToUpgrade(setLocation, "candidate_limit", planEnforcement.plan.tierName);
                      }}
                      style={{ flex: 1, background: "#3b82f6", color: "#fff", border: "none", fontSize: 13 }}
                    >
                      Upgrade Plan →
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            <Card className="p-4">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role Title</TableHead>
                    <TableHead>Employer</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Applicants</TableHead>
                    <TableHead>Questions</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="font-medium">{job.title}</TableCell>
                      <TableCell>{(job as any).employer_name || "VEXIS"}</TableCell>
                      <TableCell>{job.location || "Remote"}</TableCell>
                      <TableCell>
                        <Badge variant={job.status === "active" ? "default" : "secondary"}>
                          {job.status || "Active"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{(job as any).applicant_count || 0}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{(job as any).question_count || 0}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            title="Edit role"
                            data-testid={`button-edit-role-${job.id}`}
                            onClick={() => {
                              setActiveTab("jobs");
                              openEditJobModal(job);
                            }}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            title="Generate & copy invite link"
                            data-testid={`button-role-invite-${job.id}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await generateRoleInviteLink(job.id);
                            }}
                          >
                            <Link2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {jobs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                        <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">No roles created yet</p>
                        <p className="text-sm mb-4">Create your first role to start receiving applications</p>
                        <Link href="/agent/create-job">
                          <Button>
                            <Plus className="w-4 h-4 mr-2" />
                            Create Role
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </Card>
          </div>
        )}

        {activeTab === "matching" && (
          <div className="space-y-6">
            {/* Guidance Banner */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-700 dark:text-blue-100 font-medium">Match approved candidates to approved roles only.</p>
                  <p className="text-blue-600/70 dark:text-blue-200/70 text-sm mt-1">Select a role to see best-fit candidates based on skills, experience, and interview performance.</p>
                </div>
              </div>
            </div>

            <div>
              <h1 className="text-2xl font-bold">Smart Matching</h1>
              <p className="text-muted-foreground">Candidate-to-role matching suggestions</p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Active Roles
                </h3>
                <div className="space-y-2">
                  {jobs.filter(j => j.status === "active").slice(0, 8).map((job) => (
                    <div key={job.id} className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{job.title}</p>
                          <p className="text-sm text-muted-foreground">{job.location || "Remote"}</p>
                        </div>
                        <Badge variant="outline">{(job as any).applicant_count || 0} applicants</Badge>
                      </div>
                    </div>
                  ))}
                  {jobs.filter(j => j.status === "active").length === 0 && (
                    <div className="text-center py-10">
                      <p className="font-semibold text-foreground mb-1">No active roles yet</p>
                      <p className="text-sm text-muted-foreground mb-4">Create your first Role Brief to start sending candidates for assessment. It takes about 5 minutes to set up.</p>
                      <Button size="sm" onClick={() => setActiveTab("jobs")} data-testid="button-create-role-brief-empty">Create Role Brief →</Button>
                    </div>
                  )}
                </div>
              </Card>

              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Approved Candidates
                </h3>
                <div className="text-center py-12 text-muted-foreground">
                  <GitMerge className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Select a role to see matches</p>
                  <p className="text-sm">Best-fit candidates will appear based on skills and experience</p>
                </div>
              </Card>
            </div>
          </div>
        )}

        {activeTab === "shortlists" && (
          <div className="space-y-6">
            {/* Guidance Banner */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-700 dark:text-blue-100 font-medium">Track what's been sent to employers and await their decisions.</p>
                  <p className="text-blue-600/70 dark:text-blue-200/70 text-sm mt-1">Monitor shortlist status: Sent, Awaiting Decision, Shortlisted, or Rejected.</p>
                </div>
              </div>
            </div>

            <div>
              <h1 className="text-2xl font-bold">Shortlists</h1>
              <p className="text-muted-foreground">Curated candidate shortlists ready for employer presentation</p>
            </div>
            
            <Card className="p-4">
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead>Rank</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.filter(c => c.applications?.some((a: any) => a.pipeline_stage === "dossier_ready")).map((candidate, index) => {
                    const app = candidate.applications?.find((a: any) => a.pipeline_stage === "dossier_ready");
                    if (!app) return null;
                    return (
                      <TableRow key={candidate.id} data-testid={`row-shortlist-${candidate.id}`}>
                        <TableCell className="font-medium">{candidate.fullName || candidate.full_name || "Unknown"}</TableCell>
                        <TableCell>{app?.role_title || (app as any)?.jobTitle || "N/A"}</TableCell>
                        <TableCell>
                          <Badge variant="default">{app?.overall_score || 0}%</Badge>
                        </TableCell>
                        <TableCell>
                          {app?.human_verified ? (
                            <Badge className="bg-green-500/10 text-green-600">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">#{index + 1}</Badge>
                        </TableCell>
                        <TableCell>{app?.updated_at ? new Date(app.updated_at).toLocaleDateString() : "N/A"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => setActiveTab("dossiers")} data-testid={`button-view-dossier-shortlist-${candidate.id}`}>
                              <FileText className="w-3 h-3 mr-1" />
                              Dossier
                            </Button>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => openSendDossierModal(app)} data-testid={`button-send-shortlist-${candidate.id}`}>
                              <Send className="w-3 h-3 mr-1" />
                              Send
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {candidates.filter(c => c.applications?.some((a: any) => a.pipeline_stage === "dossier_ready")).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                        <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">No shortlisted candidates</p>
                        <p className="text-sm">Approved candidates will appear here for presentation to employers</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </Card>
          </div>
        )}

        {activeTab === "hired" && (
          <div className="space-y-6">
            {/* Guidance Banner */}
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-green-700 dark:text-green-100 font-medium">Final outcomes and successful placements.</p>
                  <p className="text-green-600/70 dark:text-green-200/70 text-sm mt-1">Track time-to-decision and review placement notes for performance insights.</p>
                </div>
              </div>
            </div>

            <div>
              <h1 className="text-2xl font-bold">Hired / Closed</h1>
              <p className="text-muted-foreground">Completed placements and closed positions</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{candidates.filter(c => c.applications?.some((a: any) => a.pipeline_stage === "outcome")).length}</p>
                    <p className="text-sm text-muted-foreground">Total Placements</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{jobs.filter(j => j.status === "filled").length}</p>
                    <p className="text-sm text-muted-foreground">Roles Filled</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{jobs.filter(j => j.status === "closed").length}</p>
                    <p className="text-sm text-muted-foreground">Roles Closed</p>
                  </div>
                </div>
              </Card>
            </div>
            
            <Card className="p-4">
              <h3 className="font-semibold mb-4">Recent Placements</h3>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Employer</TableHead>
                    <TableHead>Hired Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {candidates.filter(c => c.applications?.some((a: any) => a.pipeline_stage === "outcome")).map((candidate) => {
                    const app = candidate.applications?.find((a: any) => a.pipeline_stage === "outcome");
                    return (
                      <TableRow key={candidate.id}>
                        <TableCell className="font-medium">{candidate.fullName}</TableCell>
                        <TableCell>{app?.role_title || "N/A"}</TableCell>
                        <TableCell>{app?.employer_name || "N/A"}</TableCell>
                        <TableCell>{app?.updated_at ? new Date(app.updated_at).toLocaleDateString() : "N/A"}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-500/10 text-green-600">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Hired
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {candidates.filter(c => c.applications?.some((a: any) => a.pipeline_stage === "outcome")).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p className="font-medium">No placements yet</p>
                        <p className="text-sm">Successful placements will be recorded here</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </Card>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="space-y-6 max-w-2xl">
            <div>
              <h1 className="text-2xl font-bold">Settings</h1>
              <p className="text-muted-foreground">Manage your account and platform preferences</p>
            </div>

            <PlanUsageFull />

            {/* ─── Team Members card ──────────────────────────────────────── */}
            <Card className="p-6" data-testid="card-team-members">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Team Members
                </h3>
                <span className="text-xs text-muted-foreground" data-testid="text-seat-count">
                  {teamLoaded
                    ? teamMeta.maxSeats === null
                      ? `${teamMeta.currentSeats} members (unlimited seats)`
                      : `${teamMeta.currentSeats} / ${teamMeta.maxSeats} seats used`
                    : "Loading…"}
                </span>
              </div>

              {/* Seat limit exceeded warning */}
              {teamMeta.seatLimitExceeded && (
                <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2" data-testid="banner-seat-limit-exceeded">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-300">Seat limit exceeded</p>
                    <p className="text-amber-400/80 mt-0.5">
                      Your workspace has more members than your {teamMeta.tierDisplayName ?? "current"} plan allows.
                      Please remove team members until you are within your plan limit.
                      New invites are paused until resolved.
                    </p>
                    <button
                      className="mt-2 text-xs font-medium text-amber-300 underline underline-offset-2"
                      onClick={() => window.location.href = "/pricing"}
                    >
                      Upgrade your plan →
                    </button>
                  </div>
                </div>
              )}

              {/* Members list */}
              <div className="space-y-2 mb-5">
                {!teamLoaded && (
                  <p className="text-sm text-muted-foreground">Loading team members…</p>
                )}
                {teamLoaded && teamMembers.length === 0 && (
                  <p className="text-sm text-muted-foreground">No team members found.</p>
                )}
                {teamLoaded && teamMembers.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-2 rounded-md bg-muted/30" data-testid={`row-team-member-${m.id}`}>
                    <div>
                      <p className="text-sm font-medium">{m.email}</p>
                      <p className="text-xs text-muted-foreground capitalize">{m.role === "RECRUITMENT_AGENT" ? "Recruiter" : "Admin"}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Invite form */}
              <div className="border-t border-border/40 pt-4">
                <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Invite a team member</p>
                {teamInviteSuccess && (
                  <div className="mb-3 rounded-md bg-green-500/10 border border-green-500/30 p-2 text-sm text-green-400" data-testid="text-invite-success">
                    {teamInviteSuccess}
                  </div>
                )}
                {teamInviteError && (
                  <div className="mb-3 rounded-md bg-red-500/10 border border-red-500/30 p-2 text-sm text-red-400 flex items-start gap-2" data-testid="text-invite-error">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{teamInviteError}</span>
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <input
                    type="text"
                    placeholder="Full name"
                    value={teamInviteName}
                    onChange={e => { setTeamInviteName(e.target.value); setTeamInviteError(null); setTeamInviteSuccess(null); }}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="input-invite-name"
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={teamInviteEmail}
                    onChange={e => { setTeamInviteEmail(e.target.value); setTeamInviteError(null); setTeamInviteSuccess(null); }}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="input-invite-email"
                  />
                  <button
                    className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                    disabled={teamInviting || !teamInviteEmail.trim() || !teamInviteName.trim() || teamMeta.seatLimitExceeded}
                    data-testid="button-send-invite"
                    onClick={async () => {
                      setTeamInviting(true);
                      setTeamInviteError(null);
                      setTeamInviteSuccess(null);
                      try {
                        const res = await fetch("/api/agent/team/invite", {
                          method: "POST",
                          credentials: "include",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ email: teamInviteEmail.trim(), name: teamInviteName.trim() }),
                        });
                        const data = await res.json();
                        if (!res.ok) {
                          if (data.error === "seat_limit_exceeded") {
                            setTeamInviteError("Your workspace has more members than your current plan allows. Please remove team members before inviting new ones.");
                          } else if (data.error === "seat_limit_reached") {
                            setTeamInviteError(`You've reached the seat limit for your ${teamMeta.tierDisplayName ?? "current"} plan (${data.maxSeats} seat${data.maxSeats === 1 ? "" : "s"}). Upgrade to add more team members.`);
                          } else {
                            setTeamInviteError(data.message ?? data.error ?? "Invite failed — please try again.");
                          }
                        } else {
                          setTeamInviteSuccess(`Invite sent to ${teamInviteEmail.trim()}.`);
                          setTeamInviteEmail("");
                          setTeamInviteName("");
                          // Refresh team list
                          setTeamLoaded(false);
                          fetchTeamMembers();
                        }
                      } catch {
                        setTeamInviteError("Network error — please try again.");
                      } finally {
                        setTeamInviting(false);
                      }
                    }}
                  >
                    {teamInviting ? "Sending…" : "Send invite"}
                  </button>
                </div>
                {teamMeta.maxSeats !== null && teamMeta.currentSeats >= teamMeta.maxSeats && !teamMeta.seatLimitExceeded && (
                  <p className="mt-2 text-xs text-amber-400" data-testid="text-seats-at-limit">
                    You've used all {teamMeta.maxSeats} seat{teamMeta.maxSeats === 1 ? "" : "s"} on your {teamMeta.tierDisplayName ?? "current"} plan.{" "}
                    <button className="underline underline-offset-2" onClick={() => window.location.href = "/pricing"}>Upgrade to add more</button>
                  </p>
                )}
              </div>
            </Card>

            {/* Feature gating status card */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Workspace Feature Access
              </h3>
              <div className="space-y-3">
                {[
                  {
                    key: "canRemoveBranding",
                    label: "Remove Vexis branding",
                    desc: "Hide 'Powered by Vexis' on candidate-facing pages",
                    enabled: planEnforcement.plan.canRemoveBranding,
                    unlocksAt: "Growth",
                  },
                  {
                    key: "canEditTemplates",
                    label: "Custom email templates",
                    desc: "Edit template body text for candidate communications",
                    enabled: planEnforcement.plan.canEditTemplates,
                    unlocksAt: "Starter",
                  },
                  {
                    key: "whiteLabel",
                    label: "White-label platform",
                    desc: "Replace Vexis branding with your agency brand throughout",
                    enabled: planEnforcement.plan.whiteLabel,
                    unlocksAt: "Agency",
                  },
                  {
                    key: "clientPortal",
                    label: "Client portal",
                    desc: "Shareable dossier portal for employer review",
                    enabled: planEnforcement.plan.clientPortal,
                    unlocksAt: "Agency",
                  },
                ].map(({ key, label, desc, enabled, unlocksAt }) => (
                  <div key={key} className="flex items-center justify-between gap-4" data-testid={`feature-gate-${key}`}>
                    <div>
                      <p className="font-medium text-sm" style={{ color: enabled ? "#f1f5f9" : "#64748b" }}>{label}</p>
                      <p className="text-xs" style={{ color: "#475569" }}>{desc}</p>
                    </div>
                    {enabled ? (
                      <Badge variant="secondary" style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.25)", fontSize: 11, whiteSpace: "nowrap" }}>
                        Enabled
                      </Badge>
                    ) : (
                      <button
                        data-testid={`upgrade-for-${key}`}
                        onClick={() => navigateToUpgrade(setLocation, `feature_gate_${key}`, planEnforcement.plan.tierName)}
                        style={{ background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 6, padding: "3px 10px", color: "#818cf8", fontSize: 11, cursor: "pointer", whiteSpace: "nowrap", fontWeight: 600 }}
                      >
                        🔒 {unlocksAt}+
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Account Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Agent Name</label>
                    <p className="font-medium">{user?.fullName || user?.email || "Agent"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email</label>
                    <p className="font-medium">{user?.email || "N/A"}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Role</label>
                  <p className="font-medium">
                    {user?.role === "ADMIN" ? "Administrator" : user?.role === "RECRUITMENT_AGENT" ? "Recruitment Agent" : "Agent"}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notification Preferences
              </h3>
              <div className="space-y-4">
                {[
                  { key: "candidate_submitted", label: "Assessment submitted", desc: "Notify me when a candidate submits their full assessment" },
                  { key: "ai_scoring_complete", label: "AI scoring complete", desc: "Notify me when AI triage has finished scoring a submission" },
                  { key: "reminder_48h", label: "48-hour reminder sent", desc: "Notify me when a 48-hour reminder email is dispatched" },
                  { key: "role_expiring", label: "Role approaching expiry", desc: "Notify me when a role is 3 days from its expiry date" },
                  { key: "billing_renewal", label: "Billing renewal reminder", desc: "Notify me 7 days before my subscription renews" },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{label}</p>
                      <p className="text-sm text-muted-foreground">{desc}</p>
                    </div>
                    <Switch
                      data-testid={`notif-toggle-${key}`}
                      checked={!!notifPrefs[key]}
                      disabled={savingNotifPref === key}
                      onCheckedChange={v => saveNotifPref(key, v)}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border">
                Notifications are sent to your registered email address. To update your notification email, contact your system administrator.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Communications
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email and SMS</p>
                    <p className="text-sm text-muted-foreground">Configure outbound email and SMS credentials for your workspace</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Set up your workspace email credentials to send assessment invitations, submission confirmations, and outcome notifications directly to candidates.
                </p>
                <Button variant="outline" size="sm" className="border-border" onClick={() => setLocation("/settings/communications")} data-testid="settings-go-to-communications">
                  <Mail className="w-4 h-4 mr-2" />
                  Manage Communications
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                ATS Connection
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Connection status</p>
                    <p className="text-sm text-muted-foreground">Sync candidates and placements with your ATS</p>
                  </div>
                  <Badge variant="secondary">Managed</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Connect your platform to automatically push candidate updates, sync placement outcomes, and keep your system in sync with VEXIS pipeline activity.
                </p>
                <Button variant="outline" size="sm" className="border-border" onClick={() => setLocation("/settings/integrations")} data-testid="settings-connect-ats">
                  <Share2 className="w-4 h-4 mr-2" />
                  Manage Integrations
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Platform Settings
              </h3>
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Theme</p>
                    <p className="text-sm text-muted-foreground">Switch between light and dark mode</p>
                  </div>
                  <ThemeToggle />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">Default Interview Duration</p>
                    <p className="text-sm text-muted-foreground">Time limit per question response (seconds)</p>
                  </div>
                  <Input
                    type="number"
                    min={15}
                    max={300}
                    step={15}
                    data-testid="input-interview-duration"
                    value={defaultSettings.interviewDurationSeconds}
                    onChange={e => setDefaultSettings(s => ({ ...s, interviewDurationSeconds: parseInt(e.target.value) || 60 }))}
                    className="w-24 text-center"
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">Default Invite Expiry</p>
                    <p className="text-sm text-muted-foreground">How long interview invites remain valid (hours)</p>
                  </div>
                  <Input
                    type="number"
                    min={24}
                    max={168}
                    step={24}
                    data-testid="input-invite-expiry"
                    value={defaultSettings.inviteExpiryHours}
                    onChange={e => setDefaultSettings(s => ({ ...s, inviteExpiryHours: parseInt(e.target.value) || 48 }))}
                    className="w-24 text-center"
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">Default Question Count</p>
                    <p className="text-sm text-muted-foreground">Number of questions per interview</p>
                  </div>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    data-testid="input-question-count"
                    value={defaultSettings.defaultQuestionCount}
                    onChange={e => setDefaultSettings(s => ({ ...s, defaultQuestionCount: parseInt(e.target.value) || 5 }))}
                    className="w-24 text-center"
                  />
                </div>
                <div className="flex justify-end pt-2 border-t border-border">
                  <Button
                    size="sm"
                    data-testid="button-save-default-settings"
                    disabled={savingDefaultSettings}
                    onClick={saveDefaultSettings}
                  >
                    {savingDefaultSettings ? "Saving…" : "Save Changes"}
                  </Button>
                </div>
              </div>
            </Card>

            {user?.role === "ADMIN" && <IntegrationHealthPanel />}

            {user?.role === "ADMIN" && (
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  Dossier Sync Settings
                </h3>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Configure automatic sync to dossier.vexistalentagency.com for published candidates.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Configuration Status</label>
                      <p className="text-xs text-muted-foreground mb-1">
                        Set via DOSSIER_API_URL and DOSSIER_API_KEY environment variables
                      </p>
                      <Badge variant="outline" className="font-mono text-xs">
                        Contact admin to configure
                      </Badge>
                    </div>
                  </div>
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground">
                      When configured, candidate data syncs automatically when dossiers are published.
                      Manual JSON export is available from the Pipeline tab for each candidate.
                    </p>
                  </div>
                </div>
              </Card>
            )}

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Data & Privacy
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Data stored on this platform</p>
                    <p className="text-sm text-muted-foreground">Candidate video responses, assessment scores, dossier outputs, and employer records are stored on secure Replit infrastructure and production Supabase database.</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Compliance framework</p>
                    <p className="text-sm text-muted-foreground">VEXIS operates under the NZ Privacy Act 2020. All candidate data is collected with explicit consent and used solely for hiring evaluation purposes.</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Candidate data deletion</p>
                    <p className="text-sm text-muted-foreground">Candidates may request deletion of their data at any time via privacy@vexistalentagency.com. Requests are actioned within 20 working days.</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Data retention</p>
                    <p className="text-sm text-muted-foreground">Assessment recordings and candidate records are retained for 12 months from submission date unless otherwise instructed by the client employer.</p>
                  </div>
                </div>
                <div className="border-t border-border pt-4">
                  <p className="font-medium mb-1">Video Retention Policy</p>
                  <p className="text-sm text-muted-foreground mb-2">Interview video recordings are automatically deleted 90 days after submission. Recruiters can manually delete recordings at any time from the candidate review screen.</p>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>Retention period: <span className="font-medium text-foreground">90 days</span></span>
                    <span>Auto-deletion: <span className="font-medium text-green-600">Enabled</span></span>
                  </div>
                  {/* SETTINGS_FUTURE: Add workspace-level retention period override (30/60/90/180 days) when agency settings are extended. */}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border flex gap-3">
                <Link href="/privacy">
                  <Button variant="outline" size="sm" className="border-border" data-testid="settings-privacy-policy">
                    <FileText className="w-4 h-4 mr-2" />
                    Privacy Policy
                  </Button>
                </Link>
                <Link href="/compliance-data-handling">
                  <Button variant="outline" size="sm" className="border-border" data-testid="settings-compliance">
                    <Shield className="w-4 h-4 mr-2" />
                    Compliance & Data Handling
                  </Button>
                </Link>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <HelpCircle className="w-4 h-4" />
                Help & Support
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Dashboard Tour</p>
                    <p className="text-sm text-muted-foreground">Take an interactive tour of the dashboard features</p>
                  </div>
                  <TourTriggerButton onClick={startTour} data-testid="settings-start-tour" />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">User Guide</p>
                    <p className="text-sm text-muted-foreground">Download the complete PDF user guide</p>
                  </div>
                  <Link href="/user-guide">
                    <Button variant="outline" size="sm" className="border-border" data-testid="settings-user-guide">
                      <FileText className="w-4 h-4 mr-2" />
                      Open Guide
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>

            {user?.role === "ADMIN" && <AgentManagementSection />}

            <Card className="p-6 border-destructive/50">
              <h3 className="font-semibold mb-4 flex items-center gap-2 text-destructive">
                <LogOut className="w-4 h-4" />
                Session
              </h3>
              <p className="text-sm text-muted-foreground mb-4">Sign out of your agent account</p>
              <Button variant="destructive" onClick={logout}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </Card>
          </div>
        )}

        {activeTab === "guide" && (
          <div className="space-y-8 max-w-3xl">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">User Guide</h1>
              <p className="text-muted-foreground text-sm">Step-by-step instructions for using Vexis</p>
            </div>

            {/* Section 1 — Getting Started */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                Getting Started
              </h2>
              <p className="text-sm text-muted-foreground">Welcome to Vexis. Here's how the platform works in 5 steps:</p>
              <ol className="space-y-3">
                {[
                  { n: 1, text: "Set up your roles with custom interview questions in Roles & Questions" },
                  { n: 2, text: "Connect JobAdder in Settings → Integrations" },
                  { n: 3, text: "Click 'Vexis Interview' on any JobAdder application to send an assessment" },
                  { n: 4, text: "Review completed assessments in Assessment Pipeline" },
                  { n: 5, text: "Send the dossier to your client when ready" },
                ].map(({ n, text }) => (
                  <li key={n} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">{n}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Section 2 — Setting Up a Role */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                Setting Up a Role
              </h2>
              <p className="text-sm text-muted-foreground">Before sending any assessment, each role needs custom interview questions.</p>
              <ol className="space-y-3">
                {[
                  { n: 1, text: "Go to Roles & Questions in the sidebar" },
                  { n: 2, text: "Find the role with the ⚠ Set Questions badge" },
                  { n: 3, text: "Click Set Up Questions" },
                  { n: 4, text: "Go to the Interview Questions tab" },
                  { n: 5, text: "Add 3–7 questions specific to this role" },
                  { n: 6, text: "Click Add Question after each one" },
                  { n: 7, text: "When done, the role shows ✓ Ready" },
                ].map(({ n, text }) => (
                  <li key={n} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">{n}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Section 3 — Sending Assessments via JobAdder */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                Sending Assessments via JobAdder
              </h2>
              <ol className="space-y-3">
                {[
                  { n: 1, text: "Open JobAdder and find a candidate's application" },
                  { n: 2, text: "Click Actions → Vexis Interview" },
                  { n: 3, text: "The Vexis panel opens showing the candidate name and role" },
                  { n: 4, text: "If the role shows 'Role Setup Required', click Setup Questions first and return when done" },
                  { n: 5, text: "Click Send Assessment to dispatch the video interview link" },
                  { n: 6, text: "The candidate receives a personalised email and SMS with their unique link" },
                  { n: 7, text: "Track their progress in Assessment Pipeline" },
                ].map(({ n, text }) => (
                  <li key={n} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">{n}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Section 4 — Reviewing & Creating Dossiers */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                Reviewing Interviews & Creating Dossiers
              </h2>
              <ol className="space-y-3">
                {[
                  { n: 1, text: "When a candidate submits their interview, you'll see a badge on Assessment Pipeline" },
                  { n: 2, text: "Open the candidate record and watch their video responses" },
                  { n: 3, text: "Review the AI-scored transcript — Green (70+), Amber (45–69), Red (under 45)" },
                  { n: 4, text: "Add your human review notes and mark your decision" },
                  { n: 5, text: "Click Complete Review and Create Dossier to generate the boardroom-ready document" },
                  { n: 6, text: "Once the dossier is ready, click Send Dossier to Employer to dispatch it with a secure link" },
                ].map(({ n, text }) => (
                  <li key={n} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">{n}</span>
                    <span>{text}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Section 5 — Tips */}
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6 space-y-3">
              <h2 className="text-lg font-semibold text-amber-700 dark:text-amber-300">Tips</h2>
              <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                <li>Set up role questions before connecting JobAdder — assessments can be sent immediately once roles are ready</li>
                <li>Interview links expire after 72 hours — use Resend Link on the candidate card if a candidate needs a new link</li>
                <li>Candidates can complete the interview on any device — no app required</li>
                <li>The AI triage score is a guide — always apply your own human review before creating a dossier</li>
                <li>Need help? Email <a href="mailto:support@vexistalentagency.com" className="underline">support@vexistalentagency.com</a></li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === "howto" && (
          <div className="space-y-6 max-w-4xl">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-1">The Vexis Workflow</h1>
              <p className="text-muted-foreground text-sm">8 stages from application to outcome</p>
            </div>

            {/* Sub-tab navigation */}
            <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit">
              <button
                onClick={() => setHelpSubTab("howItWorks")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${helpSubTab === "howItWorks" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="help-tab-how-it-works"
              >
                How It Works
              </button>
              <button
                onClick={() => setHelpSubTab("faq")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${helpSubTab === "faq" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="help-tab-faq"
              >
                FAQ
              </button>
            </div>

            {helpSubTab === "howItWorks" && (
              <div className="space-y-3">
                {[
                  {
                    step: "01",
                    label: "Applied",
                    color: "bg-slate-500",
                    text: "A candidate has been added to a role. Click Send Interview Link to dispatch their personalised token link by email and SMS. Before sending, confirm the role brief has the right questions for this role."
                  },
                  {
                    step: "02",
                    label: "Link Sent",
                    color: "bg-blue-500",
                    text: "The candidate has received their interview link. It is valid for 72 hours. You will see whether they have opened it yet. If the link has not been opened after 48 hours, send a reminder."
                  },
                  {
                    step: "03",
                    label: "In Progress",
                    color: "bg-blue-600",
                    text: "The candidate is actively recording their interview. No action needed - you will see a live indicator on their card."
                  },
                  {
                    step: "04",
                    label: "Submitted",
                    color: "bg-indigo-500",
                    text: "The interview is complete. Scoring is running - this takes about 90 seconds. The candidate has received a confirmation email."
                  },
                  {
                    step: "05",
                    label: "Triage",
                    color: "bg-violet-500",
                    text: "Scoring is complete. The candidate has been banded Green, Amber, or Red based on their overall score and the confidence level. This is your review queue. Green candidates are fastest to review."
                  },
                  {
                    step: "06",
                    label: "Under Review",
                    color: "bg-amber-500",
                    text: "You are reviewing this candidate. Use the transcript as your primary source. Watch video only when needed. Complete the scoring form and click Complete Review and Create Dossier when done."
                  },
                  {
                    step: "07",
                    label: "Dossier Ready",
                    color: "bg-orange-500",
                    text: "The dossier has been created at dossier.vexistalentagency.com. Review it, then click Send Dossier to Employer to dispatch it to the hiring manager with a secure link."
                  },
                  {
                    step: "08",
                    label: "Outcome",
                    color: "bg-green-500",
                    text: "The employer has reviewed the dossier and selected an outcome. The candidate has been notified automatically - a personalised message if not proceeding, or a calendar booking link if invited to in-person."
                  },
                ].map(({ step, label, color, text }) => (
                  <Card key={step} className="p-4 bg-card border-border">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full ${color} text-white flex items-center justify-center font-bold text-xs`}>
                          {step}
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-foreground mb-1">{label}</p>
                        <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {helpSubTab === "faq" && (
              <div className="space-y-3">
                {[
                  {
                    q: "What is the triage banding system?",
                    a: "After a candidate submits their interview, the scoring system analyses the transcript against the role scoring criteria and assigns a score to each answer. Candidates are placed into one of three bands: Green (score 70 or above, high confidence - transcript review only, about 3 minutes), Amber (score 45-69, or any low-confidence score - watch the flagged question, about 6 minutes), or Red (score under 45, high confidence - watch the 30-second highlight clip, about 45 seconds). Every candidate in every band gets a complete review."
                  },
                  {
                    q: "Can I override the score?",
                    a: "Yes - always. The score is a draft suggestion to speed up your review. You can adjust any per-question score in the review form. Your score becomes the final score and appears in the employer dossier. If you override a score significantly, add a note explaining why - this helps improve the scoring model over time."
                  },
                  {
                    q: "What happens if a candidate's link expires before they complete the interview?",
                    a: "The link becomes inactive and the candidate sees a message telling them to contact the company. You can reissue the link from the candidate's card - click the three-dot menu and select Reissue Interview Link. The new link has a fresh 72-hour window."
                  },
                  {
                    q: "What does the scripted detection flag mean?",
                    a: "This is an advisory signal, not a hard flag. It indicates that the candidate's responses showed patterns unusual for natural spoken language - overly formal phrasing, corporate jargon, or unusually precise structure that may suggest pre-scripted or prepared answers. It is a prompt to pay closer attention during your review, not an automatic negative. Many candidates prepare thoroughly and their responses can appear structured without being scripted."
                  },
                  {
                    q: "How does the dossier get to the employer?",
                    a: "Once you complete a review and click Complete Review and Create Dossier, the scored data is sent to dossier.vexistalentagency.com where the professional dossier document is generated automatically. A secure share link valid for 14 days is returned and stored. When you click Send Dossier to Employer, a professional email is sent to the employer contact with that link. The score and dossier URL are also written back to the candidate record in your ATS automatically."
                  },
                  {
                    q: "Can the employer watch the candidate's video?",
                    a: "The dossier includes an option for the employer to watch a short highlight clip from the candidate's highest-scoring answer. They can request the full video via their Vexis contact. Full videos are automatically deleted 24 hours after the review is completed."
                  },
                  {
                    q: "What is a Role Brief?",
                    a: "A Role Brief is the foundation of every assessment. It defines the role title and seniority level, 3 core outcomes the successful candidate must achieve in their first 90 days, 5-7 interview questions tailored specifically to those outcomes, and scoring guidance for each question. Good role briefs produce better candidate signal than generic question templates. The question bank in the Role Brief builder has 40 pre-written questions by category to help you get started."
                  },
                  {
                    q: "Why does every candidate get an interview - not just the shortlisted ones?",
                    a: "Because resume filtering eliminates qualified candidates before any human ever sees them. Research shows 88% of employers have lost good candidates to filtering. Vexis's position is that every applicant deserves the opportunity to show what they can do. The triage system makes this commercially viable - reviewing 300 candidates takes one agent under 48 hours."
                  },
                ].map(({ q, a }, i) => (
                  <Card key={i} className="p-5 bg-card border-border">
                    <p className="font-semibold text-foreground mb-2">{q}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{a}</p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent className="w-full sm:w-[500px] sm:max-w-[500px] overflow-y-auto p-4 md:p-6">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Agent Verification Panel
            </SheetTitle>
          </SheetHeader>
          
          {selectedApplication && (
            <div className="mt-6 space-y-6">
              <Card className="p-4 bg-muted/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold">{selectedApplication.candidate_name || "Candidate"}</p>
                    <p className="text-sm text-muted-foreground">[Contact details protected]</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link href={`/dossier/${selectedApplication.id}`}>
                    <Button size="sm" variant="outline" className="gap-1">
                      <FileText className="w-3 h-3" />
                      View Dossier
                    </Button>
                  </Link>
                  <Button 
                    size="sm" 
                    variant={showVideoPlayer ? "default" : "outline"} 
                    className="gap-1"
                    onClick={() => fetchInterviewVideos(selectedApplication.id, (selectedApplication as any).source)}
                    disabled={loadingVideos}
                  >
                    <Video className="w-3 h-3" />
                    {loadingVideos ? "Loading..." : showVideoPlayer ? "Viewing..." : "Watch Interview"}
                  </Button>
                </div>
              </Card>

              {/* Video Player Section - Agent Only */}
              {showVideoPlayer && (
                <Card className="p-4 border-primary/30 bg-primary/5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold flex items-center gap-2">
                      <Video className="w-4 h-4 text-primary" />
                      Interview Recording
                    </h3>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setShowVideoPlayer(false)}
                      className="h-6 w-6 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Auto-delete warning / post-deletion banners */}
                  {interviewVideoStatus && !interviewVideoStatus.videosDeleted && interviewVideoStatus.videoDeleteAt && (() => {
                    const deleteAt = new Date(interviewVideoStatus.videoDeleteAt);
                    const daysLeft = Math.ceil((deleteAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                    if (daysLeft <= 14) {
                      return (
                        <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2">
                            <span className="text-base leading-none mt-0.5">🕐</span>
                            <div>
                              <p className="text-sm font-medium text-amber-800">
                                Interview videos will be automatically deleted on {deleteAt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} — {daysLeft} day{daysLeft !== 1 ? "s" : ""} remaining.
                              </p>
                              <p className="text-xs text-amber-600 mt-0.5">Delete now or download before this date.</p>
                            </div>
                          </div>
                          <button
                            className="text-xs text-amber-700 hover:text-amber-900 font-medium whitespace-nowrap"
                            onClick={() => setShowBulkDeleteConfirm(true)}
                          >
                            Delete Now
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {interviewVideoStatus?.videosDeleted && interviewVideoStatus.videosDeletedBy === "system" && (
                    <div className="mb-3 p-3 rounded-lg bg-slate-100 border border-slate-200">
                      <p className="text-sm text-slate-500">
                        Interview videos were automatically deleted on {interviewVideoStatus.videosDeletedAt ? new Date(interviewVideoStatus.videosDeletedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "an earlier date"} in line with our 90-day privacy policy.
                      </p>
                    </div>
                  )}
                  {interviewVideoStatus?.videosDeleted && interviewVideoStatus.videosDeletedBy === "recruiter" && (
                    <div className="mb-3 p-3 rounded-lg bg-slate-100 border border-slate-200">
                      <p className="text-sm text-slate-500">
                        Interview videos were manually deleted by your team on {interviewVideoStatus.videosDeletedAt ? new Date(interviewVideoStatus.videosDeletedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "an earlier date"}.
                      </p>
                    </div>
                  )}
                  
                  {loadingVideos ? (
                    <div className="aspect-video bg-black/80 rounded-lg flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full mx-auto mb-2" />
                        <p className="text-sm">Loading interview...</p>
                      </div>
                    </div>
                  ) : interviewVideos.length > 0 ? (
                    <div className="space-y-3">
                      {/* Bulk delete control row */}
                      {interviewVideos.some(v => !v.videoDeleted) && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Interview recordings</span>
                          {showBulkDeleteConfirm ? (
                            <div className="flex flex-col items-end gap-1">
                              <p className="text-xs text-slate-600 text-right max-w-xs">Delete all video recordings? Transcripts and AI scores are not affected. This cannot be undone.</p>
                              <div className="flex gap-2">
                                <button className="text-xs text-slate-500 hover:text-slate-700" onClick={() => setShowBulkDeleteConfirm(false)}>Cancel</button>
                                <button
                                  className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                                  disabled={deletingAllVideos}
                                  data-testid="confirm-delete-all-videos"
                                  onClick={async () => {
                                    if (!interviewSessionId) return;
                                    setDeletingAllVideos(true);
                                    try {
                                      const res = await apiRequest("DELETE", `/api/agent/interviews/${interviewSessionId}/videos`);
                                      if (res.status === 207) {
                                        toast.warning("Some videos could not be deleted. Refresh and retry.");
                                      } else if (!res.ok) {
                                        toast.error("Could not delete videos — please retry");
                                      } else {
                                        toast.success("All videos deleted");
                                        setInterviewVideos(vs => vs.map(v => ({ ...v, videoDeleted: true, videoDeletedAt: new Date().toISOString() })));
                                        setInterviewVideoStatus(s => s ? { ...s, videosDeleted: true, videosDeletedAt: new Date().toISOString(), videosDeletedBy: "recruiter" } : s);
                                        setVideoUrls({});
                                      }
                                    } catch {
                                      toast.error("Could not delete videos — please retry");
                                    } finally {
                                      setDeletingAllVideos(false);
                                      setShowBulkDeleteConfirm(false);
                                    }
                                  }}
                                >
                                  {deletingAllVideos ? "Deleting..." : "Delete All Videos"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              className="text-sm text-red-500 hover:text-red-700"
                              data-testid="bulk-delete-videos-btn"
                              onClick={() => setShowBulkDeleteConfirm(true)}
                            >
                              Delete all videos
                            </button>
                          )}
                        </div>
                      )}

                      {/* Current video player or deleted placeholder */}
                      {(() => {
                        const currentVideo = interviewVideos[activeVideoIndex];
                        if (!currentVideo) return null;
                        if (currentVideo.videoDeleted) {
                          return (
                            <div className="aspect-video bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-center">
                              <div className="text-center">
                                <p className="text-sm font-medium text-slate-500">Video deleted</p>
                                {currentVideo.videoDeletedAt && (
                                  <p className="text-xs text-slate-400 mt-1">
                                    Deleted {new Date(currentVideo.videoDeletedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                                  </p>
                                )}
                                <p className="text-xs text-slate-400 mt-0.5">Transcript available below</p>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-1">
                            <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                              {videoUrls[currentVideo.questionId] ? (
                                <video
                                  key={activeVideoIndex}
                                  src={videoUrls[currentVideo.questionId]}
                                  controls
                                  className="w-full h-full object-cover"
                                  data-testid="video-player"
                                />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="text-center text-white/70">
                                    <Play className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Loading video...</p>
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Per-video delete button */}
                            {confirmDeleteVideoId === currentVideo.responseId ? (
                              <div className="flex items-center justify-end gap-2 pt-1">
                                <span className="text-xs text-slate-500">Delete this recording? Transcript stays. Cannot be undone.</span>
                                <button className="text-xs text-slate-400 hover:text-slate-600" onClick={() => setConfirmDeleteVideoId(null)}>Cancel</button>
                                <button
                                  className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                                  disabled={deletingVideoId === currentVideo.responseId}
                                  data-testid={`confirm-delete-video-${currentVideo.responseId}`}
                                  onClick={async () => {
                                    setDeletingVideoId(currentVideo.responseId);
                                    try {
                                      const res = await apiRequest("DELETE", `/api/agent/responses/${currentVideo.responseId}/video`);
                                      if (!res.ok) {
                                        const data = await res.json().catch(() => ({}));
                                        if (data.error === "already_deleted") {
                                          toast.info("Video already deleted");
                                        } else {
                                          toast.error("Could not delete video — please retry");
                                        }
                                      } else {
                                        toast.success("Video deleted successfully");
                                        const deletedAt = new Date().toISOString();
                                        setInterviewVideos(vs => vs.map(v => v.responseId === currentVideo.responseId ? { ...v, videoDeleted: true, videoDeletedAt: deletedAt } : v));
                                        setVideoUrls(u => { const next = { ...u }; delete next[currentVideo.questionId]; return next; });
                                        const remaining = interviewVideos.filter(v => v.responseId !== currentVideo.responseId && !v.videoDeleted);
                                        if (remaining.length === 0) {
                                          setInterviewVideoStatus(s => s ? { ...s, videosDeleted: true, videosDeletedAt: deletedAt, videosDeletedBy: "recruiter" } : s);
                                        }
                                      }
                                    } catch {
                                      toast.error("Could not delete video — please retry");
                                    } finally {
                                      setDeletingVideoId(null);
                                      setConfirmDeleteVideoId(null);
                                    }
                                  }}
                                >
                                  {deletingVideoId === currentVideo.responseId ? "Deleting..." : "Delete Video"}
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-end pt-1">
                                <button
                                  className="text-sm text-red-500 hover:text-red-700"
                                  data-testid={`delete-video-btn-${currentVideo.responseId}`}
                                  onClick={() => setConfirmDeleteVideoId(currentVideo.responseId)}
                                >
                                  🗑 Delete this video
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      
                      <div className="space-y-2">
                        <p className="text-xs font-medium">
                          Question {activeVideoIndex + 1} of {interviewVideos.length}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {interviewVideos[activeVideoIndex]?.question}
                        </p>
                      </div>
                      
                      <div className="flex gap-1 flex-wrap">
                        {interviewVideos.map((v, index) => (
                          <Button
                            key={index}
                            size="sm"
                            variant={index === activeVideoIndex ? "default" : "outline"}
                            onClick={() => { setActiveVideoIndex(index); setConfirmDeleteVideoId(null); }}
                            className={`w-8 h-8 p-0 text-xs ${v.videoDeleted ? "opacity-50 line-through" : ""}`}
                            data-testid={`video-button-${index}`}
                          >
                            {index + 1}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                      <div className="text-center text-muted-foreground">
                        <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No interview found</p>
                      </div>
                    </div>
                  )}
                </Card>
              )}

              <div className="space-y-4">
                <h3 className="font-bold flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  Verification Checklist
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Checkbox 
                      id="identity"
                      checked={verification.identity_verified}
                      onCheckedChange={(checked) => setVerification({...verification, identity_verified: !!checked})}
                    />
                    <div className="flex-1">
                      <label htmlFor="identity" className="font-medium cursor-pointer">Identity Verified</label>
                      <p className="text-xs text-muted-foreground">Confirm candidate's identity matches documentation</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Checkbox 
                      id="right-to-work"
                      checked={verification.right_to_work_verified}
                      onCheckedChange={(checked) => setVerification({...verification, right_to_work_verified: !!checked})}
                    />
                    <div className="flex-1">
                      <label htmlFor="right-to-work" className="font-medium cursor-pointer">Right to Work</label>
                      <p className="text-xs text-muted-foreground">Verified work authorization for New Zealand</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Checkbox 
                      id="authenticity"
                      checked={verification.authenticity_verified}
                      onCheckedChange={(checked) => setVerification({...verification, authenticity_verified: !!checked})}
                    />
                    <div className="flex-1">
                      <label htmlFor="authenticity" className="font-medium cursor-pointer">Authenticity Check</label>
                      <p className="text-xs text-muted-foreground">Resume claims verified against interview responses</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-bold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  Flag Issues (Optional)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["Inconsistent timeline", "Unverifiable claims", "Poor communication", "Technical gaps"].map(issue => (
                    <Badge 
                      key={issue}
                      variant={verification.flagged_issues.includes(issue) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        if (verification.flagged_issues.includes(issue)) {
                          setVerification({...verification, flagged_issues: verification.flagged_issues.filter(i => i !== issue)});
                        } else {
                          setVerification({...verification, flagged_issues: [...verification.flagged_issues, issue]});
                        }
                      }}
                    >
                      {issue}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-bold">Agent Summary</h3>
                <p className="text-xs text-muted-foreground">Your professional opinion for employers (overrides candidate bio)</p>
                <Textarea 
                  placeholder="Write your professional assessment of this candidate..."
                  value={verification.agent_summary}
                  onChange={(e) => setVerification({...verification, agent_summary: e.target.value})}
                  rows={4}
                />
              </div>

              <div className="space-y-3">
                <h3 className="font-bold">Private Agent Notes</h3>
                <p className="text-xs text-muted-foreground">Internal notes (not visible to employers or candidates)</p>
                <Textarea 
                  placeholder="Add any private notes about this candidate..."
                  value={verification.agent_notes}
                  onChange={(e) => setVerification({...verification, agent_notes: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setSidebarOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={saveVerification} className="flex-1 gap-2">
                  <UserCheck className="w-4 h-4" />
                  {verification.identity_verified && verification.right_to_work_verified && verification.authenticity_verified
                    ? "Mark Human-Verified"
                    : "Save Progress"
                  }
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Onboarding Tour */}
      <OnboardingTour
        steps={dashboardTourSteps}
        isOpen={tourOpen}
        onClose={closeTour}
        onComplete={completeTour}
        resetKey={resetCounter}
      />

      {/* Floating Help / Tour Button */}
      <button
        id="tour-help"
        onClick={() => {
          resetTourStorage();
          startOnboardingTour();
        }}
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          zIndex: 9999,
          background: "#6366f1",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: "48px",
          height: "48px",
          fontSize: "20px",
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "bold",
          lineHeight: 1,
        }}
        title="Click for platform tour • support@vexistalentagency.com for help"
        data-testid="button-tour-help"
      >
        ?
      </button>

      {/* Review & Complete Dossier Panel — rendered at AgentDashboard level so it is visible from any tab */}
      {reviewingInterview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="review-dossier-modal">
          <div className="bg-background rounded-xl shadow-2xl w-full max-w-lg border border-border">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div>
                <h2 className="text-lg font-bold">Candidate Review — {reviewingInterview.candidateName}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {reviewingInterview.roleTitle}
                  {reviewingInterview.completedAt && ` · Submitted ${Math.max(0, Math.floor((Date.now() - new Date(reviewingInterview.completedAt).getTime()) / 36e5))}h ago`}
                  {reviewingInterview.overallScore !== undefined && ` · ${reviewingInterview.overallScore >= 70 ? "Green" : reviewingInterview.overallScore >= 50 ? "Amber" : "Red"} band · ${reviewingInterview.overallScore}/100 AI score`}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeReview} data-testid="button-close-review-modal">✕</Button>
            </div>

            <div className="p-5 space-y-5">
              {reviewingInterview.overallScore !== undefined && (
                <div className={`p-3 rounded-lg border text-sm ${reviewingInterview.overallScore >= 70 ? "bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-300" : reviewingInterview.overallScore >= 40 ? "bg-amber-950 border-amber-800 text-amber-300" : "bg-red-950 border-red-800 text-red-300"}`}>
                  {reviewingInterview.overallScore >= 70 ? (
                    <>This candidate scored {reviewingInterview.overallScore}/100 with high confidence. Review the transcript below — video is available if you want to verify. Estimated time: 2–4 minutes.</>
                  ) : reviewingInterview.overallScore >= 40 ? (
                    <>⚠ AI Score: {reviewingInterview.overallScore}/100 — Amber band. Review the transcript before proceeding.</>
                  ) : (
                    <>⚠ AI Score: {reviewingInterview.overallScore}/100 — Red band. Review the transcript before proceeding.</>
                  )}
                </div>
              )}

              {dossierResult ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-green-700 dark:text-green-400">Dossier created successfully</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Score: {dossierResult.score ?? "N/A"}% · Recommendation: <span className="font-medium capitalize">{dossierResult.recommendation}</span>
                      </p>
                    </div>
                  </div>
                  {dossierResult.url ? (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">Dossier URL:</p>
                      <a
                        href={dossierResult.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-sm text-blue-500 hover:underline break-all font-mono bg-muted/50 p-3 rounded-lg"
                        data-testid="link-dossier-url"
                      >
                        {dossierResult.url}
                      </a>
                      <Button
                        className="w-full bg-green-600 hover:bg-green-700 text-white gap-2"
                        onClick={() => window.open(dossierResult.url, "_blank")}
                        data-testid="button-send-dossier-employer"
                      >
                        Send Dossier to Employer →
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">The dossier has been saved. A link will be available shortly.</p>
                  )}
                  <Button variant="outline" className="w-full" onClick={closeReview}>Done</Button>
                </div>
              ) : (
                <>
                  {dossierError && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                      <div className="text-yellow-600 text-sm">
                        <p>The dossier site is temporarily unavailable. Your review has been saved. Click Retry Dossier to try again — your scores and notes are not lost.</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="recommendation-select">Your recommendation <span className="text-red-500">*</span></label>
                    <select
                      id="recommendation-select"
                      className={`w-full border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring ${reviewErrors.recommendation ? "border-red-500 ring-1 ring-red-500" : "border-input"}`}
                      value={reviewForm.recommendation}
                      onChange={e => setReviewForm(f => ({ ...f, recommendation: e.target.value }))}
                      disabled={completing}
                      data-testid="select-recommendation"
                    >
                      <option value="">Select a recommendation…</option>
                      <option value="proceed">Strong — recommend for in-person</option>
                      <option value="hold">Consider — borderline, worth a conversation</option>
                      <option value="decline">Not proceeding — not the right fit at this time</option>
                    </select>
                    {reviewErrors.recommendation && (
                      <p className="text-xs text-red-500 font-medium" data-testid="error-recommendation">⚠ {reviewErrors.recommendation}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="agent-notes-input">Summary for the dossier <span className="text-red-500">*</span></label>
                    <textarea
                      id="agent-notes-input"
                      className={`w-full border rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring min-h-[110px] resize-none ${reviewErrors.agentNotes ? "border-red-500 ring-1 ring-red-500" : "border-input"}`}
                      placeholder="What stood out? What should the employer know before meeting this person?"
                      value={reviewForm.agentNotes}
                      onChange={e => setReviewForm(f => ({ ...f, agentNotes: e.target.value }))}
                      disabled={completing}
                      data-testid="input-agent-notes"
                    />
                    {reviewErrors.agentNotes && (
                      <p className="text-xs text-red-500 font-medium" data-testid="error-agent-notes">⚠ {reviewErrors.agentNotes}</p>
                    )}
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/30">
                    <input
                      type="checkbox"
                      id="ai-scripted-advisory"
                      className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                      checked={!!(reviewForm as any).aiScriptedAdvisory}
                      onChange={e => setReviewForm(f => ({ ...f, aiScriptedAdvisory: e.target.checked } as any))}
                      disabled={completing}
                      data-testid="checkbox-ai-scripted-advisory"
                    />
                    <div>
                      <label htmlFor="ai-scripted-advisory" className="text-sm font-medium cursor-pointer">AI-scripted advisory</label>
                      <p className="text-xs text-muted-foreground mt-0.5">Tick this if responses felt rehearsed, overly formal, or inconsistent with natural spoken language. This appears as an advisory note in the dossier — not a hard flag.</p>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-1">
                    <Button variant="outline" onClick={closeReview} disabled={completing} className="flex-1">Cancel</Button>
                    <Button
                      onClick={completeDossier}
                      disabled={completing}
                      className="flex-1 gap-2 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="button-complete-review-create-dossier"
                    >
                      {completing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Creating dossier…
                        </>
                      ) : dossierError ? (
                        "Retry Dossier"
                      ) : (
                        "Complete Review and Create Dossier →"
                      )}
                    </Button>
                  </div>
                  {!completing && !dossierError && (
                    <p className="text-[10px] text-muted-foreground text-center -mt-2">This action is final. The dossier will be sent to dossier.vexistalentagency.com and the result written back to your ATS.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </AgentDashboardLayout>
  );
}
